import L from "leaflet";

const STATE_KEY = "hydrotools:slip-inventory:v2";
const API_BASE = "/api/slip-inventory/v1/slip-inventory";
const COL_CFG_KEY = "hydrotools:slip-inventory-cols:v1";
const REMOTE_SYNC_INTERVAL_MS = 12000;
const LOCAL_SAVE_GRACE_MS = 2500;

const ALL_COLUMNS = [
  { key: "measurement_id",        label: "Meas ID",          width: 130, numeric: false },
  { key: "marina_id",             label: "Marina ID",         width: 80,  numeric: false },
  { key: "marina_name",           label: "Marina",            width: 140, numeric: false },
  { key: "accepted_adjusted",     label: "Accepted/Adj",      width: 110, numeric: false },
  { key: "dock_name",             label: "Dock",              width: 80,  numeric: false },
  { key: "adjusted_dock",         label: "Adj Dock",          width: 80,  numeric: false },
  { key: "slip_id",               label: "Slip ID",           width: 100, numeric: false },
  { key: "adjusted_slip_id",      label: "Adj Slip ID",       width: 100, numeric: false },
  { key: "multislip_yn",          label: "Multi-slip",        width: 80,  numeric: false },
  { key: "multislip_id",          label: "Multi-slip ID",     width: 100, numeric: false },
  { key: "adjusted_multislip_id", label: "Adj Multi-slip ID", width: 125, numeric: false },
  { key: "space_type",            label: "Slip Type",         width: 100, numeric: false },
  { key: "adjusted_space_type",   label: "Adj Slip Type",     width: 115, numeric: false },
  { key: "covered_yn",            label: "Covered",           width: 75,  numeric: false },
  { key: "adjusted_covered",      label: "Adj Covered",       width: 90,  numeric: false },
  { key: "boatlift_yn",           label: "Boatlift",          width: 75,  numeric: false },
  { key: "fueldock_yn",           label: "Fuel Dock",         width: 80,  numeric: false },
  { key: "out_of_service",        label: "Out of Service",    width: 90,  numeric: false },
  { key: "out_of_service_reason", label: "OOS Reason",        width: 130, numeric: false },
  { key: "measured_linear_feet",  label: "Meas L (ft)",       width: 90,  numeric: true  },
  { key: "marketable_linear_feet",label: "Mkt L (ft)",        width: 80,  numeric: true  },
  { key: "measured_width_feet",   label: "Meas W (ft)",       width: 90,  numeric: true  },
  { key: "marketable_width_feet", label: "Mkt W (ft)",        width: 80,  numeric: true  },
  { key: "marina_comments",       label: "Comments",          width: 180, numeric: false },
  { key: "measured_depth_feet",   label: "Depth (ft)",        width: 80,  numeric: true  },
  { key: "measurement_date",      label: "Meas Date",         width: 100, numeric: false },
  { key: "geometry_wkt",          label: "Geometry",          width: 210, numeric: false },
  { key: "confidence_score",      label: "Confidence",        width: 90,  numeric: false },
  { key: "record_created_timestamp", label: "Created",        width: 140, numeric: false },
];

const DEFAULT_HIDDEN_COLS = new Set([
  "measurement_id", "marina_id", "marina_name", "adjusted_dock", "adjusted_slip_id",
  "multislip_id", "adjusted_multislip_id", "adjusted_space_type", "adjusted_covered",
  "marketable_linear_feet", "marketable_width_feet",
  "marina_comments", "geometry_wkt", "record_created_timestamp",
]);

const BINARY_COLS = new Set(["multislip_yn", "covered_yn", "boatlift_yn", "fueldock_yn", "out_of_service"]);
const NUMERIC_COLS = new Set(["measured_linear_feet", "marketable_linear_feet", "measured_width_feet", "marketable_width_feet", "measured_depth_feet"]);
const NON_EDITABLE_COLS = new Set(["measurement_id", "geometry_wkt", "record_created_timestamp"]);

const SPACE_TYPE_OPTIONS = ["Side Tie", "Pilings", "Finger", "Med-Mooring", "Travel Lift", "Boat Ramp", "Dock", "Other"];
const OOS_REASON_OPTIONS = ["Environmental", "Damage", "Silting", "Other"];

const RECORD_HEADERS = ALL_COLUMNS.map((c) => c.key);

function readColCfg() {
  try {
    const raw = localStorage.getItem(COL_CFG_KEY);
    const saved = raw ? JSON.parse(raw) : {};
    const allKeys = ALL_COLUMNS.map((c) => c.key);
    const savedOrder = Array.isArray(saved?.order) ? saved.order.filter((k) => allKeys.includes(k)) : [];
    const order = [...savedOrder, ...allKeys.filter((k) => !savedOrder.includes(k))];
    const hidden = new Set(Array.isArray(saved?.hidden) ? saved.hidden.filter((k) => allKeys.includes(k)) : [...DEFAULT_HIDDEN_COLS]);
    const widths = (saved?.widths && typeof saved.widths === "object") ? { ...saved.widths } : {};
    return { order, hidden, widths };
  } catch {
    return { order: ALL_COLUMNS.map((c) => c.key), hidden: new Set(DEFAULT_HIDDEN_COLS), widths: {} };
  }
}

function writeColCfg(cfg) {
  try {
    localStorage.setItem(COL_CFG_KEY, JSON.stringify({ order: cfg.order, hidden: [...cfg.hidden], widths: cfg.widths }));
  } catch {}
}

function getColWidth(cfg, key) {
  const saved = Number(cfg.widths[key]);
  if (Number.isFinite(saved) && saved >= 30) return saved;
  return ALL_COLUMNS.find((c) => c.key === key)?.width || 100;
}

function visibleCols(cfg) {
  return cfg.order
    .filter((k) => !cfg.hidden.has(k))
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter(Boolean);
}

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultState() {
  return {
    marinaId: "",
    marinaName: "",
    measurementDate: todayDate(),
    confidenceScore: "High",
    selectedMeasurementId: "",
    dockName: "",
    slipIdSeparator: "-",
    slipIdPart3: "",
    slipIdAutoIncrement: true,
    slipIdIncrementBy: 1,
    spaceType: "",
    lineSlipCount: 6,
    slipGeometryTypes: [],
    slipGeometryPolygons: [],
    slipGeometrySelectedId: "",
    slipGeometrySelectedIds: [],
    slipGeometryDraftTypeId: "",
    slipGeometryRunBetweenCount: 5,
    showSlipIds: true,
    showDepths: false,
    tableSortKey: "slip_id",
    tableSortDir: "asc",
    colorSlipsBy: "none",
    displayMeasurementToleranceFt: 0.1,
    dimensionColorToleranceFt: 0.5,
    showDimensionLegend: true,
    lengthWidthToleranceFt: 0.5,
    rotationToleranceDeg: 0.5,
    filterHiddenValues: {},
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const merged = { ...defaultState(), ...(parsed && typeof parsed === "object" ? parsed : {}) };
    merged.measurementDate = todayDate();
    merged.dockName = String(parsed?.dockName ?? parsed?.slipIdPart1 ?? merged.dockName ?? "");
    merged.slipIdSeparator = ["-", "."].includes(String(merged.slipIdSeparator || "")) ? String(merged.slipIdSeparator) : "-";
    merged.spaceType = SPACE_TYPE_OPTIONS.includes(String(merged.spaceType || "")) ? String(merged.spaceType) : "";
    merged.slipIdIncrementBy = Math.max(1, Number(merged.slipIdIncrementBy || 1) || 1);
    merged.slipIdAutoIncrement = merged.slipIdAutoIncrement !== false;
    merged.lineSlipCount = Math.max(1, Number(merged.lineSlipCount || 6) || 6);
    merged.slipGeometryTypes = Array.isArray(merged.slipGeometryTypes) ? merged.slipGeometryTypes : [];
    merged.slipGeometryPolygons = Array.isArray(merged.slipGeometryPolygons) ? merged.slipGeometryPolygons : [];
    merged.slipGeometrySelectedId = "";
    merged.slipGeometrySelectedIds = [];
    merged.slipGeometryDraftTypeId = "";
    merged.slipGeometryRunBetweenCount = Math.max(1, Number(merged.slipGeometryRunBetweenCount || 5) || 5);
    merged.showSlipIds = merged.showSlipIds !== false;
    merged.showDepths = merged.showDepths === true;
    merged.tableSortKey = String(merged.tableSortKey || "slip_id");
    merged.tableSortDir = String(merged.tableSortDir || "asc").toLowerCase() === "desc" ? "desc" : "asc";
    merged.colorSlipsBy = ["none", "dimension", "confidence", "space_type"].includes(String(merged.colorSlipsBy || "").toLowerCase())
      ? String(merged.colorSlipsBy || "").toLowerCase()
      : "none";
    merged.displayMeasurementToleranceFt = [0.1, 0.25, 0.5, 1].includes(Number(merged.displayMeasurementToleranceFt))
      ? Number(merged.displayMeasurementToleranceFt)
      : 0.1;
    merged.dimensionColorToleranceFt = [0.5, 1].includes(Number(merged.dimensionColorToleranceFt))
      ? Number(merged.dimensionColorToleranceFt)
      : 0.5;
    merged.showDimensionLegend = merged.showDimensionLegend !== false;
    merged.lengthWidthToleranceFt = [0.1, 0.25, 0.5, 1].includes(Number(merged.lengthWidthToleranceFt))
      ? Number(merged.lengthWidthToleranceFt)
      : 0.5;
    merged.rotationToleranceDeg = [0.1, 0.5, 1].includes(Number(merged.rotationToleranceDeg))
      ? Number(merged.rotationToleranceDeg)
      : 0.5;
    merged.filterHiddenValues = (merged.filterHiddenValues && typeof merged.filterHiddenValues === "object" && !Array.isArray(merged.filterHiddenValues)) ? merged.filterHiddenValues : {};
    for (const mk of Object.keys(merged.filterHiddenValues)) {
      merged.filterHiddenValues[mk] = Array.isArray(merged.filterHiddenValues[mk]) ? merged.filterHiddenValues[mk].map(String) : [];
    }
    return merged;
  } catch {
    return defaultState();
  }
}

function persistState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

function makeId(prefix = "MEAS") {
  return `${prefix}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

function buildSlipIdFromState(state) {
  const dock = String(state?.dockName || "");
  const sep = String(state?.slipIdSeparator || "-");
  const num = String(state?.slipIdPart3 || "");
  if (!dock && !num) return "";
  if (!dock) return num;
  if (!num) return dock;
  return dock + sep + num;
}

function incrementSlipIdNumber(state) {
  if (state?.slipIdAutoIncrement !== true) return;
  const current = String(state?.slipIdPart3 || "").trim();
  const incrementBy = Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1);
  if (!/^\d+$/.test(current)) return;
  const width = current.length;
  const next = String((Number(current) || 0) + incrementBy);
  state.slipIdPart3 = next.length < width ? next.padStart(width, "0") : next;
}

function slipIdForDockNameChange(record, nextDockName, fallbackSeparator = "-") {
  const oldSlipId = String(record?.slip_id || "").trim();
  const oldDockName = String(record?.dock_name || "").trim();
  const nextDock = String(nextDockName || "").trim();
  const sep = String(fallbackSeparator || "-") || "-";
  if (!oldSlipId) return nextDock;

  if (oldDockName) {
    if (oldSlipId === oldDockName) return nextDock;
    if (oldSlipId.startsWith(oldDockName)) {
      const suffix = oldSlipId.slice(oldDockName.length);
      if (!suffix || /^[-.\s_]/.test(suffix)) return nextDock ? nextDock + suffix : suffix.replace(/^[-.\s_]+/, "");
    }
  }

  const separated = /^(.+?)([-.])(.+)$/.exec(oldSlipId);
  if (separated) return nextDock ? nextDock + separated[2] + separated[3] : separated[3];
  if (/^\d+$/.test(oldSlipId)) return nextDock ? nextDock + sep + oldSlipId : oldSlipId;
  return nextDock ? nextDock + sep + oldSlipId : oldSlipId;
}

function previewSlipIds(state, count) {
  const total = Math.max(1, Number(count || 0) || 0);
  const base = {
    dockName: String(state?.dockName || ""),
    slipIdSeparator: String(state?.slipIdSeparator || "-"),
    slipIdPart3: String(state?.slipIdPart3 || ""),
    slipIdAutoIncrement: state?.slipIdAutoIncrement !== false,
    slipIdIncrementBy: Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1),
  };
  const values = [];
  for (let index = 0; index < total; index += 1) {
    values.push(buildSlipIdFromState(base) || `SLIP-${String(index + 1).padStart(4, "0")}`);
    incrementSlipIdNumber(base);
  }
  return values;
}

function buildPointWkt(lng, lat) {
  return `POINT(${Number(lng).toFixed(6)} ${Number(lat).toFixed(6)})`;
}

function buildPolygonWkt(latLngRing) {
  const ring = Array.isArray(latLngRing) ? latLngRing : [];
  if (ring.length < 3) return "";
  const coords = ring.map((pair) => {
    const lat = Number(pair?.[0]);
    const lng = Number(pair?.[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? `${lng.toFixed(7)} ${lat.toFixed(7)}` : null;
  }).filter(Boolean);
  if (coords.length < 3) return "";
  if (coords[0] !== coords[coords.length - 1]) coords.push(coords[0]);
  return `POLYGON((${coords.join(", ")}))`;
}

function parsePolygonWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^POLYGON\s*\(\(\s*(.+?)\s*\)\)$/i.exec(text);
  if (!match) return null;
  const coords = String(match[1] || "")
    .split(",")
    .map((chunk) => String(chunk || "").trim())
    .map((chunk) => {
      const parts = chunk.split(/\s+/);
      if (parts.length < 2) return null;
      const lng = Number(parts[0]);
      const lat = Number(parts[1]);
      return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
    })
    .filter(Boolean);
  if (coords.length < 4) return null;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first && last && Number(first[0]) === Number(last[0]) && Number(first[1]) === Number(last[1])) {
    coords.pop();
  }
  return coords.length >= 3 ? coords : null;
}

function parsePointWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(text);
  if (!match) return null;
  const lng = Number(match[1]);
  const lat = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function parseLineStringWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^LINESTRING\s*\(\s*(.+?)\s*\)$/i.exec(text);
  if (!match) return null;
  const coords = String(match[1] || "")
    .split(",")
    .map((chunk) => String(chunk || "").trim())
    .map((chunk) => {
      const parts = chunk.split(/\s+/);
      if (parts.length < 2) return null;
      const lng = Number(parts[0]);
      const lat = Number(parts[1]);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    })
    .filter(Boolean);
  return coords.length >= 2 ? coords : null;
}

function geometryFromWkt(wkt) {
  const point = parsePointWkt(wkt);
  if (point) {
    return {
      type: "Point",
      coordinates: [Number(point.lng), Number(point.lat)],
    };
  }
  const line = parseLineStringWkt(wkt);
  if (Array.isArray(line) && line.length >= 2) {
    return {
      type: "LineString",
      coordinates: line.map((entry) => [Number(entry.lng), Number(entry.lat)]),
    };
  }
  const polygon = parsePolygonWkt(wkt);
  if (Array.isArray(polygon) && polygon.length >= 3) {
    const ring = polygon.map((pair) => [Number(pair[1]), Number(pair[0])]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!last || Number(first?.[0]) !== Number(last?.[0]) || Number(first?.[1]) !== Number(last?.[1])) {
      ring.push([Number(first?.[0]), Number(first?.[1])]);
    }
    return {
      type: "Polygon",
      coordinates: [ring],
    };
  }
  return null;
}

function polygonAreaCentroid(ring) {
  const n = ring.length;
  // Translate to origin first — without this, cross products of large geographic
  // coordinates (e.g. lat≈38, lng≈-122) lose all precision for marina-scale polygons.
  const refLat = Number(ring[0][0]);
  const refLng = Number(ring[0][1]);
  let A = 0;
  let cLat = 0;
  let cLng = 0;
  for (let i = 0; i < n; i++) {
    const dLat0 = Number(ring[i][0]) - refLat;
    const dLng0 = Number(ring[i][1]) - refLng;
    const dLat1 = Number(ring[(i + 1) % n][0]) - refLat;
    const dLng1 = Number(ring[(i + 1) % n][1]) - refLng;
    const cross = dLng0 * dLat1 - dLng1 * dLat0;
    A += cross;
    cLat += (dLat0 + dLat1) * cross;
    cLng += (dLng0 + dLng1) * cross;
  }
  A /= 2;
  if (Math.abs(A) < 1e-20) {
    const sum = ring.reduce((acc, p) => [acc[0] + Number(p[0]), acc[1] + Number(p[1])], [0, 0]);
    return { lat: sum[0] / n, lng: sum[1] / n };
  }
  return { lat: refLat + cLat / (6 * A), lng: refLng + cLng / (6 * A) };
}

function pointFromAnyWkt(wkt) {
  const point = parsePointWkt(wkt);
  if (point) return point;
  const line = parseLineStringWkt(wkt);
  if (Array.isArray(line) && line.length >= 2) {
    const mid = line[Math.floor(line.length / 2)];
    if (mid) return { lat: Number(mid.lat), lng: Number(mid.lng) };
  }
  const ring = parsePolygonWkt(wkt);
  if (!Array.isArray(ring) || ring.length < 3) return null;
  return polygonAreaCentroid(ring);
}

function inferPolyMetricsFromRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const ll = ring.map((pair) => L.latLng(Number(pair[0]), Number(pair[1]))).filter((entry) => Number.isFinite(entry.lat) && Number.isFinite(entry.lng));
  if (ll.length < 3) return null;
  const center = {
    lat: ll.reduce((sum, p) => sum + p.lat, 0) / ll.length,
    lng: ll.reduce((sum, p) => sum + p.lng, 0) / ll.length,
  };
  const cosLat = Math.max(0.000001, Math.cos((center.lat * Math.PI) / 180));
  const localMeters = ll.map((p) => ({
    x: (p.lng - center.lng) * (111320 * cosLat),
    y: (p.lat - center.lat) * 111320,
  }));
  const candidateAngles = [];
  for (let i = 0; i < localMeters.length; i += 1) {
    const a = localMeters[i];
    const b = localMeters[(i + 1) % localMeters.length];
    const dx = Number(b.x - a.x);
    const dy = Number(b.y - a.y);
    const segLen = Math.hypot(dx, dy);
    if (segLen < 0.01) continue;
    const angle = Math.atan2(dx, dy);
    const normalized = ((angle % Math.PI) + Math.PI) % Math.PI;
    if (!candidateAngles.some((value) => Math.abs(value - normalized) < 1e-6)) candidateAngles.push(normalized);
  }
  if (!candidateAngles.length) candidateAngles.push(0);
  let bestFit = null;
  for (const angle of candidateAngles) {
    const ux = Math.sin(angle);
    const uy = Math.cos(angle);
    const vx = Math.sin(angle + (Math.PI / 2));
    const vy = Math.cos(angle + (Math.PI / 2));
    const projections = localMeters.map((p) => ({
      along: (p.x * ux) + (p.y * uy),
      across: (p.x * vx) + (p.y * vy),
    }));
    const alongVals = projections.map((entry) => entry.along);
    const acrossVals = projections.map((entry) => entry.across);
    const alongRange = Math.max(...alongVals) - Math.min(...alongVals);
    const acrossRange = Math.max(...acrossVals) - Math.min(...acrossVals);
    const area = alongRange * acrossRange;
    if (!bestFit || area < bestFit.area) {
      bestFit = {
        angle,
        projections,
        alongRange,
        acrossRange,
        area,
      };
    }
  }
  const chosenAngle = Number(bestFit?.angle || 0);
  const alongRange = Number(bestFit?.alongRange || 0);
  const acrossRange = Number(bestFit?.acrossRange || 0);
  const headingRad = alongRange >= acrossRange ? chosenAngle : (chosenAngle + (Math.PI / 2));
  const headingDeg = (((headingRad * (180 / Math.PI)) % 360) + 360) % 360;
  const rad = headingRad;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const local = localMeters.map((p) => ({
    along: (p.x * ux) + (p.y * uy),
    across: (p.x * vx) + (p.y * vy),
  }));
  const alongVals = local.map((entry) => entry.along);
  const acrossVals = local.map((entry) => entry.across);
  const lengthM = Math.max(...alongVals) - Math.min(...alongVals);
  const widthM = Math.max(...acrossVals) - Math.min(...acrossVals);
  let chamferMagnitudeFt = 0;
  let chamferAngleDeg = 0;
  let chamferEnabled = false;
  let chamferSide = 1;
  if (local.length >= 6 && lengthM > 0.0001 && widthM > 0.0001) {
    const scoreChamferAtSide = (side) => {
      const byAlong = [...local].sort((a, b) => (
        side > 0 ? Number(b.along) - Number(a.along) : Number(a.along) - Number(b.along)
      ));
      const edgeMost = byAlong.slice(0, 2);
      const nextEdge = byAlong.slice(2, 4);
      if (edgeMost.length !== 2 || nextEdge.length !== 2) return null;
      const edgeAlongAvg = (Number(edgeMost[0].along) + Number(edgeMost[1].along)) / 2;
      const nextAlongAvg = (Number(nextEdge[0].along) + Number(nextEdge[1].along)) / 2;
      const cutLengthM = Math.abs(edgeAlongAvg - nextAlongAvg);
      const halfWidthM = widthM / 2;
      const edgeAcrossAbsAvg = (Math.abs(Number(edgeMost[0].across)) + Math.abs(Number(edgeMost[1].across))) / 2;
      const cutWidthM = Math.max(0, halfWidthM - edgeAcrossAbsAvg);
      if (cutWidthM <= 0.02 || cutLengthM <= 0.005) return null;
      return { side, cutWidthM, cutLengthM, score: cutWidthM * cutLengthM };
    };
    const candidates = [scoreChamferAtSide(1), scoreChamferAtSide(-1)].filter(Boolean);
    if (candidates.length) {
      const bestSide = candidates.sort((a, b) => Number(b.score) - Number(a.score))[0];
      chamferMagnitudeFt = roundToHalfFoot(metersToFeet(bestSide.cutWidthM));
      chamferAngleDeg = Math.max(0, Math.min(85, Math.round(Math.atan2(bestSide.cutLengthM, bestSide.cutWidthM) * (180 / Math.PI))));
      chamferEnabled = chamferMagnitudeFt > 0;
      chamferSide = Number(bestSide.side) < 0 ? -1 : 1;
    }
  }
  return {
    center,
    headingDeg,
    lengthFt: Math.max(1, metersToFeet(lengthM)),
    widthFt: Math.max(1, metersToFeet(widthM)),
    chamferEnabled,
    chamferSide,
    chamferAngleDeg,
    chamferMagnitudeFt,
  };
}

function transformRingLatLngsWithMetrics({
  ringLatLngs,
  oldCenter,
  newCenter,
  oldHeadingDeg,
  newHeadingDeg,
  oldLengthFt,
  newLengthFt,
  oldWidthFt,
  newWidthFt,
}) {
  const ring = Array.isArray(ringLatLngs) ? ringLatLngs : [];
  if (!ring.length) return null;
  const oc = { lat: Number(oldCenter?.lat), lng: Number(oldCenter?.lng) };
  const nc = { lat: Number(newCenter?.lat), lng: Number(newCenter?.lng) };
  if (!Number.isFinite(oc.lat) || !Number.isFinite(oc.lng) || !Number.isFinite(nc.lat) || !Number.isFinite(nc.lng)) return null;

  const oldHeadingRad = (Number(oldHeadingDeg || 0) * Math.PI) / 180;
  const newHeadingRad = (Number(newHeadingDeg || 0) * Math.PI) / 180;
  const oldUx = Math.sin(oldHeadingRad);
  const oldUy = Math.cos(oldHeadingRad);
  const oldVx = Math.sin(oldHeadingRad + (Math.PI / 2));
  const oldVy = Math.cos(oldHeadingRad + (Math.PI / 2));
  const newUx = Math.sin(newHeadingRad);
  const newUy = Math.cos(newHeadingRad);
  const newVx = Math.sin(newHeadingRad + (Math.PI / 2));
  const newVy = Math.cos(newHeadingRad + (Math.PI / 2));

  const oldLen = Math.max(0.0001, Number(oldLengthFt || 1));
  const oldWid = Math.max(0.0001, Number(oldWidthFt || 1));
  const newLen = Math.max(0.0001, Number(newLengthFt || oldLen));
  const newWid = Math.max(0.0001, Number(newWidthFt || oldWid));
  const lenScale = newLen / oldLen;
  const widScale = newWid / oldWid;
  const cosLat = Math.max(0.000001, Math.cos((oc.lat * Math.PI) / 180));

  const out = ring.map((pair) => {
    const lat = Number(pair?.[0]);
    const lng = Number(pair?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const dLatM = (lat - oc.lat) * 111320;
    const dLngM = (lng - oc.lng) * (111320 * cosLat);
    const along = ((dLngM * oldUx) + (dLatM * oldUy)) * lenScale;
    const across = ((dLngM * oldVx) + (dLatM * oldVy)) * widScale;
    const nextDlngM = (along * newUx) + (across * newVx);
    const nextDlatM = (along * newUy) + (across * newVy);
    const nextLat = nc.lat + (nextDlatM / 111320);
    const nextLng = nc.lng + (nextDlngM / (111320 * Math.max(0.000001, Math.cos((nc.lat * Math.PI) / 180))));
    return [nextLat, nextLng];
  }).filter(Boolean);
  return out.length >= 3 ? out : null;
}

function translateRingLatLngs(ringLatLngs, fromCenter, toCenter) {
  const ring = Array.isArray(ringLatLngs) ? ringLatLngs : [];
  if (!ring.length) return null;
  const fc = { lat: Number(fromCenter?.lat), lng: Number(fromCenter?.lng) };
  const tc = { lat: Number(toCenter?.lat), lng: Number(toCenter?.lng) };
  if (!Number.isFinite(fc.lat) || !Number.isFinite(fc.lng) || !Number.isFinite(tc.lat) || !Number.isFinite(tc.lng)) return null;
  const dLat = tc.lat - fc.lat;
  const dLng = tc.lng - fc.lng;
  const moved = ring
    .map((pair) => [Number(pair?.[0]) + dLat, Number(pair?.[1]) + dLng])
    .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
  return moved.length >= 3 ? moved : null;
}

function roundToHalfFoot(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0.5, Math.round(n * 2) / 2);
}

function roundToStep(value, step, min = 0) {
  const n = Number(value);
  const s = Number(step);
  if (!Number.isFinite(n)) return Number(min) || 0;
  if (!Number.isFinite(s) || s <= 0) return Math.max(Number(min) || 0, n);
  const rounded = Math.round(n / s) * s;
  const places = String(s).includes(".") ? String(s).split(".")[1].length : 0;
  return Math.max(Number(min) || 0, Number(rounded.toFixed(Math.min(6, places))));
}

function toFeetDistance(a, b) {
  const meters = Number(a?.distanceTo?.(b) || 0);
  return meters * 3.280839895;
}

function metersToFeet(meters) {
  return Number(meters || 0) * 3.280839895;
}

function feetToMeters(feet) {
  return Number(feet || 0) / 3.280839895;
}

function offsetPoint(center, dxMeters, dyMeters) {
  const lat = Number(center.lat);
  const lng = Number(center.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat: 0, lng: 0 };
  const dLat = dyMeters / 111320;
  const dLng = dxMeters / (111320 * Math.max(0.000001, Math.cos((lat * Math.PI) / 180)));
  return { lat: lat + dLat, lng: lng + dLng };
}

// ── Multislip geometry helpers ────────────────────────────────────────────────
function msDockvec(p1, p2) {
  // Unit vector (east/north) along dock + raw length in metres
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (p2.lng - p1.lng) * cosLat * 111320;
  const ny = (p2.lat - p1.lat) * 111320;
  const lenM = Math.sqrt(ex * ex + ny * ny);
  return lenM < 0.001 ? { ux: 1, uy: 0, lenM: 0 } : { ux: ex / lenM, uy: ny / lenM, lenM };
}

function msPerpDistFt(p1, p2, mouse) {
  // Signed perpendicular distance (feet) from dock line to mouse point.
  // Positive = left when facing p1→p2; negative = right.
  const { ux, uy } = msDockvec(p1, p2);
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (mouse.lng - p1.lng) * cosLat * 111320;
  const ny = (mouse.lat - p1.lat) * 111320;
  return (-uy * ex + ux * ny) * 3.280839895; // dot with left-normal (-uy, ux)
}

function msRectCorners(p1, p2, widthFt) {
  // Rectangle: dock line + perpendicular extrusion. Returns [p1, p2, c3, c4].
  const { ux, uy } = msDockvec(p1, p2);
  const wM = widthFt / 3.280839895;
  const c3 = offsetPoint(p2, -uy * wM, ux * wM);
  const c4 = offsetPoint(p1, -uy * wM, ux * wM);
  return [p1, p2, c3, c4];
}

function msSlotCentroid(p1, p2, widthFt, i, N) {
  const t = (i + 0.5) / N;
  const along = { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
  const { ux, uy } = msDockvec(p1, p2);
  const halfM = widthFt / 2 / 3.280839895;
  return offsetPoint(along, -uy * halfM, ux * halfM);
}

function msSlotCorners(p1, p2, widthFt, i, N) {
  const t1 = i / N, t2 = (i + 1) / N;
  const sp1 = { lat: p1.lat + t1 * (p2.lat - p1.lat), lng: p1.lng + t1 * (p2.lng - p1.lng) };
  const sp2 = { lat: p1.lat + t2 * (p2.lat - p1.lat), lng: p1.lng + t2 * (p2.lng - p1.lng) };
  return msRectCorners(sp1, sp2, widthFt);
}
function msSlotCornersPerLen(p1, p2, slotLenFt, i, N) {
  const t1 = i / N, t2 = (i + 1) / N;
  const sp1 = { lat: p1.lat + t1 * (p2.lat - p1.lat), lng: p1.lng + t1 * (p2.lng - p1.lng) };
  const sp2 = { lat: p1.lat + t2 * (p2.lat - p1.lat), lng: p1.lng + t2 * (p2.lng - p1.lng) };
  return msRectCorners(sp1, sp2, slotLenFt);
}
function msRotateAroundPivot(point, pivot, fromDir, toDir) {
  // Rotate `point` around `pivot` by the angle delta from (pivot→fromDir) to (pivot→toDir).
  const cosLat = Math.cos(pivot.lat * Math.PI / 180);
  const toVec = (pt) => ({ e: (pt.lng - pivot.lng) * cosLat * 111320, n: (pt.lat - pivot.lat) * 111320 });
  const f = toVec(fromDir), t = toVec(toDir), p = toVec(point);
  const dTheta = Math.atan2(t.n, t.e) - Math.atan2(f.n, f.e);
  const cos = Math.cos(dTheta), sin = Math.sin(dTheta);
  return {
    lat: pivot.lat + (p.e * sin + p.n * cos) / 111320,
    lng: pivot.lng + (p.e * cos - p.n * sin) / (cosLat * 111320),
  };
}
function msSlotOffshorePoint(p1, p2, slotLenFt, i, N) {
  const t = (i + 0.5) / N;
  const along = { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
  const { ux, uy } = msDockvec(p1, p2);
  const wM = slotLenFt / 3.280839895;
  return offsetPoint(along, -uy * wM, ux * wM);
}

// ─────────────────────────────────────────────────────────────────────────────

function computeSlipPolygonLatLngs(poly) {
  if (Array.isArray(poly?.ringLatLngs) && poly.ringLatLngs.length >= 3) {
    return poly.ringLatLngs
      .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
      .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
  }
  const center = { lat: Number(poly?.center?.lat), lng: Number(poly?.center?.lng) };
  const lengthM = feetToMeters(poly?.lengthFt || 0);
  const widthM = feetToMeters(poly?.widthFt || 0);
  const headingDeg = Number(poly?.headingDeg || 0);
  const rad = (headingDeg * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const hl = lengthM / 2;
  const hw = widthM / 2;
  const localCorners = [
    { x: hl, y: hw },
    { x: hl, y: -hw },
    { x: -hl, y: -hw },
    { x: -hl, y: hw },
  ];
  let localPolygon = localCorners.slice();
  const chamferEnabled = !!poly?.chamferEnabled;
  const chamferSide = Number(poly?.chamferSide || 1) < 0 ? -1 : 1;
  const chamferMagnitudeM = feetToMeters(Number(poly?.chamferMagnitudeFt || 0));
  const chamferAngleDeg = Math.max(0, Math.min(85, Number(poly?.chamferAngleDeg || 0)));
  if (chamferEnabled && chamferMagnitudeM > 0.0001) {
    // Chamfer corners on chosen short end (x = +hl or x = -hl).
    const cutWidth = Math.min(hw * 0.95, chamferMagnitudeM);
    const cutLength = Math.min(hl * 0.95, cutWidth * Math.tan((chamferAngleDeg * Math.PI) / 180));
    if (chamferSide > 0) {
      const c0 = localCorners[0];
      const c1 = localCorners[1];
      localPolygon = [
        { x: c0.x - cutLength, y: c0.y },
        { x: c0.x, y: c0.y - cutWidth },
        { x: c1.x, y: c1.y + cutWidth },
        { x: c1.x - cutLength, y: c1.y },
        localCorners[2],
        localCorners[3],
      ];
    } else {
      const c2 = localCorners[2];
      const c3 = localCorners[3];
      localPolygon = [
        localCorners[0],
        localCorners[1],
        { x: c2.x + cutLength, y: c2.y },
        { x: c2.x, y: c2.y + cutWidth },
        { x: c3.x, y: c3.y - cutWidth },
        { x: c3.x + cutLength, y: c3.y },
      ];
    }
  }
  const corners = localPolygon.map((point) => ({
    x: (ux * point.x) + (vx * point.y),
    y: (uy * point.x) + (vy * point.y),
  }));
  return corners.map((corner) => {
    const ll = offsetPoint(center, corner.x, corner.y);
    return [ll.lat, ll.lng];
  });
}

function computeParametricSlipPolygonLatLngs({
  center,
  lengthFt,
  widthFt,
  headingDeg,
  chamferEnabled,
  chamferSide,
  chamferAngleDeg,
  chamferMagnitudeFt,
}) {
  const c = { lat: Number(center?.lat), lng: Number(center?.lng) };
  const lengthM = feetToMeters(lengthFt || 0);
  const widthM = feetToMeters(widthFt || 0);
  const heading = Number(headingDeg || 0);
  const rad = (heading * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const hl = lengthM / 2;
  const hw = widthM / 2;
  const localCorners = [
    { x: hl, y: hw },
    { x: hl, y: -hw },
    { x: -hl, y: -hw },
    { x: -hl, y: hw },
  ];
  let localPolygon = localCorners.slice();
  const chamferOn = !!chamferEnabled;
  const side = Number(chamferSide || 1) < 0 ? -1 : 1;
  const chamferMagnitudeM = feetToMeters(Number(chamferMagnitudeFt || 0));
  const chamferAngle = Math.max(0, Math.min(85, Number(chamferAngleDeg || 0)));
  if (chamferOn && chamferMagnitudeM > 0.0001) {
    const cutWidth = Math.min(hw * 0.95, chamferMagnitudeM);
    const cutLength = Math.min(hl * 0.95, cutWidth * Math.tan((chamferAngle * Math.PI) / 180));
    if (side > 0) {
      const c0 = localCorners[0];
      const c1 = localCorners[1];
      localPolygon = [
        { x: c0.x - cutLength, y: c0.y },
        { x: c0.x, y: c0.y - cutWidth },
        { x: c1.x, y: c1.y + cutWidth },
        { x: c1.x - cutLength, y: c1.y },
        localCorners[2],
        localCorners[3],
      ];
    } else {
      const c2 = localCorners[2];
      const c3 = localCorners[3];
      localPolygon = [
        localCorners[0],
        localCorners[1],
        { x: c2.x + cutLength, y: c2.y },
        { x: c2.x, y: c2.y + cutWidth },
        { x: c3.x, y: c3.y - cutWidth },
        { x: c3.x + cutLength, y: c3.y },
      ];
    }
  }
  const corners = localPolygon.map((point) => ({
    x: (ux * point.x) + (vx * point.y),
    y: (uy * point.x) + (vy * point.y),
  }));
  return corners.map((corner) => {
    const ll = offsetPoint(c, corner.x, corner.y);
    return [ll.lat, ll.lng];
  });
}

function pointInPolygon(point, ring) {
  if (!point || !Array.isArray(ring) || ring.length < 3) return false;
  const x = Number(point.lng);
  const y = Number(point.lat);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][1]);
    const yi = Number(ring[i][0]);
    const xj = Number(ring[j][1]);
    const yj = Number(ring[j][0]);
    const intersects = ((yi > y) !== (yj > y))
      && (x < (((xj - xi) * (y - yi)) / Math.max(1e-12, (yj - yi)) + xi));
    if (intersects) inside = !inside;
  }
  return inside;
}

function ringSimilarityScoreMeters(ringA, ringB) {
  if (!Array.isArray(ringA) || !Array.isArray(ringB) || ringA.length < 3 || ringB.length < 3) return Number.POSITIVE_INFINITY;
  const aa = ringA
    .map((pair) => L.latLng(Number(pair?.[0]), Number(pair?.[1])))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  const bb = ringB
    .map((pair) => L.latLng(Number(pair?.[0]), Number(pair?.[1])))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (aa.length < 3 || bb.length < 3) return Number.POSITIVE_INFINITY;
  const minTo = (p, set) => set.reduce((best, q) => Math.min(best, Number(p.distanceTo(q) || 0)), Number.POSITIVE_INFINITY);
  const aToB = aa.reduce((sum, p) => sum + minTo(p, bb), 0) / aa.length;
  const bToA = bb.reduce((sum, p) => sum + minTo(p, aa), 0) / bb.length;
  return aToB + bToA;
}

function makeGeometryId() {
  return `GEOM-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

function bearingDegrees(start, end) {
  const lat1 = (Number(start?.lat || 0) * Math.PI) / 180;
  const lat2 = (Number(end?.lat || 0) * Math.PI) / 180;
  const dLon = ((Number(end?.lng || 0) - Number(start?.lng || 0)) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = (Math.cos(lat1) * Math.sin(lat2))
    - (Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon));
  const brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function movingAverage(values, radius = 2) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length || radius <= 0) return source.slice();
  return source.map((_, index) => {
    let total = 0;
    let count = 0;
    const start = Math.max(0, index - radius);
    const end = Math.min(source.length - 1, index + radius);
    for (let cursor = start; cursor <= end; cursor += 1) {
      total += Number(source[cursor]) || 0;
      count += 1;
    }
    return count ? total / count : 0;
  });
}

function seriesStats(values) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return { mean: 0, stdDev: 0 };
  const mean = source.reduce((sum, value) => sum + (Number(value) || 0), 0) / source.length;
  const variance = source.reduce((sum, value) => {
    const delta = (Number(value) || 0) - mean;
    return sum + (delta * delta);
  }, 0) / source.length;
  return { mean, stdDev: Math.sqrt(Math.max(0, variance)) };
}

function medianValue(values) {
  const nums = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : ((nums[mid - 1] + nums[mid]) / 2);
}

function detectSignalPeaks(signal, { minDistance = 6, minProminence = 0 } = {}) {
  const values = Array.isArray(signal) ? signal : [];
  const distance = Math.max(1, Math.round(Number(minDistance) || 1));
  const prominenceFloor = Math.max(0, Number(minProminence) || 0);
  const candidates = [];
  for (let index = 1; index < values.length - 1; index += 1) {
    const current = Number(values[index]) || 0;
    const prev = Number(values[index - 1]) || 0;
    const next = Number(values[index + 1]) || 0;
    if (!(current > prev && current >= next)) continue;
    const leftBase = Math.min(prev, Number(values[Math.max(0, index - distance)]) || prev);
    const rightBase = Math.min(next, Number(values[Math.min(values.length - 1, index + distance)]) || next);
    const prominence = current - Math.max(leftBase, rightBase);
    if (prominence < prominenceFloor) continue;
    candidates.push({ index, value: current, prominence });
  }
  candidates.sort((a, b) => b.prominence - a.prominence);
  const selected = [];
  for (const candidate of candidates) {
    if (selected.some((entry) => Math.abs(entry.index - candidate.index) < distance)) continue;
    selected.push(candidate);
  }
  return selected.sort((a, b) => a.index - b.index);
}

function sampleCanvasLuma(imageData, width, height, x, y) {
  const maxX = Math.max(0, width - 1);
  const maxY = Math.max(0, height - 1);
  const fx = clampNumber(Number(x) || 0, 0, maxX);
  const fy = clampNumber(Number(y) || 0, 0, maxY);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(maxX, x0 + 1);
  const y1 = Math.min(maxY, y0 + 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const lumaAt = (px, py) => {
    const offset = ((py * width) + px) * 4;
    const r = Number(imageData[offset] || 0);
    const g = Number(imageData[offset + 1] || 0);
    const b = Number(imageData[offset + 2] || 0);
    return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
  };
  const l00 = lumaAt(x0, y0);
  const l10 = lumaAt(x1, y0);
  const l01 = lumaAt(x0, y1);
  const l11 = lumaAt(x1, y1);
  const top = l00 + ((l10 - l00) * tx);
  const bottom = l01 + ((l11 - l01) * tx);
  return top + ((bottom - top) * ty);
}

function sampleCanvasAlpha(imageData, width, height, x, y) {
  const maxX = Math.max(0, width - 1);
  const maxY = Math.max(0, height - 1);
  const fx = clampNumber(Number(x) || 0, 0, maxX);
  const fy = clampNumber(Number(y) || 0, 0, maxY);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(maxX, x0 + 1);
  const y1 = Math.min(maxY, y0 + 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const alphaAt = (px, py) => {
    const offset = ((py * width) + px) * 4;
    return Number(imageData[offset + 3] || 0);
  };
  const a00 = alphaAt(x0, y0);
  const a10 = alphaAt(x1, y0);
  const a01 = alphaAt(x0, y1);
  const a11 = alphaAt(x1, y1);
  const top = a00 + ((a10 - a00) * tx);
  const bottom = a01 + ((a11 - a01) * tx);
  return top + ((bottom - top) * ty);
}

function buildImagerySpacingSignal(imageData, width, height, startPoint, endPoint, { sampleCount, crossSampleCount, corridorHalfWidthPx }) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(5, Number(crossSampleCount) || 13);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    let edgeEnergy = 0;
    let previous = null;
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      const luma = sampleCanvasLuma(imageData, width, height, sampleX, sampleY);
      if (previous != null) edgeEnergy += Math.abs(luma - previous);
      previous = luma;
    }
    values.push(edgeEnergy);
  }
  return movingAverage(values, 2);
}

function percentile(values, pct) {
  const source = (Array.isArray(values) ? values : []).filter((v) => Number.isFinite(Number(v))).map(Number).sort((a, b) => a - b);
  if (!source.length) return null;
  const p = clampNumber(Number(pct) || 0.5, 0, 1);
  const idx = clampNumber(Math.round((source.length - 1) * p), 0, source.length - 1);
  return source[idx];
}

function buildImagerySpacingSignalRobust(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  darkPercentile = 0.3,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(9, Number(crossSampleCount) || 25);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    const lumas = [];
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
    const darkValue = percentile(lumas, darkPercentile);
    values.push(Number.isFinite(Number(darkValue)) ? Number(darkValue) : 0);
  }
  return movingAverage(values, 2);
}

function buildImagerySpacingSignalBinary(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  alphaThreshold = 10,
  darkPercentile = 0.35,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(11, Number(crossSampleCount) || 33);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    const lumas = [];
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      const alpha = sampleCanvasAlpha(imageData, width, height, sampleX, sampleY);
      if (alpha < alphaThreshold) continue;
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
    if (!lumas.length) {
      values.push(0);
      continue;
    }
    const darkCutoff = percentile(lumas, darkPercentile);
    if (!Number.isFinite(Number(darkCutoff))) {
      values.push(0);
      continue;
    }
    const darkHits = lumas.filter((value) => Number(value) <= Number(darkCutoff)).length;
    values.push(darkHits / Math.max(1, lumas.length));
  }
  return movingAverage(values, 2);
}

function estimateSignalCoverage(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  alphaThreshold = 10,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return { coverageRatio: 0, meanLuma: 0, lumaStdDev: 0 };
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(9, Number(crossSampleCount) || 25);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  let total = 0;
  let covered = 0;
  const lumas = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      total += 1;
      const alpha = sampleCanvasAlpha(imageData, width, height, sampleX, sampleY);
      if (alpha < alphaThreshold) continue;
      covered += 1;
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
  }
  const lumaStats = seriesStats(lumas);
  return {
    coverageRatio: total > 0 ? (covered / total) : 0,
    meanLuma: Number.isFinite(lumaStats.mean) ? lumaStats.mean : 0,
    lumaStdDev: Number.isFinite(lumaStats.stdDev) ? lumaStats.stdDev : 0,
  };
}

function estimateSignalLag(signal, { minLag, maxLag }) {
  const source = Array.isArray(signal) ? signal : [];
  if (source.length < 8) return null;
  const { mean, stdDev } = seriesStats(source);
  if (!Number.isFinite(stdDev) || stdDev < 1e-6) return null;
  const normalized = source.map((value) => ((Number(value) || 0) - mean) / stdDev);
  const startLag = clampNumber(Math.round(Number(minLag) || 0), 1, Math.max(1, source.length - 2));
  const endLag = clampNumber(Math.round(Number(maxLag) || 0), startLag, Math.max(startLag, source.length - 2));
  let best = null;
  const scores = [];
  for (let lag = startLag; lag <= endLag; lag += 1) {
    let total = 0;
    let count = 0;
    for (let index = 0; index + lag < normalized.length; index += 1) {
      total += normalized[index] * normalized[index + lag];
      count += 1;
    }
    if (!count) continue;
    const score = total / count;
    scores.push(score);
    if (!best || score > best.score) best = { lag, score };
  }
  if (!best) return null;
  const sortedScores = scores.slice().sort((a, b) => b - a);
  const secondBest = sortedScores[1] ?? sortedScores[0] ?? 0;
  const cycles = source.length / Math.max(1, best.lag);
  const sharpness = Math.max(0, best.score - secondBest);
  const confidence = clampNumber(((best.score - 0.15) / 0.85), 0, 1)
    * clampNumber((cycles - 2) / 5, 0, 1)
    * clampNumber((sharpness + 0.04) / 0.22, 0, 1);
  return {
    lag: best.lag,
    score: best.score,
    confidence,
  };
}

function estimatePitchFromSignal(signal, { minLag, maxLag }) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 8) return null;
  const stats = seriesStats(values);
  const smoothed = movingAverage(values, 1);
  const peaks = detectSignalPeaks(smoothed, {
    minDistance: Math.max(3, Math.round((Number(minLag) || 6) * 0.65)),
    minProminence: Math.max(0.5, stats.stdDev * 0.22),
  });
  const peakIntervals = [];
  for (let index = 1; index < peaks.length; index += 1) {
    const delta = peaks[index].index - peaks[index - 1].index;
    if (delta >= minLag * 0.5 && delta <= maxLag * 1.5) peakIntervals.push(delta);
  }
  const peakMedian = medianValue(peakIntervals);
  const lagEstimate = estimateSignalLag(smoothed, { minLag, maxLag });
  const autocorrLag = lagEstimate?.lag || null;
  let chosenLag = null;
  let method = "";
  if (peakMedian && autocorrLag) {
    const ratio = autocorrLag / Math.max(1e-6, peakMedian);
    if (ratio > 1.75 && ratio < 2.25) {
      chosenLag = peakMedian;
      method = "peaks";
    } else if (ratio > 0.45 && ratio < 0.75) {
      chosenLag = autocorrLag;
      method = "autocorr";
    } else {
      chosenLag = Math.abs(peakMedian - autocorrLag) <= (minLag * 0.45) ? ((peakMedian + autocorrLag) / 2) : peakMedian;
      method = "hybrid";
    }
  } else if (peakMedian) {
    chosenLag = peakMedian;
    method = "peaks";
  } else if (autocorrLag) {
    chosenLag = autocorrLag;
    method = "autocorr";
  } else {
    return null;
  }
  chosenLag = clampNumber(chosenLag, minLag, maxLag);
  const intervalStats = seriesStats(peakIntervals);
  const peakConsistency = peakIntervals.length >= 2 && peakMedian
    ? clampNumber(1 - ((intervalStats.stdDev || 0) / Math.max(peakMedian, 1)), 0, 1)
    : 0;
  const confidence = clampNumber(Math.max(lagEstimate?.confidence || 0, peakConsistency * 0.9), 0, 1);
  return {
    lag: chosenLag,
    confidence,
    method,
    peakCount: peaks.length,
  };
}

function median(values) {
  const source = (Array.isArray(values) ? values : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!source.length) return null;
  const mid = Math.floor(source.length / 2);
  if (source.length % 2) return source[mid];
  return (source[mid - 1] + source[mid]) / 2;
}

function estimatePitchFromPeaks(signal, { minLag, maxLag, seedLag = null }) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 20) return null;
  const stats = seriesStats(values);
  if (!Number.isFinite(stats.stdDev) || stats.stdDev < 1e-6) return null;
  const minDistance = Math.max(4, Math.round((Number(minLag) || 8) * 0.55));
  const minProminence = Math.max(0.015, stats.stdDev * 0.24);
  const peaks = detectSignalPeaks(values, { minDistance, minProminence });
  if (!peaks || peaks.length < 3) return null;
  const diffs = [];
  for (let i = 1; i < peaks.length; i += 1) {
    const delta = Number(peaks[i].index) - Number(peaks[i - 1].index);
    if (!Number.isFinite(delta)) continue;
    if (delta < Number(minLag) * 0.7 || delta > Number(maxLag) * 1.15) continue;
    diffs.push(delta);
  }
  if (diffs.length < 2) return null;
  const lagMedian = median(diffs);
  if (!Number.isFinite(lagMedian) || lagMedian <= 0) return null;
  let lag = lagMedian;
  if (Number.isFinite(Number(seedLag)) && Number(seedLag) > 0) {
    const sortedBySeed = diffs
      .slice()
      .sort((a, b) => Math.abs(a - Number(seedLag)) - Math.abs(b - Number(seedLag)));
    const nearestSeedLag = sortedBySeed[0];
    if (Number.isFinite(nearestSeedLag)) {
      const nearSeedConfidence = clampNumber(1 - (Math.abs(nearestSeedLag - Number(seedLag)) / Math.max(1, Number(seedLag))), 0, 1);
      const medianConfidence = clampNumber(1 - (Math.abs(lagMedian - Number(seedLag)) / Math.max(1, Number(seedLag))), 0, 1);
      if (nearSeedConfidence >= Math.max(0.45, medianConfidence - 0.1)) {
        lag = nearestSeedLag;
      }
    }
  }
  const spread = seriesStats(diffs).stdDev || 0;
  const relSpread = spread / Math.max(1e-6, lag);
  const confidence = clampNumber((1 - relSpread) * clampNumber(diffs.length / 8, 0, 1), 0, 1);
  return {
    lag,
    score: confidence,
    confidence,
    method: "peak-interval",
    samples: diffs.length,
  };
}

function deriveSlipCentersFromDockPeaks(signal, {
  minLag,
  maxLag,
  seedLag = null,
  sampleCount = 0,
} = {}) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 20 || sampleCount < 20) return null;
  const stats = seriesStats(values);
  if (!Number.isFinite(stats.stdDev) || stats.stdDev < 1e-6) return null;
  const peaks = detectSignalPeaks(values, {
    minDistance: Math.max(3, Math.round((Number(minLag) || 8) * 0.4)),
    minProminence: Math.max(0.006, stats.stdDev * 0.09),
  });
  if (!peaks || peaks.length < 3) return null;
  const intervals = [];
  for (let i = 1; i < peaks.length; i += 1) {
    const delta = Number(peaks[i].index) - Number(peaks[i - 1].index);
    if (!Number.isFinite(delta)) continue;
    if (delta < Number(minLag) * 0.55 || delta > Number(maxLag) * 1.2) continue;
    intervals.push({ delta, left: peaks[i - 1].index, right: peaks[i].index });
  }
  if (intervals.length < 2) return null;
  let selected = intervals;
  if (Number.isFinite(Number(seedLag)) && Number(seedLag) > 0) {
    const seed = Number(seedLag);
    const nearSeed = intervals.filter((entry) => Math.abs(entry.delta - seed) <= Math.max(3, seed * 0.45));
    if (nearSeed.length >= 4) selected = nearSeed;
  }
  const deltas = selected.map((entry) => entry.delta);
  const medianLag = medianValue(deltas);
  if (!Number.isFinite(medianLag) || medianLag <= 0) return null;
  const filtered = selected.filter((entry) => Math.abs(entry.delta - medianLag) <= Math.max(3, medianLag * 0.22));
  const stable = filtered.length >= Math.max(4, Math.round(selected.length * 0.55)) ? filtered : selected;
  const centerRatios = stable
    .map((entry) => ((Number(entry.left) + Number(entry.right)) / 2) / Math.max(1, sampleCount - 1))
    .filter((ratio) => Number.isFinite(ratio))
    .map((ratio) => clampNumber(ratio, 0, 1));
  if (centerRatios.length < 2) return null;
  const spread = seriesStats(stable.map((entry) => entry.delta)).stdDev || 0;
  const confidence = clampNumber((1 - (spread / Math.max(1e-6, medianLag))) * clampNumber(stable.length / 8, 0, 1), 0, 1);
  return {
    centerRatios,
    lag: medianLag,
    confidence,
    intervalsUsed: stable.length,
    peakCount: peaks.length,
  };
}

function chooseConsensusPitchEstimate(estimates = []) {
  const candidates = (Array.isArray(estimates) ? estimates : [])
    .filter((entry) => entry && Number.isFinite(entry.lag) && entry.lag > 0 && Number.isFinite(entry.confidence));
  if (!candidates.length) return null;
  const toleranceFor = (lag) => Math.max(3, Number(lag) * 0.18);
  let bestGroup = null;
  candidates.forEach((candidate) => {
    const group = candidates.filter((entry) => Math.abs(entry.lag - candidate.lag) <= toleranceFor(candidate.lag));
    const support = group.reduce((sum, entry) => sum + Math.max(0.05, Number(entry.confidence) || 0), 0);
    const weightedLag = group.reduce((sum, entry) => sum + (entry.lag * Math.max(0.05, Number(entry.confidence) || 0)), 0) / Math.max(1e-6, support);
    if (!bestGroup || support > bestGroup.support) {
      bestGroup = {
        support,
        weightedLag,
        members: group,
      };
    }
  });
  if (!bestGroup) return candidates[0];
  const bestConfidence = clampNumber(bestGroup.support / Math.max(1, bestGroup.members.length), 0, 1);
  const methods = Array.from(new Set(bestGroup.members.map((entry) => entry.method).filter(Boolean)));
  return {
    lag: bestGroup.weightedLag,
    confidence: bestConfidence,
    method: methods.join("+") || "consensus",
    samples: bestGroup.members.length,
  };
}

function buildSparklinePath(values, width, height, padding = 10) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return "";
  const minValue = Math.min(...source);
  const maxValue = Math.max(...source);
  const range = Math.max(1e-6, maxValue - minValue);
  return source.map((value, index) => {
    const x = padding + ((index / Math.max(1, source.length - 1)) * (width - (padding * 2)));
    const y = height - padding - (((value - minValue) / range) * (height - (padding * 2)));
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function estimateWidthFromPerpendicularProfile(imageData, width, height, centerPoint, axisUnit, pixelsPerFoot, priorWidthFt = null) {
  const nx = -Number(axisUnit.y);
  const ny = Number(axisUnit.x);
  const maxHalfWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 26), 18, 120);
  let minWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 6), 6, 90);
  let maxWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 28), minWidthPx + 6, 220);
  const priorPx = Number.isFinite(Number(priorWidthFt))
    ? Number(priorWidthFt) * Math.max(1e-6, Number(pixelsPerFoot) || 1)
    : null;
  if (Number.isFinite(priorPx) && priorPx > 0) {
    minWidthPx = clampNumber(Math.round(priorPx * 0.6), 6, maxHalfWidthPx * 2);
    maxWidthPx = clampNumber(Math.round(priorPx * 1.5), minWidthPx + 4, maxHalfWidthPx * 2);
  }
  const samples = [];
  for (let offset = -maxHalfWidthPx; offset <= maxHalfWidthPx; offset += 1) {
    const x = Number(centerPoint.x) + (nx * offset);
    const y = Number(centerPoint.y) + (ny * offset);
    const luma = sampleCanvasLuma(imageData, width, height, x, y);
    samples.push({ offset, luma });
  }
  if (samples.length < 8) return null;
  const smooth = samples.map((sample, idx) => {
    const prev = samples[Math.max(0, idx - 1)].luma;
    const curr = sample.luma;
    const next = samples[Math.min(samples.length - 1, idx + 1)].luma;
    return (prev + curr + next) / 3;
  });
  const gradients = [];
  for (let i = 1; i < smooth.length; i += 1) {
    gradients.push({
      leftOffset: samples[i - 1].offset,
      rightOffset: samples[i].offset,
      magnitude: Math.abs(smooth[i] - smooth[i - 1]),
    });
  }
  const leftEdges = gradients.filter((g) => g.rightOffset < 0).sort((a, b) => b.magnitude - a.magnitude).slice(0, 20);
  const rightEdges = gradients.filter((g) => g.leftOffset > 0).sort((a, b) => b.magnitude - a.magnitude).slice(0, 20);
  const centerIndex = samples.findIndex((entry) => Number(entry.offset) === 0);
  const sampleLumaAt = (offset) => {
    const idx = clampNumber(Math.round(offset + maxHalfWidthPx), 0, smooth.length - 1);
    return smooth[idx];
  };
  let best = null;
  leftEdges.forEach((left) => {
    rightEdges.forEach((right) => {
      const span = Math.abs(Number(right.rightOffset) - Number(left.leftOffset));
      if (!Number.isFinite(span) || span < minWidthPx || span > maxWidthPx) return;
      const leftIdx = clampNumber(Math.round(Number(left.leftOffset) + maxHalfWidthPx), 0, smooth.length - 1);
      const rightIdx = clampNumber(Math.round(Number(right.rightOffset) + maxHalfWidthPx), 0, smooth.length - 1);
      if (rightIdx <= leftIdx + 2) return;
      const interior = smooth.slice(leftIdx + 1, rightIdx);
      if (!interior.length) return;
      const interiorMean = interior.reduce((sum, value) => sum + Number(value || 0), 0) / interior.length;
      const edgeOuter = (sampleLumaAt(Number(left.leftOffset) - 3) + sampleLumaAt(Number(right.rightOffset) + 3)) / 2;
      const darknessContrast = edgeOuter - interiorMean;
      const centerBias = centerIndex >= 0
        ? 1 - (Math.abs(((leftIdx + rightIdx) / 2) - centerIndex) / Math.max(1, smooth.length * 0.5))
        : 0.5;
      const priorBias = Number.isFinite(priorPx) && priorPx > 0
        ? 1 - (Math.abs(span - priorPx) / Math.max(priorPx, 1))
        : 0.5;
      const score = (Number(left.magnitude || 0) + Number(right.magnitude || 0))
        + (Math.max(0, darknessContrast) * 1.8)
        + (Math.max(0, centerBias) * 4)
        + (Math.max(0, priorBias) * 3);
      if (!best || score > best.score) best = { spanPx: span, score };
    });
  });
  return best?.spanPx ? best : null;
}

function normalizeRecord(record) {
  const source = record && typeof record === "object" ? record : {};
  const rawMeasurementDate = String(source.measurement_date || "").trim();
  const measurementDate = rawMeasurementDate.includes("T")
    ? rawMeasurementDate.slice(0, 10)
    : rawMeasurementDate;
  const optText = (v) => (v == null ? "" : String(v));
  const optNum = (v) => (v == null || v === "" ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
  return {
    measurement_id: String(source.measurement_id || ""),
    marina_id: String(source.marina_id || ""),
    marina_name: String(source.marina_name || ""),
    accepted_adjusted: optText(source.accepted_adjusted),
    dock_name: optText(source.dock_name),
    adjusted_dock: optText(source.adjusted_dock),
    slip_id: String(source.slip_id || ""),
    adjusted_slip_id: optText(source.adjusted_slip_id),
    multislip_yn: optText(source.multislip_yn),
    multislip_id: optText(source.multislip_id),
    adjusted_multislip_id: optText(source.adjusted_multislip_id),
    space_type: optText(source.space_type),
    adjusted_space_type: optText(source.adjusted_space_type),
    covered_yn: optText(source.covered_yn),
    adjusted_covered: optText(source.adjusted_covered),
    boatlift_yn: optText(source.boatlift_yn),
    fueldock_yn: optText(source.fueldock_yn),
    out_of_service: optText(source.out_of_service),
    out_of_service_reason: optText(source.out_of_service_reason),
    measured_linear_feet: source.measured_linear_feet == null || source.measured_linear_feet === "" ? null : roundToTenthFoot(source.measured_linear_feet),
    marketable_linear_feet: optNum(source.marketable_linear_feet) != null ? roundToTenthFoot(source.marketable_linear_feet) : null,
    measured_width_feet: source.measured_width_feet == null || source.measured_width_feet === "" ? null : roundToTenthFoot(source.measured_width_feet),
    marketable_width_feet: optNum(source.marketable_width_feet) != null ? roundToTenthFoot(source.marketable_width_feet) : null,
    marina_comments: optText(source.marina_comments),
    measured_depth_feet: source.measured_depth_feet == null || source.measured_depth_feet === "" ? null : roundToTenthFoot(source.measured_depth_feet),
    measurement_date: measurementDate,
    geometry_wkt: String(source.geometry_wkt || ""),
    confidence_score: String(source.confidence_score || "High"),
    record_created_timestamp: String(source.record_created_timestamp || ""),
  };
}

const DIMENSION_COLOR_CACHE = new Map();
const SPACE_TYPE_PALETTE = [
  "#ef4444", "#3b82f6", "#22c55e", "#06b6d4", "#a855f7",
  "#14b8a6", "#f97316", "#ec4899", "#84cc16", "#06b6d4",
  "#eab308", "#8b5cf6",
];
// Fixed, locked color assignments for known space types — adding new types never shifts these
const SPACE_TYPE_COLOR_MAP = {
  "Side Tie":    "#ef4444",
  "Pilings":     "#3b82f6",
  "Finger":      "#22c55e",
  "Med-Mooring": "#06b6d4",
  "Travel Lift": "#a855f7",
  "Boat Ramp":   "#14b8a6",
  "Dock":        "#f97316",
  "Other":       "#ec4899",
  "unknown":     "#84cc16",
};

function dimensionKey(lengthFt, widthFt, toleranceFt = 0.5) {
  const normalize = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const tol = [0.5, 1].includes(Number(toleranceFt)) ? Number(toleranceFt) : 0.5;
    // Group by configurable tolerance bins for color classes.
    return Math.round(n / tol) * tol;
  };
  const l = Number(lengthFt);
  const w = Number(widthFt);
  if (!Number.isFinite(l) || !Number.isFinite(w)) return "";
  const ln = normalize(l);
  const wn = normalize(w);
  if (!Number.isFinite(ln) || !Number.isFinite(wn)) return "";
  return `${ln.toFixed(1)}x${wn.toFixed(1)}`;
}

function roundToTenthFoot(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number((Math.round(n * 10) / 10).toFixed(1));
}

function formatFeetTenth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(1);
}

function normalizeDisplayToleranceFt(value) {
  const n = Number(value);
  return [0.1, 0.25, 0.5, 1].includes(n) ? n : 0.1;
}

function roundFeetForDisplay(value, toleranceFt = 0.1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const tol = normalizeDisplayToleranceFt(toleranceFt);
  return Number((Math.round(n / tol) * tol).toFixed(2));
}

function formatFeetForDisplay(value, toleranceFt = 0.1) {
  const rounded = roundFeetForDisplay(value, toleranceFt);
  if (!Number.isFinite(rounded)) return "";
  const tol = normalizeDisplayToleranceFt(toleranceFt);
  return rounded.toFixed(tol === 0.25 ? 2 : 1);
}

function buildDisplayExportRecord(record, toleranceFt = 0.1) {
  const normalized = normalizeRecord(record);
  return {
    ...normalized,
    measured_linear_feet: normalized.measured_linear_feet == null ? null : roundFeetForDisplay(normalized.measured_linear_feet, toleranceFt),
    measured_width_feet: normalized.measured_width_feet == null ? null : roundFeetForDisplay(normalized.measured_width_feet, toleranceFt),
  };
}

function paletteColorForKey(key) {
  const cacheKey = String(key || "");
  if (!cacheKey) return "#38bdf8";
  if (DIMENSION_COLOR_CACHE.has(cacheKey)) return DIMENSION_COLOR_CACHE.get(cacheKey);
  let hash = 0;
  const text = cacheKey;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  const abs = Math.abs(hash);
  const hue = abs % 360;
  const sat = 68 + ((abs >> 8) % 18); // 68..85
  const light = 50 + ((abs >> 16) % 10); // 50..59
  const color = `hsl(${hue} ${sat}% ${light}%)`;
  DIMENSION_COLOR_CACHE.set(cacheKey, color);
  return color;
}

function confidenceColor(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "high") return "#22c55e";
  if (v === "medium") return "#f59e0b";
  if (v === "low") return "#ef4444";
  const n = Number(value);
  if (Number.isFinite(n)) {
    if (n >= 0.8) return "#22c55e";
    if (n >= 0.5) return "#f59e0b";
    return "#ef4444";
  }
  return "#38bdf8";
}

function downloadText(filename, text, type = "text/plain") {
  const blob = new Blob([String(text || "")], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function sanitizeFilenamePart(value, fallback = "slip_inventory") {
  const text = String(value || "").trim().replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "");
  return text || fallback;
}

function buildCsv(records, toleranceFt = 0.1) {
  const escapeCsv = (value) => {
    const text = value == null ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replaceAll("\"", "\"\"")}"` : text;
  };
  const rows = records
    .map((record) => buildDisplayExportRecord(record, toleranceFt))
    .map((record) => RECORD_HEADERS.map((header) => escapeCsv(record[header])).join(","));
  return `${RECORD_HEADERS.join(",")}\n${rows.join("\n")}`;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

export function createSlipInventoryRuntime(app) {
  const TASK_SOURCE = "slip-inventory";
  function taskStatus(message, progress = null, running = true) {
    app?.events?.emit?.("task:status", {
      source: TASK_SOURCE,
      label: "Slip Inventory",
      message,
      progress,
      status: running ? "running" : "idle",
    });
  }
  function taskIdle() { taskStatus("", null, false); }

  const map = app.map.instance;
  const slipIdPane = map.getPane("slip-id-badge-pane") || map.createPane("slip-id-badge-pane");
  slipIdPane.style.zIndex = "750";
  const root = L.featureGroup().addTo(map);
  const canvasRenderer = L.canvas({ padding: 0.5 });
  const markersLayer = L.featureGroup().addTo(root);
  const slipIdMarkersLayer = L.featureGroup().addTo(root);
  const depthMarkersLayer = L.featureGroup().addTo(root);
  const previewLayer = L.featureGroup().addTo(root);
  const geometryLayer = L.featureGroup().addTo(root);
  const geometryHandlesLayer = L.featureGroup().addTo(root);
  const geometryPlacePreviewLayer = L.featureGroup().addTo(root);
  const geometryRunPreviewLayer = L.featureGroup().addTo(root);
  const geometryRunHandleLayer = L.featureGroup().addTo(root);
  const geometryFreeDrawLayer = L.featureGroup().addTo(root);
  const geometryFreeDrawDynamicLayer = L.featureGroup().addTo(root);
  const multislipPreviewLayer = L.featureGroup().addTo(root);
  const multislipHandleLayer = L.featureGroup().addTo(root);
  const geometryLayerCache = new Map(); // polyId → { path, interactive, renderKey, ringKey }
  let html2canvasLoader = null;
  let containerEl = null;
  let tablePanelEl = null;
  let tablePanelResizeObserver = null;

  // --- performance monitor (Alt+Shift+D to toggle) ---
  let _perfLastCaller = "—";
  let _perfLastPanelKey = "—";
  (() => {
    let visible = false;
    let el = null;
    let intervalId = null;
    let _prevRenderCount = 0;
    let _rendersPerSec = 0;
    const canvasLayers = () => Object.keys(canvasRenderer?._layers || {}).length;
    const featureLayers = (fg) => Object.keys(fg?._layers || {}).length;
    const mbStr = (bytes) => bytes ? (bytes / 1048576).toFixed(1) + "MB" : "?";
    const update = () => {
      if (!el) return;
      _rendersPerSec = _perfRenderCount - _prevRenderCount;
      _prevRenderCount = _perfRenderCount;
      const mem = performance.memory;
      const zoom = map?.getZoom?.()?.toFixed(1) ?? "?";
      const rpsColor = _rendersPerSec > 10 ? "#f87171" : _rendersPerSec > 2 ? "#fbbf24" : "#4ade80";
      el.innerHTML = [
        `<b style="color:#60a5fa">⚡ Perf (Alt+Shift+D)</b>`,
        `Heap: ${mbStr(mem?.usedJSHeapSize)} / ${mbStr(mem?.jsHeapSizeLimit)}`,
        `Records: ${records.length} | PolyState: ${state.slipGeometryPolygons.length}`,
        `GeoCache: ${geometryLayerCache.size} | CanvasLayers: ${canvasLayers()}`,
        `GeoLayer: ${featureLayers(geometryLayer)} | Markers: ${featureLayers(markersLayer)}`,
        `SlipBadges: ${featureLayers(slipIdMarkersLayer)} | RunPreview: ${featureLayers(geometryRunPreviewLayer)}`,
        `FreeDraw: ${featureLayers(geometryFreeDrawLayer)}+${featureLayers(geometryFreeDrawDynamicLayer)}`,
        `render() fired: ${_perfRenderCount} | panel rebuilt: ${_perfPanelRebuildCount}`,
        `<span style="color:${rpsColor}">renders/sec: ${_rendersPerSec}</span> | Zoom: ${zoom} | RecordsVer: ${recordsVersion}`,
        `Last caller: ${_perfLastCaller}`,
        `Last key: ${_perfLastPanelKey.slice(0, 80)}`,
      ].join("<br>");
    };
    const toggle = () => {
      visible = !visible;
      if (visible) {
        el = document.createElement("div");
        el.style.cssText = "position:fixed;top:60px;right:12px;z-index:99999;padding:8px 12px;background:rgba(2,6,23,0.94);border:1px solid rgba(56,189,248,0.35);border-radius:8px;font:11px/1.6 monospace;color:#e2e8f0;pointer-events:none;min-width:240px;";
        document.body.appendChild(el);
        update();
        intervalId = setInterval(update, 1000);
      } else {
        clearInterval(intervalId); intervalId = null;
        el?.remove(); el = null;
      }
    };
    document.addEventListener("keydown", (e) => {
      if (e.altKey && e.shiftKey && e.key === "D") toggle();
    });
  })();
  // --- end performance monitor ---

  let state = readState();
  if (!Array.isArray(state.slipGeometryTypes) || !state.slipGeometryTypes.length) {
    state.slipGeometryTypes = [{ id: makeGeometryId(), name: "Standard 40x14", lengthFt: 40, widthFt: 14 }];
    state.slipGeometryDraftTypeId = state.slipGeometryTypes[0].id;
    persistState(state);
  }
  let records = [];
  let recordsVersion = 0;
  let tableSelectedMeasurementIds = new Set(); // tracks table multi-select directly, parallel to geometry selection
  let _perfRenderCount = 0;
  let _perfPanelRebuildCount = 0;
  let spaceTypeColorMap = new Map();
  let initialFetchDone = false; // mount() fires on every renderSidebar(); only fetch once
  let remoteSyncTimer = null;
  let remoteSyncInFlight = false;
  let lastLocalSaveAt = 0;
  let remoteSyncFocusHandler = null;
  let remoteSyncVisibilityHandler = null;
  let filterMarinaId = "";
  let showNoMarinaCode = false;
  let status = "Slip inventory is ready.";
  let imageryEstimateStatus = "";
  let imageryEstimateDebug = null;
  let geometryImageryEstimateStatus = "";
  let geometryImageryEstimateDebug = null;
  let pointDropActive = false;
  let pointDropOff = null;
  let lineLayoutActive = false;
  let lineLayoutOff = null;
  let marinaCatalog = [];
  let lineEndpoints = [];
  let lineLayoutMode = "endpoint";
  let lineHoverPoint = null;
  let lineAnchorPoint = null;
  let zoomHandler = null;
  let mapSelectClickHandler = null;
  let activeGeometryPlaceMode = false;
  let geometryPlaceOff = null;
  let geometryPlaceHoverPoint = null;
  let geometryPlaceDraft = null;
  let geometryRunLayoutActive = false;
  let geometryRunLayoutOff = null;
  let geometryRunEndpoints = [];
  let geometryRunHoverPoint = null;
  let freeDrawActive = false;
  let freeDrawPoints = [];
  let freeDrawHoverPoint = null;
  let freeDrawOff = null;
  let multislipActive = false;
  let multislipMode = "length"; // "length" = dock-run first (default), "width" = per-slip width first
  let multislipP1 = null;
  let multislipP2 = null;
  let multislipWidthFt = 0;   // signed (positive=left, negative=right of dock line)
  let multislipPartitions = 1;
  let multislipSlotLengths = []; // per-slot offshore length (signed ft); set when width is clicked
  let multislipDragging = false; // true while a handle drag is in progress
  let multislipHoverPoint = null;
  let multislipRotating = false;  // true after step-3 click until rotation is locked
  let multislipP2Base = null;     // original P2 before rotation
  let multislipP3 = null;         // step-3 click position (rotation angle reference)
  let multislipOff = null;
  let globalRunHotkeyHandler = null;
  let globalWheelAdjustKeydownHandler = null;
  let globalWheelAdjustKeyupHandler = null;
  let globalWheelAdjustHandler = null;
  const wheelAdjustHeldSlots = new Set();
  let wheelAdjustSpaceHeld = false;
  let wheelAdjustBacktickHeld = false;
  let suppressMapClickUntil = 0;
  let bottomTableScrollTop = 0;
  let bottomTablePointerInside = false;
  let bottomTableScrollActiveUntil = 0;
  let _selectModeHandler = null;
  let _filterPanelCloseHandler = null;
  let _filterPanelShouldBeOpen = false;
  let copiedSlipGeometryTemplate = null;
  let slipHandleDragging = false;
  let dimensionLegendControl = null;
  let draftMultiSlip = false;
  let draftMultiSlipId = "";
  let draftCovered = false;
  let draftBoatlift = false;
  let draftFuelDock = false;
  let draftComments = "";

  function clearDimensionLegend() {
    if (!dimensionLegendControl) return;
    try { map.removeControl(dimensionLegendControl); } catch {}
    dimensionLegendControl = null;
  }

  function renderDimensionLegend(entries = []) {
    clearDimensionLegend();
    if (!["dimension", "space_type"].includes(String(state.colorSlipsBy || "none"))) return;
    if (state.showDimensionLegend === false) return;
    const items = Array.isArray(entries) ? entries : [];
    if (!items.length) return;
    const control = L.control({ position: "bottomleft" });
    control.onAdd = () => {
      const div = L.DomUtil.create("div", "slip-dimension-legend leaflet-bar");
      div.style.background = "rgba(2,6,23,0.88)";
      div.style.border = "1px solid rgba(56,189,248,0.38)";
      div.style.borderRadius = "10px";
      div.style.padding = "8px 10px";
      div.style.minWidth = "180px";
      div.style.maxHeight = "220px";
      div.style.overflow = "auto";
      div.style.color = "#e2e8f0";
      div.style.font = "600 11px/1.3 system-ui, sans-serif";
      const rows = items.map((entry) => `
        <div style="display:flex; align-items:center; gap:8px; margin:4px 0;">
          <span style="display:inline-block; width:12px; height:12px; border-radius:3px; background:${esc(entry.color)}; border:1px solid rgba(255,255,255,.35);"></span>
          <span>${esc(entry.label)}</span>
        </div>
      `).join("");
      div.innerHTML = `<div style="font-weight:700; margin-bottom:6px; color:#bae6fd;">Dimension Legend</div>${rows}`;
      L.DomEvent.disableClickPropagation(div);
      L.DomEvent.disableScrollPropagation(div);
      return div;
    };
    control.addTo(map);
    dimensionLegendControl = control;
  }

  function persist() {
    persistState(state);
  }

  function lengthWidthToleranceStep() {
    const raw = Number(state?.lengthWidthToleranceFt);
    return [0.1, 0.25, 0.5, 1].includes(raw) ? raw : 0.5;
  }

  function rotationToleranceStep() {
    const raw = Number(state?.rotationToleranceDeg);
    return [0.1, 0.5, 1].includes(raw) ? raw : 0.5;
  }

  function snapLengthWidth(value) {
    return roundToStep(value, lengthWidthToleranceStep(), 0.1);
  }

  function nudgeSelectedGeometry(directionKey) {
    const selected = selectedGeometryPolygon();
    if (!selected) return false;
    const stepFeet = lengthWidthToleranceStep();
    const stepMeters = feetToMeters(stepFeet);
    const key = String(directionKey || "").toLowerCase();
    let dx = 0;
    let dy = 0;
    if (key === "arrowup") dy = stepMeters;
    else if (key === "arrowdown") dy = -stepMeters;
    else if (key === "arrowleft") dx = -stepMeters;
    else if (key === "arrowright") dx = stepMeters;
    else return false;

    const center = selected?.center || { lat: 0, lng: 0 };
    const nextCenter = offsetPoint(center, dx, dy);
    updateSelectedGeometry({ center: nextCenter });
    status = `Nudged ${stepFeet} ft (${key.replace("arrow", "")}).`;
    render();
    return true;
  }

  function suppressMapClicks(ms = 400) {
    suppressMapClickUntil = Date.now() + Math.max(50, Number(ms) || 400);
  }

  function normalizeMarinaCatalog(items = []) {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
      .map((item) => {
        const marinaId = String(item?.portfolio?.code || item?.name || "").trim();
        const marinaName = String(item?.name || item?.portfolio?.name || "").trim();
        const lat = Number(item?.lat);
        const lng = Number(item?.lng);
        if (!marinaId || !marinaName || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const key = marinaId.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        return { marinaId, marinaName, lat, lng };
      })
      .filter(Boolean)
      .sort((a, b) => a.marinaId.localeCompare(b.marinaId));
  }

  function marinaOptionForId(marinaId, fallbackName = "") {
    const text = String(marinaId || "").trim();
    if (!text) return null;
    const existing = marinaCatalog.find((entry) => entry.marinaId === text);
    if (existing) return existing;
    return {
      marinaId: text,
      marinaName: String(fallbackName || text),
      lat: null,
      lng: null,
    };
  }

  function setMarinaDefaultsFromId(marinaId) {
    const marina = marinaOptionForId(marinaId, state.marinaName);
    state.marinaId = marina?.marinaId || "";
    state.marinaName = marina?.marinaName || "";
    persist();
  }

  function selectedRecord() {
    return records.find((record) => record.measurement_id === state.selectedMeasurementId) || null;
  }

  function selectedIndex() {
    const index = records.findIndex((record) => record.measurement_id === state.selectedMeasurementId);
    return index >= 0 ? index : records.length;
  }

  function fitSelection() {
    const current = selectedRecord();
    const point = pointFromAnyWkt(current?.geometry_wkt);
    if (!point) return;
    map.setView([point.lat, point.lng], Math.max(18, Number(map.getZoom?.() || 18)));
  }

  function fitMarinaById(marinaId, fallbackName = "") {
    const marina = marinaOptionForId(marinaId, fallbackName);
    if (!marina || !Number.isFinite(marina.lat) || !Number.isFinite(marina.lng)) return false;
    map.setView([marina.lat, marina.lng], Math.max(16, Number(map.getZoom?.() || 16)));
    return true;
  }

  function interpolateLatLng(start, end, ratio) {
    return {
      lat: Number(start.lat) + ((Number(end.lat) - Number(start.lat)) * ratio),
      lng: Number(start.lng) + ((Number(end.lng) - Number(start.lng)) * ratio),
    };
  }

  function spacingRunPreviewPoints() {
    if (lineEndpoints.length < 2) return [];
    const first = lineEndpoints[0];
    const second = lineEndpoints[1];
    const target = lineEndpoints[2] || lineHoverPoint || second;
    const startPoint = map.latLngToLayerPoint([first.lat, first.lng]);
    const spacingPoint = map.latLngToLayerPoint([second.lat, second.lng]);
    const targetPoint = map.latLngToLayerPoint([target.lat, target.lng]);
    const vx = Number(spacingPoint.x) - Number(startPoint.x);
    const vy = Number(spacingPoint.y) - Number(startPoint.y);
    const spacingPx = Math.hypot(vx, vy);
    if (!Number.isFinite(spacingPx) || spacingPx <= 0) return [first, second];
    const ux = vx / spacingPx;
    const uy = vy / spacingPx;
    const tx = Number(targetPoint.x) - Number(startPoint.x);
    const ty = Number(targetPoint.y) - Number(startPoint.y);
    const projectedLength = Math.max(spacingPx, (tx * ux) + (ty * uy));
    const count = Math.max(1, Math.floor((projectedLength / spacingPx) + 0.35) + 1);
    return Array.from({ length: count }, (_, index) => {
      const point = L.point(startPoint.x + (ux * spacingPx * index), startPoint.y + (uy * spacingPx * index));
      const latlng = map.layerPointToLatLng(point);
      return { lat: Number(latlng.lat), lng: Number(latlng.lng) };
    });
  }

  function resolvedLineSlipCount() {
    return Math.max(1, Number(state.lineSlipCount || 0) || 1);
  }

  function linePreviewRatios() {
    const count = resolvedLineSlipCount();
    if (count <= 1) return [0.5];
    return Array.from({ length: count }, (_, index) => index / Math.max(1, count - 1));
  }

  function linePreviewPoints() {
    if (lineLayoutMode === "spacing") return spacingRunPreviewPoints();
    if (lineEndpoints.length < 2) return [];
    const [start, end] = lineEndpoints;
    const count = resolvedLineSlipCount();
    const anchor = lineAnchorPoint && Number.isFinite(Number(lineAnchorPoint.index))
      ? {
          index: Math.max(1, Math.min(Math.max(1, count - 2), Number(lineAnchorPoint.index))),
          lat: Number(lineAnchorPoint.lat),
          lng: Number(lineAnchorPoint.lng),
        }
      : null;
    if (anchor && count >= 3 && Number.isFinite(anchor.lat) && Number.isFinite(anchor.lng)) {
      return Array.from({ length: count }, (_, index) => {
        if (index <= anchor.index) {
          const leftRatio = anchor.index <= 0 ? 0 : (index / Math.max(1, anchor.index));
          return interpolateLatLng(start, { lat: anchor.lat, lng: anchor.lng }, leftRatio);
        }
        const rightSteps = Math.max(1, (count - 1 - anchor.index));
        const rightRatio = (index - anchor.index) / rightSteps;
        return interpolateLatLng({ lat: anchor.lat, lng: anchor.lng }, end, rightRatio);
      });
    }
    return linePreviewRatios().map((ratio) => interpolateLatLng(start, end, ratio));
  }

  function linePreviewSegments() {
    if (lineEndpoints.length < 2) return [];
    const start = lineEndpoints[0];
    const end = lineEndpoints[lineEndpoints.length - 1];
    const count = Math.max(1, linePreviewPoints().length);
    return Array.from({ length: count }, (_, index) => {
      const t0 = index / count;
      const t1 = (index + 1) / count;
      const tm = (t0 + t1) / 2;
      return {
        start: interpolateLatLng(start, end, t0),
        end: interpolateLatLng(start, end, t1),
        center: interpolateLatLng(start, end, tm),
      };
    });
  }

  function projectRatiosOnSegment(points, start, end) {
    const startPt = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPt = map.latLngToContainerPoint([end.lat, end.lng]);
    const vx = Number(endPt.x) - Number(startPt.x);
    const vy = Number(endPt.y) - Number(startPt.y);
    const len2 = (vx * vx) + (vy * vy);
    if (!Number.isFinite(len2) || len2 <= 1e-6) return [];
    return (Array.isArray(points) ? points : []).map((point) => {
      const p = map.latLngToContainerPoint([point.lat, point.lng]);
      const tx = Number(p.x) - Number(startPt.x);
      const ty = Number(p.y) - Number(startPt.y);
      const ratio = ((tx * vx) + (ty * vy)) / len2;
      return clampNumber(ratio, 0, 1);
    });
  }

  async function loadHtml2Canvas() {
    if (!html2canvasLoader) {
      html2canvasLoader = import("html2canvas").then((module) => module.default || module);
    }
    return html2canvasLoader;
  }

  function hasVisibleRasterImagery() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) return false;
    return !!mapContainer.querySelector(".leaflet-tile-pane img, .leaflet-tile-pane canvas, .leaflet-overlay-pane img, .leaflet-overlay-pane canvas");
  }

  function isGeoTiffLikeLayer(layer) {
    if (!layer || typeof layer !== "object") return false;
    if (String(layer.type || "") === "geotiff-url") return true;
    const meta = layer.meta && typeof layer.meta === "object" ? layer.meta : {};
    if (String(meta.sourceType || "") === "raster-tiles") return true;
    if (String(meta.rasterJobId || "").trim()) return true;
    return false;
  }

  async function waitForRenderFrame() {
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  async function captureGeoTiffImageryCanvas() {
    const layersService = app?.services?.layers;
    if (!layersService?.listLayers) return captureVisibleImageryCanvas();
    const layers = layersService.listLayers();
    const visibleGeoTiffs = layers.filter((layer) => layer?.visible !== false && isGeoTiffLikeLayer(layer));
    if (!visibleGeoTiffs.length) return null;
    const nonGeoVisible = layers.filter((layer) => layer?.visible !== false && !isGeoTiffLikeLayer(layer));
    const muted = [];
    let rootDetached = false;
    const hiddenToolLayers = [];
    try {
      try {
        if (map?.hasLayer?.(root)) {
          map.removeLayer(root);
          rootDetached = true;
        }
      } catch {}
      const localOverlayGroups = [
        previewLayer,
        geometryLayer,
        geometryHandlesLayer,
        geometryRunPreviewLayer,
        geometryRunHandleLayer,
      ];
      localOverlayGroups.forEach((group) => {
        if (!group) return;
        if (!root?.hasLayer?.(group)) return;
        try {
          root.removeLayer(group);
          hiddenToolLayers.push(group);
        } catch {}
      });
      for (const layer of nonGeoVisible) {
        if (!layer?.id) continue;
        muted.push({ id: layer.id, opacity: Number(layer.opacity) });
        if (typeof layersService.setOpacity === "function") {
          layersService.setOpacity(layer.id, 0, { emit: false, persist: false });
        } else if (typeof layersService.updateLayer === "function") {
          layersService.updateLayer(layer.id, { opacity: 0 });
        }
      }
      await waitForRenderFrame();
      await waitForRenderFrame();
      return captureVisibleImageryCanvas();
    } finally {
      for (const layer of muted) {
        const restoreOpacity = Number.isFinite(layer.opacity) ? layer.opacity : 1;
        if (typeof layersService.setOpacity === "function") {
          layersService.setOpacity(layer.id, restoreOpacity, { emit: false, persist: false });
        } else if (typeof layersService.updateLayer === "function") {
          layersService.updateLayer(layer.id, { opacity: restoreOpacity });
        }
      }
      hiddenToolLayers.forEach((group) => {
        try { root.addLayer(group); } catch {}
      });
      if (rootDetached) {
        try { root.addTo(map); } catch {}
      }
      app?.events?.emit?.("layers:changed", layersService.listLayers?.() || []);
    }
  }

  async function captureVisibleImageryCanvas() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) throw new Error("Map container is not available.");
    const html2canvas = await loadHtml2Canvas();
    return html2canvas(mapContainer, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: 1,
      ignoreElements(element) {
        const className = String(element?.className?.baseVal || element?.className || "");
        if (className.includes("leaflet-control")) return true;
        if (className.includes("leaflet-marker-pane")) return true;
        if (className.includes("leaflet-tooltip-pane")) return true;
        if (className.includes("leaflet-popup-pane")) return true;
        return false;
      },
    });
  }

  async function estimateLineSlipCountFromImagery() {
    if (lineEndpoints.length < 2) {
      imageryEstimateStatus = "Draw a line first.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Checking the visible imagery along this line...";
    render();
    const [start, end] = lineEndpoints;
    const lineLengthFeet = (Number(map.distance([start.lat, start.lng], [end.lat, end.lng])) || 0) * 3.280839895;
    if (!Number.isFinite(lineLengthFeet) || lineLengthFeet <= 0) {
      imageryEstimateStatus = "Could not determine line length from the current map.";
      render();
      return;
    }
    if (!hasVisibleRasterImagery()) {
      imageryEstimateStatus = "No readable aerial imagery layer is visible on the map.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const startPoint = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPoint = map.latLngToContainerPoint([end.lat, end.lng]);
    const lineLengthPx = Math.hypot(Number(endPoint.x) - Number(startPoint.x), Number(endPoint.y) - Number(startPoint.y));
    if (!Number.isFinite(lineLengthPx) || lineLengthPx < 40) {
      imageryEstimateStatus = "Zoom in a bit more before estimating from imagery.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Analyzing repeating imagery pattern along the line...";
    render();
    const canvas = await captureGeoTiffImageryCanvas();
    if (!canvas) {
      geometryImageryEstimateStatus = "No visible GeoTIFF layer found. Import and enable a GeoTIFF first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const context = canvas?.getContext?.("2d", { willReadFrequently: true });
    if (!context) {
      imageryEstimateStatus = "Could not read the visible imagery layer.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const width = Number(canvas.width) || 0;
    const height = Number(canvas.height) || 0;
    if (!width || !height) {
      imageryEstimateStatus = "Visible imagery capture returned an empty canvas.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const pixels = context.getImageData(0, 0, width, height).data;
    const sampleCount = clampNumber(Math.round(lineLengthPx), 80, 420);
    const pixelsPerFoot = lineLengthPx / Math.max(1, lineLengthFeet);
    const minPitchFeet = 12;
    const maxPitchFeet = 30;
    const minLag = clampNumber(Math.round(minPitchFeet * pixelsPerFoot), 6, Math.max(12, Math.round(lineLengthPx / 2)));
    const maxLag = clampNumber(Math.round(maxPitchFeet * pixelsPerFoot), minLag + 2, Math.max(minLag + 2, Math.round(lineLengthPx / 2)));
    const corridorHalfWidths = [
      clampNumber(Math.round(lineLengthPx * 0.02), 7, 12),
      clampNumber(Math.round(lineLengthPx * 0.03), 9, 16),
      clampNumber(Math.round(lineLengthPx * 0.04), 11, 22),
      clampNumber(Math.round(lineLengthPx * 0.05), 13, 28),
    ];
    const signalRuns = corridorHalfWidths.map((corridorHalfWidthPx) => {
      const signal = buildImagerySpacingSignal(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 15,
        corridorHalfWidthPx,
      });
      const estimate = estimatePitchFromSignal(signal, { minLag, maxLag });
      return {
        corridorHalfWidthPx,
        signal,
        estimate: estimate ? { ...estimate, corridorHalfWidthPx } : null,
      };
    });
    const candidateEstimates = signalRuns.map((run) => run.estimate).filter(Boolean);
    const lagEstimate = chooseConsensusPitchEstimate(candidateEstimates);
    if (!lagEstimate?.lag) {
      imageryEstimateStatus = "Could not find a reliable repeating spacing in the imagery.";
      imageryEstimateDebug = {
        signalRuns,
        chosenCorridorHalfWidthPx: null,
        chosenSignal: [],
        suggestedSpacingSamples: null,
      };
      render();
      return;
    }
    const chosenRun = signalRuns.reduce((best, run) => {
      if (!run?.estimate) return best;
      if (Math.abs(run.estimate.lag - lagEstimate.lag) > Math.max(3, lagEstimate.lag * 0.2)) return best;
      if (!best) return run;
      return (run.estimate.confidence || 0) > (best.estimate?.confidence || 0) ? run : best;
    }, null);
    const currentCount = Math.max(2, Number(state.lineSlipCount || 2) || 2);
    const estimatedPitchFeet = lagEstimate.lag / Math.max(1e-6, pixelsPerFoot);
    const estimatedCount = Math.max(2, Math.round(lineLengthFeet / Math.max(1e-6, estimatedPitchFeet)) + 1);
    if (!Number.isFinite(estimatedCount) || estimatedCount < 2 || estimatedCount > 500) {
      imageryEstimateStatus = "Imagery estimate landed outside a safe slip-count range.";
      render();
      return;
    }
    const allowedDelta = Math.max(2, Math.round(currentCount * 0.35));
    if (lagEstimate.confidence < 0.28 && Math.abs(estimatedCount - currentCount) > allowedDelta) {
      imageryEstimateStatus = `Imagery pattern was too weak to safely move from ${currentCount} to ${estimatedCount} slips. Try zooming in more or draw the line tighter along one row.`;
      render();
      return;
    }
    state.lineSlipCount = estimatedCount;
    if (lineAnchorPoint && Number(lineAnchorPoint.index) >= estimatedCount - 1) lineAnchorPoint = null;
    persist();
    renderPreview();
    imageryEstimateDebug = {
      signalRuns,
      chosenCorridorHalfWidthPx: chosenRun?.corridorHalfWidthPx || null,
      chosenSignal: chosenRun?.signal || [],
      suggestedSpacingSamples: lagEstimate.lag,
    };
    const confidenceLabel = lagEstimate.confidence >= 0.66
      ? "high"
      : lagEstimate.confidence >= 0.4
        ? "medium"
        : "low";
    imageryEstimateStatus = `Estimated ${estimatedCount} slips from imagery at about ${estimatedPitchFeet.toFixed(1)} ft spacing (${confidenceLabel} confidence, ${lagEstimate.method || "signal"} method, ${lagEstimate.samples || candidateEstimates.length || 1} passes agreed).`;
    status = `Updated line preview to ${estimatedCount} slips from imagery.`;
    render();
  }

  async function estimateGeometryRunFromImagery() {
    if (!geometryRunLayoutActive || geometryRunEndpoints.length < 2) {
      geometryImageryEstimateStatus = "Set start and end run bookends first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const selected = selectedGeometryPolygon();
    if (!selected) {
      geometryImageryEstimateStatus = "Select a base slip polygon first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const start = geometryRunEndpoints[0];
    const end = geometryRunEndpoints[1];
    const startPoint = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPoint = map.latLngToContainerPoint([end.lat, end.lng]);
    const vx = Number(endPoint.x) - Number(startPoint.x);
    const vy = Number(endPoint.y) - Number(startPoint.y);
    const lineLengthPx = Math.hypot(vx, vy);
    const lineLengthFeet = (Number(map.distance([start.lat, start.lng], [end.lat, end.lng])) || 0) * 3.280839895;
    if (!Number.isFinite(lineLengthPx) || lineLengthPx < 35 || !Number.isFinite(lineLengthFeet) || lineLengthFeet <= 0) {
      geometryImageryEstimateStatus = "Run line is too short. Extend the run and zoom in.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (!hasVisibleRasterImagery()) {
      geometryImageryEstimateStatus = "No readable imagery/raster layer is visible.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    geometryImageryEstimateStatus = "Analyzing GeoTIFF/imagery for spacing and width...";
    render();
    const canvas = await captureGeoTiffImageryCanvas();
    if (!canvas) {
      geometryImageryEstimateStatus = "No visible GeoTIFF layer found. Import and enable a GeoTIFF first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const context = canvas?.getContext?.("2d", { willReadFrequently: true });
    if (!context) {
      geometryImageryEstimateStatus = "Could not read imagery pixels from the map.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const width = Number(canvas.width) || 0;
    const height = Number(canvas.height) || 0;
    if (!width || !height) {
      geometryImageryEstimateStatus = "Imagery capture was empty.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const pixels = context.getImageData(0, 0, width, height).data;
    const pixelsPerFoot = lineLengthPx / Math.max(1e-6, lineLengthFeet);
    const sampleCount = clampNumber(Math.round(lineLengthPx * 2.25), 240, 5200);
    const currentTotal = Math.max(2, geometryRunTotalCount());
    const currentPitchFeet = lineLengthFeet / Math.max(1, currentTotal - 1);
    const seedLag = currentPitchFeet * pixelsPerFoot;
    const minPitchFeet = clampNumber(currentPitchFeet * 0.7, 8, 40);
    const maxPitchFeet = clampNumber(currentPitchFeet * 1.35, minPitchFeet + 1, 50);
    const minLag = clampNumber(Math.round(minPitchFeet * pixelsPerFoot), 6, Math.max(12, Math.round(lineLengthPx / 2)));
    const maxLag = clampNumber(Math.round(maxPitchFeet * pixelsPerFoot), minLag + 2, Math.max(minLag + 2, Math.round(lineLengthPx / 2)));
    const corridorHalfWidths = [
      clampNumber(Math.round(lineLengthPx * 0.02), 7, 12),
      clampNumber(Math.round(lineLengthPx * 0.03), 9, 16),
      clampNumber(Math.round(lineLengthPx * 0.04), 11, 22),
    ];
    const coverageCheck = estimateSignalCoverage(pixels, width, height, startPoint, endPoint, {
      sampleCount: Math.max(64, Math.min(600, sampleCount)),
      crossSampleCount: 21,
      corridorHalfWidthPx: corridorHalfWidths[1] || corridorHalfWidths[0] || 12,
      alphaThreshold: 8,
    });
    if (coverageCheck.coverageRatio < 0.06 || coverageCheck.lumaStdDev < 2) {
      geometryImageryEstimateStatus = "Insufficient GeoTIFF coverage/signal under run line. Move run over raster data.";
      geometryImageryEstimateDebug = {
        chosenSignal: [],
        centerRatios: [],
      };
      render();
      return;
    }
    const signalRuns = corridorHalfWidths.flatMap((corridorHalfWidthPx) => {
      const robustSignal = buildImagerySpacingSignalRobust(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 31,
        corridorHalfWidthPx,
        darkPercentile: 0.28,
      });
      const binarySignal = buildImagerySpacingSignalBinary(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 35,
        corridorHalfWidthPx,
        alphaThreshold: 8,
        darkPercentile: 0.35,
      });
      const variants = [
        { signal: robustSignal, mode: "robust" },
        { signal: binarySignal, mode: "binary" },
      ];
      return variants.map((variant) => {
        const autoEstimate = estimatePitchFromSignal(variant.signal, { minLag, maxLag });
        const peakEstimate = estimatePitchFromPeaks(variant.signal, { minLag, maxLag, seedLag });
        let estimate = autoEstimate;
        if (peakEstimate?.lag && autoEstimate?.lag) {
          estimate = (Number(peakEstimate.confidence || 0) >= Number(autoEstimate.confidence || 0) * 0.85)
            ? peakEstimate
            : autoEstimate;
        } else if (peakEstimate?.lag) {
          estimate = peakEstimate;
        }
        const rawLag = estimateSignalLag(variant.signal, { minLag, maxLag });
        return {
          signal: variant.signal,
          mode: variant.mode,
          corridorHalfWidthPx,
          estimate,
          rawLag,
        };
      });
    });
    const candidateEstimates = signalRuns.map((run) => run.estimate).filter(Boolean);
    let lagEstimate = chooseConsensusPitchEstimate(candidateEstimates);
    if (!lagEstimate?.lag) {
      const fallbackRun = signalRuns.reduce((best, run) => {
        if (!run?.rawLag?.lag || !Number.isFinite(run.rawLag.score)) return best;
        if (!best) return run;
        return Number(run.rawLag.score) > Number(best.rawLag?.score || -Infinity) ? run : best;
      }, null);
      if (fallbackRun?.rawLag?.lag) {
        lagEstimate = {
          lag: Number(fallbackRun.rawLag.lag),
          confidence: clampNumber(Number(fallbackRun.rawLag.confidence || 0.12), 0, 1),
          method: `autocorr-fallback-${fallbackRun.mode || "unknown"}`,
          samples: 1,
        };
      }
    }
    if (!lagEstimate?.lag) {
      const bestSignalRun = signalRuns.reduce((best, run) => {
        const sig = Array.isArray(run?.signal) ? run.signal : [];
        const variance = seriesStats(sig).stdDev || 0;
        if (!best) return { run, variance };
        return variance > best.variance ? { run, variance } : best;
      }, null)?.run || null;
      geometryImageryEstimateStatus = "Could not detect repeating slip spacing from imagery.";
      geometryImageryEstimateDebug = {
        chosenSignal: Array.isArray(bestSignalRun?.signal) ? bestSignalRun.signal : [],
        centerRatios: [],
      };
      render();
      return;
    }
    const chosenRun = signalRuns.reduce((best, run) => {
      if (!run?.estimate) return best;
      if (Math.abs(run.estimate.lag - lagEstimate.lag) > Math.max(3, lagEstimate.lag * 0.2)) return best;
      if (!best) return run;
      return (run.estimate.confidence || 0) > (best.estimate?.confidence || 0) ? run : best;
    }, null);
    const peakDerived = deriveSlipCentersFromDockPeaks(chosenRun?.signal, {
      minLag,
      maxLag,
      seedLag,
      sampleCount,
    });
    const ux = vx / Math.max(1e-6, lineLengthPx);
    const uy = vy / Math.max(1e-6, lineLengthPx);
    const previewPoints = geometryRunPreviewPoints();
    const widthCandidatesFt = previewPoints
      .slice(0, Math.min(10, previewPoints.length))
      .map((point) => map.latLngToContainerPoint([point.lat, point.lng]))
      .map((centerPoint) => estimateWidthFromPerpendicularProfile(
        pixels,
        width,
        height,
        centerPoint,
        { x: ux, y: uy },
        pixelsPerFoot,
        Number(selected?.widthFt || 0)
      ))
      .filter((entry) => entry?.spanPx)
      .map((entry) => roundToHalfFoot(entry.spanPx / Math.max(1e-6, pixelsPerFoot)))
      .filter((value) => Number.isFinite(value) && value >= 4 && value <= 35)
      .sort((a, b) => a - b);
    const widthEstimateFt = widthCandidatesFt.length
      ? widthCandidatesFt[Math.floor(widthCandidatesFt.length / 2)]
      : null;
    const canTrustPeakDerived = Number(peakDerived?.intervalsUsed || 0) >= 4 && Number(peakDerived?.confidence || 0) >= 0.35;
    const usedLag = Number((canTrustPeakDerived ? peakDerived?.lag : null) || lagEstimate.lag || 0);
    const estimatedPitchFeet = usedLag / Math.max(1e-6, pixelsPerFoot);
    const peakDerivedTotal = canTrustPeakDerived ? Number(peakDerived?.centerRatios?.length || 0) : 0;
    const estimatedTotal = peakDerivedTotal >= 2
      ? Math.max(2, peakDerivedTotal)
      : Math.max(2, Math.round(lineLengthFeet / Math.max(1e-6, estimatedPitchFeet)) + 1);
    state.slipGeometryRunBetweenCount = Math.max(1, estimatedTotal);
    if (Number.isFinite(widthEstimateFt)) {
      updateSelectedGeometry({ widthFt: widthEstimateFt });
    }
    persist();
    renderGeometry();
    renderGeometryRunPreview();
    const newPreviewPoints = geometryRunPreviewPoints();
    const previewCenterRatios = projectRatiosOnSegment(newPreviewPoints, start, end);
    const centerRatios = peakDerivedTotal >= 2
      ? peakDerived.centerRatios
      : previewCenterRatios;
    geometryImageryEstimateDebug = {
      chosenSignal: Array.isArray(chosenRun?.signal) ? chosenRun.signal : [],
      centerRatios,
      spacingLagSamples: usedLag,
      sampleCount,
    };
    const widthText = Number.isFinite(widthEstimateFt)
      ? `${Number(widthEstimateFt).toFixed(1)} ft`
      : "unchanged";
    geometryImageryEstimateStatus = `Suggested run: ${estimatedTotal} slips, spacing ~${estimatedPitchFeet.toFixed(1)} ft (seed ${currentPitchFeet.toFixed(1)}), width ${widthText}.${canTrustPeakDerived ? ` Count from dock-peak intervals (${peakDerived.intervalsUsed} intervals).` : ""}`;
    status = "Applied optional imagery-based run suggestions.";
    render();
  }

  function renderPreview() {
    previewLayer.clearLayers();
    const previewPoints = linePreviewPoints();
    const previewSegments = linePreviewSegments();
    const guidePoints = lineLayoutMode === "spacing"
      ? [...lineEndpoints, ...(lineHoverPoint && lineEndpoints.length >= 2 && lineEndpoints.length < 3 ? [lineHoverPoint] : [])]
      : lineEndpoints;
    if (lineEndpoints.length === 1) {
      L.circleMarker([lineEndpoints[0].lat, lineEndpoints[0].lng], {
        radius: 6,
        color: "#f59e0b",
        weight: 2,
        fillColor: "#f59e0b",
        fillOpacity: 0.35,
        renderer: canvasRenderer,
      }).addTo(previewLayer);
      return;
    }
    if (guidePoints.length >= 2) {
      L.polyline(guidePoints.map((point) => [point.lat, point.lng]), {
        color: "rgba(217,70,239,0.35)",
        weight: 2,
        dashArray: "4 6",
        renderer: canvasRenderer,
      }).addTo(previewLayer);
      lineEndpoints.forEach((point, index) => {
        const handle = L.marker([point.lat, point.lng], {
          draggable: true,
          autoPan: true,
          zIndexOffset: 1000,
          icon: L.divIcon({
            className: "slip-inventory-endpoint-handle",
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            html: `
              <div style="
                width:18px;
                height:18px;
                border-radius:999px;
                background:rgba(245,158,11,0.96);
                border:2px solid rgba(15,23,42,0.92);
                box-shadow:0 0 0 1px rgba(254,243,199,0.65), 0 6px 12px rgba(15,23,42,0.24);
              "></div>
            `,
          }),
        }).addTo(previewLayer);
        const tooltip = lineLayoutMode === "spacing"
          ? (index === 0 ? "Drag first slip center" : index === 1 ? "Drag spacing slip center" : "Drag run extent")
          : (index === 0 ? "Drag start point" : "Drag end point");
        handle.bindTooltip(tooltip, { direction: "top", offset: [0, -8] });
        handle.on("dragend", (event) => {
          const latlng = event?.target?.getLatLng?.();
          if (!latlng) return;
          lineEndpoints[index] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = lineLayoutMode === "spacing"
            ? "Adjusted spacing run point."
            : `Adjusted ${index === 0 ? "start" : "end"} point.`;
          renderPreview();
          render();
        });
      });
    }
    if (!previewPoints.length) return;
    if (previewSegments.length) {
      previewSegments.forEach((segment, index) => {
        L.polyline([[segment.start.lat, segment.start.lng], [segment.end.lat, segment.end.lng]], {
          color: "#d946ef",
          weight: 8,
          opacity: 0.92,
          lineCap: "round",
          renderer: canvasRenderer,
        }).addTo(previewLayer);
        if (index < previewSegments.length - 1) {
          const boundary = segment.end;
          const next = previewSegments[index + 1];
          const dx = Number(next.end.lng) - Number(next.start.lng);
          const dy = Number(next.end.lat) - Number(next.start.lat);
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const tickHalf = 0.000012;
          L.polyline([
            [boundary.lat - (ny * tickHalf), boundary.lng - (nx * tickHalf)],
            [boundary.lat + (ny * tickHalf), boundary.lng + (nx * tickHalf)],
          ], {
            color: "rgba(250,232,255,0.9)",
            weight: 2,
            opacity: 0.95,
            dashArray: "2 4",
            renderer: canvasRenderer,
          }).addTo(previewLayer);
        }
      });
    }
    const ids = previewSlipIds(state, previewPoints.length);
    previewPoints.forEach((point, index) => {
      const marker = L.marker([point.lat, point.lng], {
        interactive: true,
        draggable: lineLayoutMode !== "spacing",
        icon: L.divIcon({
          className: "slip-inventory-preview-label-wrap",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `
            <div style="transform:translate(-50%, -50%); display:flex; flex-direction:column; align-items:center; gap:4px;">
              <div style="width:16px; height:2px; background:rgba(245,158,11,0.95); box-shadow:0 0 0 1px rgba(15,23,42,0.28);"></div>
              <div style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                padding:2px 7px;
                border-radius:999px;
                background:rgba(15,23,42,0.78);
                color:#fde68a;
                border:1px solid rgba(245,158,11,0.55);
                font:700 11px/1.2 system-ui,sans-serif;
                white-space:nowrap;
              ">${esc(ids[index])}</div>
            </div>
          `,
        }),
      }).addTo(previewLayer);
      marker.bindTooltip(
        lineLayoutMode === "spacing"
          ? `Preview ${ids[index]}`
          : `Preview ${ids[index]}${index < previewPoints.length - 1 ? ` | click to set count ${index + 1}` : ""} | drag to re-space`,
        { direction: "top", offset: [0, -8] },
      );
      marker.on("click", () => {
        if (lineLayoutMode === "spacing") return;
        state.lineSlipCount = Math.max(1, index + 1);
        if (lineAnchorPoint && Number(lineAnchorPoint.index) >= state.lineSlipCount - 1) lineAnchorPoint = null;
        persist();
        renderPreview();
        render();
      });
      marker.on("dragend", (event) => {
        if (lineLayoutMode === "spacing") return;
        const latlng = event?.target?.getLatLng?.();
        if (!latlng) return;
        if (index <= 0) {
          lineEndpoints[0] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          lineAnchorPoint = null;
          status = "Adjusted line start point.";
        } else if (index >= previewPoints.length - 1) {
          lineEndpoints[1] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          lineAnchorPoint = null;
          status = "Adjusted line end point.";
        } else {
          lineAnchorPoint = {
            index,
            lat: Number(latlng.lat),
            lng: Number(latlng.lng),
          };
          status = `Re-spaced line around ${ids[index] || `slip ${index + 1}`}.`;
        }
        renderPreview();
        render();
      });
    });
  }

  function syncSelection() {
    if (state.selectedMeasurementId && records.some((record) => record.measurement_id === state.selectedMeasurementId)) return;
    if (!state.selectedMeasurementId) return;
    state.selectedMeasurementId = "";
    persist();
  }

  function getSlipTypeById(typeId) {
    const id = String(typeId || "");
    return state.slipGeometryTypes.find((entry) => String(entry.id) === id) || null;
  }

  function getSelectedGeometryIds() {
    const ids = new Set((Array.isArray(state.slipGeometrySelectedIds) ? state.slipGeometrySelectedIds : []).map((value) => String(value || "")).filter(Boolean));
    const primary = String(state.slipGeometrySelectedId || "");
    if (primary) ids.add(primary);
    return [...ids];
  }

  function setSelectedGeometryIds(ids = [], primaryId = "") {
    const list = [...new Set((Array.isArray(ids) ? ids : []).map((value) => String(value || "")).filter(Boolean))];
    state.slipGeometrySelectedIds = list;
    state.slipGeometrySelectedId = String(primaryId || list[0] || "");
    persist();
  }

  function clearGeometrySelection() {
    state.slipGeometrySelectedIds = [];
    state.slipGeometrySelectedId = "";
    tableSelectedMeasurementIds.clear();
    persist();
  }

  function applyGeometrySelection(id, { toggle = false } = {}) {
    const targetId = String(id || "");
    if (!targetId) return;
    if (toggle) {
      const ids = getSelectedGeometryIds();
      const exists = ids.includes(targetId);
      const next = exists ? ids.filter((value) => value !== targetId) : [...ids, targetId];
      setSelectedGeometryIds(next, exists ? next[0] : targetId);
      const primaryId = String(exists ? next[0] : targetId);
      const primary = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === primaryId) || null;
      if (primary?.linkedMeasurementId) state.selectedMeasurementId = String(primary.linkedMeasurementId);
      return;
    }
    setSelectedGeometryIds([targetId], targetId);
    const selected = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === targetId) || null;
    if (selected?.linkedMeasurementId) state.selectedMeasurementId = String(selected.linkedMeasurementId);
  }

  function normalizeGeometrySelection() {
    const existing = new Set(state.slipGeometryPolygons.map((poly) => String(poly.id)));
    const current = getSelectedGeometryIds();
    const next = current.filter((id) => existing.has(id));
    const primaryId = String(state.slipGeometrySelectedId || "");
    // Only normalize when selection is actually invalid (stale IDs or primary not in set)
    if (next.length !== current.length || (next.length > 0 && !next.includes(primaryId))) {
      setSelectedGeometryIds(next, next[0] || "");
    }
  }

  function isGeometrySelected(polyId) {
    const id = String(polyId || "");
    return getSelectedGeometryIds().includes(id);
  }

  function selectedGeometryPolygon() {
    return state.slipGeometryPolygons.find((entry) => String(entry.id) === String(state.slipGeometrySelectedId || "")) || null;
  }

  function selectedGeometryPolygons() {
    const ids = new Set(getSelectedGeometryIds());
    return state.slipGeometryPolygons.filter((entry) => ids.has(String(entry.id)));
  }

  function createSlipType({ name, lengthFt, widthFt }) {
    const next = {
      id: makeGeometryId(),
      name: String(name || `Type ${state.slipGeometryTypes.length + 1}`),
      lengthFt: roundToHalfFoot(lengthFt),
      widthFt: roundToHalfFoot(widthFt),
      chamferEnabled: true,
      chamferSide: 1,
      chamferAngleDeg: 0,
      chamferMagnitudeFt: 0,
    };
    state.slipGeometryTypes = [...state.slipGeometryTypes, next];
    if (!state.slipGeometryDraftTypeId) state.slipGeometryDraftTypeId = next.id;
    persist();
  }

  function placeGeometryAt(latlng, typeId, overrides = null) {
    const slipType = getSlipTypeById(typeId);
    if (!slipType || !latlng) return null;
    const source = (overrides && typeof overrides === "object") ? overrides : slipType;
    const translatedRing = (
      Array.isArray(source?.ringLatLngs)
      && source.ringLatLngs.length >= 3
      && source?.sourceCenter
    )
      ? translateRingLatLngs(
        source.ringLatLngs,
        source.sourceCenter,
        { lat: Number(latlng.lat), lng: Number(latlng.lng) },
      )
      : null;
    const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
    const poly = {
      id: makeGeometryId(),
      slipTypeId: slipType.id,
      lengthFt: Number(roundToTenthFoot(inferred?.lengthFt ?? source.lengthFt) ?? source.lengthFt ?? 40),
      widthFt: Number(roundToTenthFoot(inferred?.widthFt ?? source.widthFt) ?? source.widthFt ?? 14),
      headingDeg: Number((inferred?.headingDeg ?? source.headingDeg) || 0),
      locked: false,
      center: translatedRing
        ? { lat: Number(inferred?.center?.lat ?? latlng.lat), lng: Number(inferred?.center?.lng ?? latlng.lng) }
        : { lat: Number(latlng.lat), lng: Number(latlng.lng) },
      chamferEnabled: true,
      chamferSide: Number(source.chamferSide || inferred?.chamferSide || 1) < 0 ? -1 : 1,
      chamferAngleDeg: Number(source.chamferAngleDeg || 0),
      chamferMagnitudeFt: roundToHalfFoot(Number(source.chamferMagnitudeFt || 0)),
      ringLatLngs: translatedRing || null,
    };
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, poly];
    setSelectedGeometryIds([poly.id], poly.id);
    // Explicitly ensure new placements are editable regardless of prior lock state.
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((entry) => (
      String(entry.id) === String(poly.id) ? { ...entry, locked: false } : entry
    ));
    persist();
    return poly;
  }

function updateSelectedGeometry(changes = {}) {
    let selectedIds = getSelectedGeometryIds();
    if (!selectedIds.length && state.slipGeometryPolygons.length) {
      const fallback = [...state.slipGeometryPolygons].reverse().find((poly) => !poly?.locked) || state.slipGeometryPolygons[state.slipGeometryPolygons.length - 1];
      if (fallback?.id) {
        setSelectedGeometryIds([String(fallback.id)], String(fallback.id));
        selectedIds = getSelectedGeometryIds();
      }
    }
    if (!selectedIds.length) return;
    const targetIds = new Set(selectedIds);
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => {
      if (!targetIds.has(String(poly.id))) return poly;
      if (poly.locked && !poly.committed && (changes.center || changes.lengthFt != null || changes.widthFt != null || changes.headingDeg != null)) return poly;
      const nextCenter = changes.center ? {
        lat: Number(changes.center.lat),
        lng: Number(changes.center.lng),
      } : poly.center;
      const nextHeadingDeg = changes.headingDeg == null ? Number(poly.headingDeg || 0) : Number(changes.headingDeg || 0);
      const nextLengthFt = changes.lengthFt == null ? Number(poly.lengthFt || 0) : Number(changes.lengthFt);
      const nextWidthFt = changes.widthFt == null ? Number(poly.widthFt || 0) : Number(changes.widthFt);
      const canTransformRing = Array.isArray(poly.ringLatLngs) && poly.ringLatLngs.length >= 3
        && (
          changes.center
          || changes.lengthFt != null
          || changes.widthFt != null
          || changes.headingDeg != null
        );
      const transformedRing = canTransformRing
        ? transformRingLatLngsWithMetrics({
          ringLatLngs: poly.ringLatLngs,
          oldCenter: poly.center,
          newCenter: nextCenter,
          oldHeadingDeg: Number(poly.headingDeg || 0),
          newHeadingDeg: nextHeadingDeg,
          oldLengthFt: Number(poly.lengthFt || 0),
          newLengthFt: nextLengthFt,
          oldWidthFt: Number(poly.widthFt || 0),
          newWidthFt: nextWidthFt,
        })
        : null;
      return {
        ...poly,
        ...changes,
        center: nextCenter,
        headingDeg: nextHeadingDeg,
        lengthFt: nextLengthFt,
        widthFt: nextWidthFt,
        locked: (
          changes.center
          || changes.lengthFt != null
          || changes.widthFt != null
          || changes.headingDeg != null
          || changes.chamferAngleDeg != null
          || changes.chamferMagnitudeFt != null
        ) ? false : poly.locked,
        committed: poly.committed,
        chamferAngleDeg: changes.chamferAngleDeg == null ? Number(poly.chamferAngleDeg || 0) : Math.max(0, Math.min(85, Number(changes.chamferAngleDeg) || 0)),
        chamferMagnitudeFt: changes.chamferMagnitudeFt == null ? Number(poly.chamferMagnitudeFt || 0) : roundToHalfFoot(changes.chamferMagnitudeFt),
        ringLatLngs: transformedRing
          ? transformedRing
          : (
            changes.center
            || changes.lengthFt != null
            || changes.widthFt != null
            || changes.headingDeg != null
            || changes.chamferAngleDeg != null
            || changes.chamferMagnitudeFt != null
          ) ? null : poly.ringLatLngs,
      };
    });
    persist();
  }

  function duplicateSelectedGeometryRun({ copies = 1, spacingFt = 2 } = {}) {
    const base = selectedGeometryPolygon();
    if (!base) return 0;
    const total = Math.max(1, Math.floor(Number(copies) || 1));
    const gap = Math.max(0, Number(spacingFt) || 0);
    const headingRad = (Number(base.headingDeg || 0) * Math.PI) / 180;
    const ux = Math.sin(headingRad);
    const uy = Math.cos(headingRad);
    const stepMeters = feetToMeters((Number(base.widthFt) || 0) + gap);
    const next = [];
    for (let i = 1; i <= total; i += 1) {
      const c = offsetPoint(base.center, ux * stepMeters * i, uy * stepMeters * i);
      next.push({
        ...base,
        id: makeGeometryId(),
        center: { lat: c.lat, lng: c.lng },
        locked: false,
      });
    }
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, ...next];
    if (next.length) state.slipGeometrySelectedId = next[next.length - 1].id;
    persist();
    return next.length;
  }

  function geometryRunTotalCount() {
    return Math.max(1, Number(state.slipGeometryRunBetweenCount || 0) || 1);
  }

  function geometryRunPreviewPoints() {
    if (!geometryRunEndpoints.length) return [];
    const start = geometryRunEndpoints[0];
    const end = geometryRunEndpoints[1] || geometryRunHoverPoint;
    if (!end) return [];
    const total = geometryRunTotalCount();
    return Array.from({ length: total }, (_, index) => {
      const ratio = total <= 1 ? 0 : (index / Math.max(1, total - 1));
      return interpolateLatLng(start, end, ratio);
    });
  }

  function stopGeometryRunLayout(reason = "stopped") {
    if (geometryRunLayoutOff) {
      geometryRunLayoutOff();
      geometryRunLayoutOff = null;
    }
    geometryRunLayoutActive = false;
    geometryRunEndpoints = [];
    geometryRunHoverPoint = null;
    geometryRunPreviewLayer.clearLayers();
    geometryRunHandleLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
    if (reason === "cancelled") {
      status = "Slip geometry run layout cancelled.";
      render();
    }
  }

  function createGeometryRunFromPreview() {
    const selected = selectedGeometryPolygon();
    if (!selected) return null;
    const points = geometryRunPreviewPoints();
    if (points.length < 1) return null;
    const heading = Number(selected.headingDeg || 0);
    const selectedCenter = {
      lat: Number(selected?.center?.lat || 0),
      lng: Number(selected?.center?.lng || 0),
    };
    const hasSourceRing = Array.isArray(selected?.ringLatLngs) && selected.ringLatLngs.length >= 3;
    const firstPoint = points[0];
    const firstTargetCenter = { lat: Number(firstPoint.lat), lng: Number(firstPoint.lng) };
    const firstTranslatedRing = hasSourceRing
      ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, firstTargetCenter)
      : null;
    const firstInferred = firstTranslatedRing ? inferPolyMetricsFromRing(firstTranslatedRing) : null;
    const updatedSelected = {
      ...selected,
      runGroupId: "",
      headingDeg: Number(firstInferred?.headingDeg ?? heading),
      center: firstTranslatedRing
        ? { lat: Number(firstInferred?.center?.lat ?? firstTargetCenter.lat), lng: Number(firstInferred?.center?.lng ?? firstTargetCenter.lng) }
        : firstTargetCenter,
      ringLatLngs: firstTranslatedRing || null,
      locked: false,
      committed: false,
    };
    const created = points.slice(1).map((point) => {
      const targetCenter = { lat: Number(point.lat), lng: Number(point.lng) };
      const translatedRing = hasSourceRing
        ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, targetCenter)
        : null;
      const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
      return {
        ...selected,
        id: makeGeometryId(),
        runGroupId: "",
        headingDeg: Number(inferred?.headingDeg ?? heading),
        center: translatedRing
          ? { lat: Number(inferred?.center?.lat ?? targetCenter.lat), lng: Number(inferred?.center?.lng ?? targetCenter.lng) }
          : targetCenter,
        ringLatLngs: translatedRing || null,
        locked: false,
        committed: false,
      };
    });
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      String(poly.id) === String(selected.id) ? updatedSelected : poly
    ));
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, ...created];
    if (created.length) {
      const nextId = String(created[created.length - 1].id || "");
      setSelectedGeometryIds([nextId], nextId);
    }
    persist();
    return {
      createdCount: created.length,
      createdIds: created.map((entry) => String(entry.id)),
      sourceId: String(selected.id),
      points,
      basePoly: { ...selected },
    };
  }

  async function commitGeometryRunToRecords(runResult) {
    const points = Array.isArray(runResult?.points) ? runResult.points : [];
    if (!points.length) return 0;
    const ids = previewSlipIds(state, points.length);
    const nowIso = new Date().toISOString();
    let upserts = 0;
    const links = [];
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      const slipId = String(ids[index] || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`);
      const polygonIds = [String(runResult?.sourceId || ""), ...((Array.isArray(runResult?.createdIds) ? runResult.createdIds : []).map((id) => String(id || "")))];
      const polygonId = String(polygonIds[index] || "");
      const polygon = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === polygonId) || null;
      const polygonRing = polygon ? computeSlipPolygonLatLngs(polygon) : [];
      const polygonWkt = buildPolygonWkt(polygonRing);
      const inferred = polygonRing.length >= 3 ? inferPolyMetricsFromRing(polygonRing) : null;
      const measuredLengthFt = roundToTenthFoot(inferred?.lengthFt ?? polygon?.lengthFt ?? null);
      const measuredWidthFt = roundToTenthFoot(inferred?.widthFt ?? polygon?.widthFt ?? null);
      const existing = records.find((record) => (
        String(record?.slip_id || "") === slipId
        && String(record?.marina_id || "") === String(state.marinaId || "")
      )) || null;
      if (existing?.measurement_id) {
        const measurementId = String(existing.measurement_id || "");
        await requestJson(`${API_BASE}/${encodeURIComponent(existing.measurement_id)}`, {
          method: "PATCH",
          body: {
            ...existing,
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            operational_status: String(existing?.operational_status || "operable"),
            notes: String(existing?.notes || ""),
            geometry_wkt: polygonWkt || buildPointWkt(point.lng, point.lat),
            measured_linear_feet: measuredLengthFt,
            measured_width_feet: measuredWidthFt,
          },
        });
        links.push({ polygonId, measurementId, slipId });
      } else {
        const measurementId = makeId("MEAS");
        await requestJson(`${API_BASE}`, {
          method: "POST",
          body: {
            measurement_id: measurementId,
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            slip_id: slipId,
            dock_name: state.dockName || null,
            space_type: state.spaceType || null,
            multislip_yn: draftMultiSlip ? "yes" : "no",
            multislip_id: draftMultiSlipId || null,
            covered_yn: draftCovered ? "yes" : "no",
            boatlift_yn: draftBoatlift ? "yes" : "no",
            fueldock_yn: draftFuelDock ? "yes" : "no",
            marina_comments: draftComments || null,
            measured_linear_feet: measuredLengthFt,
            measured_width_feet: measuredWidthFt,
            measured_depth_feet: null,
            operational_status: "operable",
            notes: "",
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            record_created_timestamp: nowIso,
            geometry_wkt: polygonWkt || buildPointWkt(point.lng, point.lat),
          },
        });
        links.push({ polygonId, measurementId, slipId });
      }
      incrementSlipIdNumber(state);
      upserts += 1;
    }
    persist();
    await refreshRecords({ quiet: true });
    return { upserts, links };
  }

  async function generateRunAndPersist() {
    const runResult = createGeometryRunFromPreview();
    if (!runResult) return { createdCount: 0, upserts: 0 };
    stopGeometryRunLayout("completed");
    const commitResult = await commitGeometryRunToRecords(runResult);
    const upserts = Number(commitResult?.upserts || 0);
    const links = Array.isArray(commitResult?.links) ? commitResult.links : [];
    const committedIds = new Set([
      String(runResult.sourceId || ""),
      ...((Array.isArray(runResult.createdIds) ? runResult.createdIds : []).map((id) => String(id || ""))),
    ]);
    const linkByPolyId = new Map(links.map((entry) => [String(entry.polygonId || ""), entry]));
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      committedIds.has(String(poly.id))
        ? {
          ...poly,
          committed: true,
          linkedMeasurementId: String(linkByPolyId.get(String(poly.id))?.measurementId || poly.linkedMeasurementId || ""),
          linkedSlipId: String(linkByPolyId.get(String(poly.id))?.slipId || poly.linkedSlipId || ""),
        }
        : poly
    ));
    // Remove local editable twins that can stack on top of the newly committed run.
    // We only remove non-committed polygons that match the committed record links.
    const committedMeasurementIds = new Set(
      links
        .map((entry) => String(entry?.measurementId || ""))
        .filter(Boolean),
    );
    const committedSlipIds = new Set(
      links
        .map((entry) => String(entry?.slipId || ""))
        .filter(Boolean),
    );
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => {
      const id = String(poly?.id || "");
      if (committedIds.has(id)) return true;
      if (poly?.committed) return true;
      const measurementId = String(poly?.linkedMeasurementId || "");
      const slipId = String(poly?.linkedSlipId || "");
      if (measurementId && committedMeasurementIds.has(measurementId)) return false;
      if (slipId && committedSlipIds.has(slipId)) return false;
      return true;
    });
    // Normalize to the same committed geometry shape/style that other clients receive
    // from API rehydration, so creator client does not need a manual refresh.
    synchronizeCommittedGeometryFromRecords();
    // After commit, leave no active geometry selection so controls don't
    // accidentally keep editing the just-generated run.
    clearGeometrySelection();
    persist();
    status = `Created ${runResult.createdCount} geometry polygons and upserted ${upserts} slip records from run layout.`;
    if (upserts > 0) app.log("slip.created", { count: upserts, tool: "geometry_run", marina: state.marinaId });
    render();
    return { createdCount: runResult.createdCount, upserts };
  }

  async function commitSelectedPolygonAndPersist() {
    const selected = selectedGeometryPolygon();
    if (!selected) return { upserts: 0 };
    const runResult = {
      createdCount: 0,
      createdIds: [],
      sourceId: String(selected.id || ""),
      points: [{ lat: Number(selected.center?.lat || 0), lng: Number(selected.center?.lng || 0) }],
      basePoly: { ...selected },
    };
    const commitResult = await commitGeometryRunToRecords(runResult);
    const upserts = Number(commitResult?.upserts || 0);
    const links = Array.isArray(commitResult?.links) ? commitResult.links : [];
    const link = links[0] || null;
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      String(poly.id || "") === String(selected.id || "")
        ? {
          ...poly,
          committed: true,
          locked: true,
          linkedMeasurementId: String(link?.measurementId || poly.linkedMeasurementId || ""),
          linkedSlipId: String(link?.slipId || poly.linkedSlipId || ""),
        }
        : poly
    ));
    synchronizeCommittedGeometryFromRecords();
    clearGeometrySelection();
    persist();
    status = `Committed 1 slip polygon and upserted ${upserts} slip record.`;
    render();
    return { upserts };
  }

  function buildLineWkt(points = []) {
    const clean = (Array.isArray(points) ? points : [])
      .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (clean.length < 2) return "";
    const body = clean.map(([lat, lng]) => `${Number(lng).toFixed(6)} ${Number(lat).toFixed(6)}`).join(", ");
    return `LINESTRING(${body})`;
  }

  async function commitLineFeatureFromCurrentLine() {
    if (lineEndpoints.length < 2) {
      status = "Draw a line first (start and end) before committing side-tie feature.";
      render();
      return;
    }
    const start = lineEndpoints[0];
    const end = lineEndpoints[lineEndpoints.length - 1];
    const lineWkt = buildLineWkt([[start.lat, start.lng], [end.lat, end.lng]]);
    if (!lineWkt) return;
    const linearFeet = roundToTenthFoot(toFeetDistance(L.latLng(start.lat, start.lng), L.latLng(end.lat, end.lng)));
    const slipId = String(buildSlipIdFromState(state) || "").trim() || `SIDE-${Date.now()}`;
    const existing = records.find((record) => (
      String(record?.slip_id || "") === slipId
      && String(record?.marina_id || "") === String(state.marinaId || "")
    )) || null;
    const payload = {
      measurement_id: String(existing?.measurement_id || makeId("MEAS")),
      marina_id: state.marinaId,
      marina_name: state.marinaName,
      slip_id: slipId,
      dock_name: state.dockName || null,
      space_type: state.spaceType || null,
      multislip_yn: draftMultiSlip ? "yes" : "no",
      multislip_id: draftMultiSlipId || null,
      covered_yn: draftCovered ? "yes" : "no",
      boatlift_yn: draftBoatlift ? "yes" : "no",
      fueldock_yn: draftFuelDock ? "yes" : "no",
      marina_comments: draftComments || null,
      measured_linear_feet: roundToTenthFoot(linearFeet),
      measured_width_feet: null,
      measured_depth_feet: null,
      operational_status: String(existing?.operational_status || "operable"),
      notes: String(existing?.notes || ""),
      measurement_date: state.measurementDate,
      confidence_score: state.confidenceScore,
      record_created_timestamp: String(existing?.record_created_timestamp || new Date().toISOString()),
      geometry_wkt: lineWkt,
    };
    await requestJson(existing?.measurement_id
      ? `${API_BASE}/${encodeURIComponent(existing.measurement_id)}`
      : `${API_BASE}`, {
      method: existing?.measurement_id ? "PATCH" : "POST",
      body: payload,
    });
    incrementSlipIdNumber(state);
    await refreshRecords({ quiet: true });
    status = `Committed side-tie line feature ${slipId} (${linearFeet.toFixed(1)} ft).`;
    render();
  }

  function performRunControl(action) {
    const selected = selectedGeometryPolygon();
    const draft = activeGeometryPlaceMode ? geometryPlaceDraft : null;
    const rotateStep = rotationToleranceStep();
    const delta = 0.1;
    const key = String(action || "").trim().toLowerCase();
    const updateDraft = (changes = {}) => {
      if (!draft) return false;
      const draftRing = Array.isArray(draft?.ringLatLngs) ? draft.ringLatLngs : null;
      const draftInferred = draftRing && draftRing.length >= 3 ? inferPolyMetricsFromRing(draftRing) : null;
      const currentLengthFt = Number(draftInferred?.lengthFt || draft.lengthFt || 40);
      const currentWidthFt = Number(draftInferred?.widthFt || draft.widthFt || 14);
      const currentHeadingDeg = Number(draftInferred?.headingDeg ?? draft.headingDeg ?? 0);
      const nextLengthFt = changes.lengthFt == null ? currentLengthFt : Number(changes.lengthFt);
      const nextWidthFt = changes.widthFt == null ? currentWidthFt : Number(changes.widthFt);
      const nextHeadingDeg = changes.headingDeg == null ? currentHeadingDeg : Number(changes.headingDeg || 0);
      const nextChamferAngleDeg = changes.chamferAngleDeg == null ? Number(draft.chamferAngleDeg || 0) : Math.max(0, Math.min(85, Number(changes.chamferAngleDeg) || 0));
      const nextChamferMagnitudeFt = changes.chamferMagnitudeFt == null ? Number(draft.chamferMagnitudeFt || 0) : roundToHalfFoot(changes.chamferMagnitudeFt);
      const nextChamferSide = Number(changes.chamferSide == null ? draft.chamferSide : changes.chamferSide) < 0 ? -1 : 1;
      const ringSourceCenter = draft?.sourceCenter || null;
      const hasRing = Array.isArray(draft?.ringLatLngs) && draft.ringLatLngs.length >= 3 && ringSourceCenter;
      const changedShapeParams = (
        changes.lengthFt != null
        || changes.widthFt != null
        || changes.headingDeg != null
      );
      const changedChamferParams = (
        changes.chamferAngleDeg != null
        || changes.chamferMagnitudeFt != null
      );
      const transformedRing = (hasRing && changedShapeParams)
        ? transformRingLatLngsWithMetrics({
          ringLatLngs: draft.ringLatLngs,
          oldCenter: ringSourceCenter,
          newCenter: ringSourceCenter,
          oldHeadingDeg: currentHeadingDeg,
          newHeadingDeg: nextHeadingDeg,
          oldLengthFt: currentLengthFt,
          newLengthFt: nextLengthFt,
          oldWidthFt: currentWidthFt,
          newWidthFt: nextWidthFt,
        })
        : null;
      let resolvedChamferSide = nextChamferSide;
      let chamferAdjustedRing = null;
      if (hasRing && changedChamferParams) {
        const candidatePos = computeParametricSlipPolygonLatLngs({
          center: ringSourceCenter,
          lengthFt: nextLengthFt,
          widthFt: nextWidthFt,
          headingDeg: nextHeadingDeg,
          chamferEnabled: true,
          chamferSide: 1,
          chamferAngleDeg: nextChamferAngleDeg,
          chamferMagnitudeFt: nextChamferMagnitudeFt,
        });
        const candidateNeg = computeParametricSlipPolygonLatLngs({
          center: ringSourceCenter,
          lengthFt: nextLengthFt,
          widthFt: nextWidthFt,
          headingDeg: nextHeadingDeg,
          chamferEnabled: true,
          chamferSide: -1,
          chamferAngleDeg: nextChamferAngleDeg,
          chamferMagnitudeFt: nextChamferMagnitudeFt,
        });
        const currentRing = Array.isArray(draft.ringLatLngs) ? draft.ringLatLngs : [];
        const scorePos = ringSimilarityScoreMeters(currentRing, candidatePos);
        const scoreNeg = ringSimilarityScoreMeters(currentRing, candidateNeg);
        if (Number.isFinite(scorePos) || Number.isFinite(scoreNeg)) {
          const choosePos = scorePos <= scoreNeg;
          resolvedChamferSide = choosePos ? 1 : -1;
          chamferAdjustedRing = choosePos ? candidatePos : candidateNeg;
        } else {
          chamferAdjustedRing = computeParametricSlipPolygonLatLngs({
            center: ringSourceCenter,
            lengthFt: nextLengthFt,
            widthFt: nextWidthFt,
            headingDeg: nextHeadingDeg,
            chamferEnabled: true,
            chamferSide: resolvedChamferSide,
            chamferAngleDeg: nextChamferAngleDeg,
            chamferMagnitudeFt: nextChamferMagnitudeFt,
          });
        }
      }
      geometryPlaceDraft = {
        ...draft,
        ...changes,
        lengthFt: nextLengthFt,
        widthFt: nextWidthFt,
        headingDeg: nextHeadingDeg,
        chamferSide: resolvedChamferSide,
        chamferAngleDeg: nextChamferAngleDeg,
        chamferMagnitudeFt: nextChamferMagnitudeFt,
        ringLatLngs: chamferAdjustedRing || transformedRing || draft.ringLatLngs || null,
        sourceCenter: (chamferAdjustedRing || transformedRing || draft.ringLatLngs) ? (ringSourceCenter || null) : null,
      };
      render();
      return true;
    };
    if (!key) return false;
    const useDraftOnly = !!draft;
    if (key === "q") {
      if (useDraftOnly) {
        updateDraft({ headingDeg: Number(draft.headingDeg || 0) - rotateStep });
        status = `Draft rotation: ${(Number(draft.headingDeg || 0) - rotateStep).toFixed(1)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) - rotateStep });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) - rotateStep).toFixed(1)}deg`;
      render();
      return true;
    }
    if (key === "w") {
      state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 1) + 1);
      persist();
      status = `Run number of slips: ${geometryRunTotalCount()}.`;
      render();
      return true;
    }
    if (key === "e") {
      if (useDraftOnly) {
        updateDraft({ headingDeg: Number(draft.headingDeg || 0) + rotateStep });
        status = `Draft rotation: ${(Number(draft.headingDeg || 0) + rotateStep).toFixed(1)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) + rotateStep });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) + rotateStep).toFixed(1)}deg`;
      render();
      return true;
    }
    if (key === "a") {
      if (useDraftOnly) {
        updateDraft({ lengthFt: Number(draft.lengthFt || 0) - delta });
        status = `Draft length: ${(Math.round((Number(draft.lengthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: Number(selected.lengthFt || 0) - delta });
      status = `Run length: ${(Math.round((Number(selected.lengthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
      render();
      return true;
    }
    if (key === "s") {
      if (useDraftOnly) return false;
      if (!selected) return false;
      if (!geometryRunLayoutActive) {
        startGeometryRunLayout();
        return true;
      }
      if (geometryRunEndpoints.length >= 2) {
        generateRunAndPersist().catch((error) => {
          status = error?.message || "Failed to generate run records.";
          render();
        });
        return true;
      }
      status = "Run layout active: click end slip location first.";
      render();
      return true;
    }
    if (key === "d") {
      if (useDraftOnly) {
        updateDraft({ lengthFt: Number(draft.lengthFt || 0) + delta });
        status = `Draft length: ${(Math.round((Number(draft.lengthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: Number(selected.lengthFt || 0) + delta });
      status = `Run length: ${(Math.round((Number(selected.lengthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
      render();
      return true;
    }
    if (key === "z") {
      if (useDraftOnly) {
        updateDraft({ widthFt: Number(draft.widthFt || 0) - delta });
        status = `Draft width: ${(Math.round((Number(draft.widthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: Number(selected.widthFt || 0) - delta });
      status = `Run width: ${(Math.round((Number(selected.widthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
      render();
      return true;
    }
    if (key === "x") {
      if (useDraftOnly) return false;
      state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 1) - 1);
      persist();
      status = `Run number of slips: ${geometryRunTotalCount()}.`;
      render();
      return true;
    }
    if (key === "c") {
      if (useDraftOnly) {
        updateDraft({ widthFt: Number(draft.widthFt || 0) + delta });
        status = `Draft width: ${(Math.round((Number(draft.widthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: Number(selected.widthFt || 0) + delta });
      status = `Run width: ${(Math.round((Number(selected.widthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
      render();
      return true;
    }
    if (key === "r") {
      if (useDraftOnly) {
        updateDraft({ chamferAngleDeg: Number(draft.chamferAngleDeg || 0) - 1 });
        status = `Draft chamfer angle: ${Math.max(0, Math.min(85, Number(draft.chamferAngleDeg || 0) - 1)).toFixed(0)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferAngleDeg: Number(selected.chamferAngleDeg || 0) - 1 });
      status = `Chamfer angle: ${Math.max(0, Math.min(85, Number(selected.chamferAngleDeg || 0) - 1)).toFixed(0)}deg`;
      render();
      return true;
    }
    if (key === "f") {
      if (useDraftOnly) {
        updateDraft({ chamferAngleDeg: Number(draft.chamferAngleDeg || 0) + 1 });
        status = `Draft chamfer angle: ${Math.max(0, Math.min(85, Number(draft.chamferAngleDeg || 0) + 1)).toFixed(0)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferAngleDeg: Number(selected.chamferAngleDeg || 0) + 1 });
      status = `Chamfer angle: ${Math.max(0, Math.min(85, Number(selected.chamferAngleDeg || 0) + 1)).toFixed(0)}deg`;
      render();
      return true;
    }
    if (key === "t") {
      if (useDraftOnly) {
        updateDraft({ chamferMagnitudeFt: Number(draft.chamferMagnitudeFt || 0) - 0.5 });
        status = `Draft chamfer magnitude: ${roundToHalfFoot(Number(draft.chamferMagnitudeFt || 0) - 0.5).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferMagnitudeFt: Number(selected.chamferMagnitudeFt || 0) - 0.5 });
      status = `Chamfer magnitude: ${roundToHalfFoot(Number(selected.chamferMagnitudeFt || 0) - 0.5).toFixed(1)} ft`;
      render();
      return true;
    }
    if (key === "g") {
      if (useDraftOnly) {
        updateDraft({ chamferMagnitudeFt: Number(draft.chamferMagnitudeFt || 0) + 0.5 });
        status = `Draft chamfer magnitude: ${roundToHalfFoot(Number(draft.chamferMagnitudeFt || 0) + 0.5).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferMagnitudeFt: Number(selected.chamferMagnitudeFt || 0) + 0.5 });
      status = `Chamfer magnitude: ${roundToHalfFoot(Number(selected.chamferMagnitudeFt || 0) + 0.5).toFixed(1)} ft`;
      render();
      return true;
    }
    return false;
  }

  function isTypingTarget(target) {
    const el = target && target.nodeType === 1 ? target : null;
    if (!el) return false;
    const tag = String(el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function performWheelAdjust(slot, direction, { fine = false, coarse = false } = {}) {
    const s = Number(slot);
    const dir = Number(direction);
    if (!Number.isFinite(s) || !Number.isFinite(dir) || dir === 0) return false;
    const selected = selectedGeometryPolygon();
    const draft = activeGeometryPlaceMode ? geometryPlaceDraft : null;
    // When the draft was pasted from a copied polygon it carries ringLatLngs/sourceCenter
    // whose inferred metrics override the draft's stored lengthFt/widthFt/headingDeg in
    // renderGeometryPlacePreview — making wheel adjustments invisible.  Resolve the ring
    // to effective parametric values up-front and drop the ring on first adjustment so the
    // preview switches to parametric rendering where changes are visible immediately.
    const draftRingInferred = draft && Array.isArray(draft.ringLatLngs) && draft.ringLatLngs.length >= 3
      ? inferPolyMetricsFromRing(draft.ringLatLngs)
      : null;
    const effectiveLength = Number(draftRingInferred?.lengthFt ?? draft?.lengthFt ?? 40);
    const effectiveWidth = Number(draftRingInferred?.widthFt ?? draft?.widthFt ?? 14);
    const effectiveHeading = Number(draftRingInferred?.headingDeg ?? draft?.headingDeg ?? 0);
    const effectiveChamferAngle = Number(draft?.chamferAngleDeg ?? 0);
    const effectiveChamferMag = Number(draft?.chamferMagnitudeFt ?? 0);
    const updateDraft = (changes = {}) => {
      if (!draft) return false;
      // Drop the ring so parametric values are used by the preview going forward
      const { ringLatLngs: _r, sourceCenter: _s, ...draftBase } = draft;
      geometryPlaceDraft = {
        ...draftBase,
        lengthFt: effectiveLength,
        widthFt: effectiveWidth,
        headingDeg: effectiveHeading,
        chamferAngleDeg: effectiveChamferAngle,
        chamferMagnitudeFt: effectiveChamferMag,
        ...changes,
      };
      render();
      return true;
    };
    const useDraftOnly = !!draft;
    const lengthStep = coarse ? 1 : fine ? 0.01 : 0.1;
    const widthStep = coarse ? 1 : fine ? 0.01 : 0.1;
    const rotationStep = coarse ? 5 : fine ? rotationToleranceStep() : 2;
    const chamferAngleStep = coarse ? 5 : fine ? rotationToleranceStep() : 1;
    const chamferMagnitudeStep = coarse ? 0.5 : fine ? 0.01 : 0.1;
    const modeTag = coarse ? " [coarse]" : fine ? " [fine]" : "";

    if (s === 1) {
      const delta = dir > 0 ? lengthStep : -lengthStep;
      if (useDraftOnly) {
        updateDraft({ lengthFt: effectiveLength + delta });
        status = `Draft length: ${(Math.round((effectiveLength + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: Number(selected.lengthFt || 0) + delta });
      status = `Run length: ${(Math.round((Number(selected.lengthFt || 0) + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
      render();
      return true;
    }
    if (s === 2) {
      const delta = dir > 0 ? widthStep : -widthStep;
      if (useDraftOnly) {
        updateDraft({ widthFt: effectiveWidth + delta });
        status = `Draft width: ${(Math.round((effectiveWidth + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: Number(selected.widthFt || 0) + delta });
      status = `Run width: ${(Math.round((Number(selected.widthFt || 0) + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
      render();
      return true;
    }
    if (s === 3) {
      const delta = dir > 0 ? rotationStep : -rotationStep;
      if (useDraftOnly) {
        updateDraft({ headingDeg: effectiveHeading + delta });
        status = `Draft rotation: ${(effectiveHeading + delta).toFixed(1)}deg${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) + delta });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) + delta).toFixed(1)}deg${modeTag}`;
      render();
      return true;
    }
    if (s === 4) {
      const delta = dir > 0 ? chamferAngleStep : -chamferAngleStep;
      if (useDraftOnly) {
        updateDraft({ chamferAngleDeg: effectiveChamferAngle + delta });
        status = `Draft chamfer angle: ${Math.max(0, Math.min(85, effectiveChamferAngle + delta)).toFixed(1)}deg${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferAngleDeg: Number(selected.chamferAngleDeg || 0) + delta });
      status = `Chamfer angle: ${Math.max(0, Math.min(85, Number(selected.chamferAngleDeg || 0) + delta)).toFixed(1)}deg${modeTag}`;
      render();
      return true;
    }
    if (s === 5) {
      const delta = dir > 0 ? chamferMagnitudeStep : -chamferMagnitudeStep;
      if (useDraftOnly) {
        updateDraft({ chamferMagnitudeFt: effectiveChamferMag + delta });
        status = `Draft chamfer magnitude: ${roundToHalfFoot(effectiveChamferMag + delta).toFixed(1)} ft${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ chamferMagnitudeFt: Number(selected.chamferMagnitudeFt || 0) + delta });
      status = `Chamfer magnitude: ${roundToHalfFoot(Number(selected.chamferMagnitudeFt || 0) + delta).toFixed(1)} ft${modeTag}`;
      render();
      return true;
    }
    return false;
  }

  function shouldIgnoreGeometryHotkeys(event) {
    if (isTypingTarget(event?.target)) return true;
    if (isTypingTarget(document?.activeElement)) return true;
    return false;
  }

  function startGeometryRunLayout() {
    const selected = selectedGeometryPolygon();
    if (!selected) return;
    stopPointDrop("replaced");
    stopLineLayout("replaced");
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopFreeDrawMode();
    geometryRunLayoutActive = true;
    geometryRunEndpoints = [{ lat: Number(selected.center?.lat || 0), lng: Number(selected.center?.lng || 0) }];
    geometryRunHoverPoint = { ...geometryRunEndpoints[0] };
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    let runPreviewRafPending = false;
    const onPointerMove = (event) => {
      if (geometryRunLayoutActive && geometryRunEndpoints.length === 1) {
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) {
          geometryRunHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          if (!runPreviewRafPending) {
            runPreviewRafPending = true;
            requestAnimationFrame(() => { runPreviewRafPending = false; renderGeometryRunPreview(); });
          }
        }
      }
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCapture = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCapture) return;
      if (geometryRunEndpoints.length === 1) {
        geometryRunEndpoints = [geometryRunEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
        geometryRunHoverPoint = null;
        status = `Run preview ready with ${geometryRunTotalCount()} slips.`;
      }
      render();
    };
    const onKey = (event) => {
      if (geometryRunLayoutActive) {
        const handled = performRunControl(event.key);
        if (handled) {
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
      }
      if (event.key === "Enter" && geometryRunEndpoints.length >= 2) {
        event.preventDefault?.();
        generateRunAndPersist().catch((error) => {
          status = error?.message || "Failed to generate run records.";
          render();
        });
        return;
      }
      if (event.key === "Escape") {
        stopGeometryRunLayout("cancelled");
      }
    };
    geometryRunLayoutOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      window.removeEventListener("keydown", onKey, true);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    window.addEventListener("keydown", onKey, true);
    status = "Run layout active: existing selected polygon is slip #1. Click end slip location. Press Enter to generate or Esc to cancel.";
    render();
  }

  async function applyGeometryToSlipRecords() {
    let matched = 0;
    let unmatched = 0;
    const polygons = state.slipGeometryPolygons.map((poly) => {
      const ring = computeSlipPolygonLatLngs(poly);
      const inferred = ring.length >= 3 ? inferPolyMetricsFromRing(ring) : null;
      return {
        poly,
        ring,
        centerLL: L.latLng(Number(poly.center?.lat || 0), Number(poly.center?.lng || 0)),
        lengthFt: inferred?.lengthFt ?? poly.lengthFt ?? null,
        widthFt: inferred?.widthFt ?? poly.widthFt ?? null,
      };
    });
    // Direct link lookup by measurementId (set on polygons at commit time)
    const directByMeasurementId = new Map();
    for (const entry of polygons) {
      const mid = String(entry.poly?.linkedMeasurementId || "");
      if (mid) directByMeasurementId.set(mid, entry);
    }
    for (const record of records) {
      const measurementId = String(record?.measurement_id || "");
      // Primary: use the direct polygon↔record link established at commit time
      let hit = directByMeasurementId.get(measurementId) || null;
      if (!hit) {
        // Geometric fallback for records without a direct link
        const point = pointFromAnyWkt(record.geometry_wkt);
        if (point) {
          hit = polygons.find((entry) => pointInPolygon(point, entry.ring)) || null;
          if (!hit && polygons.length) {
            let nearest = null;
            for (const candidate of polygons) {
              const d = toFeetDistance(L.latLng(point.lat, point.lng), candidate.centerLL);
              if (!nearest || d < nearest.distanceFt) nearest = { entry: candidate, distanceFt: d };
            }
            if (nearest && nearest.distanceFt <= 8) hit = nearest.entry;
          }
        }
      }
      if (!hit) {
        unmatched += 1;
        await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
          method: "PATCH",
          body: { ...record, measured_linear_feet: null, measured_width_feet: null },
        });
        continue;
      }
      matched += 1;
      await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
        method: "PATCH",
        body: {
          ...record,
          measured_linear_feet: roundToTenthFoot(hit.lengthFt),
          measured_width_feet: roundToTenthFoot(hit.widthFt),
        },
      });
    }
    await refreshRecords({ quiet: true });
    status = `Geometry assignment complete. Matched ${matched}, unmatched ${unmatched}.`;
    render();
  }

  async function removeSelectedGeometryPolygon({ deleteLinkedRecords = true } = {}) {
    const selectedIds = new Set(getSelectedGeometryIds());
    if (!selectedIds.size) return 0;
    const removedPolys = state.slipGeometryPolygons.filter((poly) => selectedIds.has(String(poly.id)));
    if (deleteLinkedRecords) {
      const deletedMeasurementIds = new Set();
      for (const poly of removedPolys) {
        const measurementId = String(poly?.linkedMeasurementId || "");
        if (!measurementId || deletedMeasurementIds.has(measurementId)) continue;
        await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "DELETE" });
        deletedMeasurementIds.add(measurementId);
      }
      if (deletedMeasurementIds.size) {
        app.log("slip.deleted", { count: deletedMeasurementIds.size, marina: state.marinaId });
        records = records.filter((record) => !deletedMeasurementIds.has(String(record?.measurement_id || ""))); recordsVersion++;
      }
    }
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => !selectedIds.has(String(poly.id)));
    const removed = before - state.slipGeometryPolygons.length;
    if (removed > 0) {
      clearGeometrySelection();
      synchronizeCommittedGeometryFromRecords();
      persist();
    }
    return removed;
  }

  function isGeometryPolygonCommitted(poly) {
    const linkedMeasurementId = String(poly?.linkedMeasurementId || "");
    if (linkedMeasurementId) {
      return records.some((record) => String(record?.measurement_id || "") === linkedMeasurementId);
    }
    const linkedSlipId = String(poly?.linkedSlipId || "");
    if (linkedSlipId) {
      const linkedSlipRecords = records.filter((record) => String(record?.slip_id || "") === linkedSlipId);
      if (linkedSlipRecords.length === 1) return true;
    }
    return false;
  }

  function selectLatestUncommittedGeometry() {
    const candidate = [...state.slipGeometryPolygons]
      .reverse()
      .find((poly) => !isGeometryPolygonCommitted(poly));
    if (!candidate?.id) return false;
    setSelectedGeometryIds([String(candidate.id)], String(candidate.id));
    status = `Selected uncommitted polygon ${candidate.id}.`;
    persist();
    render();
    return true;
  }

  function clearUncommittedGeometryPolygons() {
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => isGeometryPolygonCommitted(poly));
    const removed = before - state.slipGeometryPolygons.length;
    normalizeGeometrySelection();
    persist();
    render();
    return removed;
  }

  function purgeStaleGeometryPolygons() {
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => isGeometryPolygonCommitted(poly));
    const removed = before - state.slipGeometryPolygons.length;
    clearGeometrySelection();
    stopGeometryPlaceMode();
    stopGeometryRunLayout("purged");
    persist();
    render();
    return removed;
  }

  function findRecordForPolygon(poly) {
    const measurementId = String(poly?.linkedMeasurementId || "").trim();
    if (measurementId) {
      const direct = records.find((record) => String(record?.measurement_id || "") === measurementId) || null;
      if (direct) return direct;
    }
    const slipId = String(poly?.linkedSlipId || "").trim();
    const marinaId = String(state.marinaId || "").trim();
    if (!slipId) return null;
    return records.find((record) => (
      String(record?.slip_id || "").trim() === slipId
      && (!marinaId || String(record?.marina_id || "").trim() === marinaId)
    )) || null;
  }

  function isRecordVisible(record) {
    const mode = String(state.colorSlipsBy || "none");
    const hidden = state.filterHiddenValues?.[mode];
    if (!hidden || !Array.isArray(hidden) || hidden.length === 0) return true;
    const hiddenSet = new Set(hidden);
    if (mode === "confidence") return !hiddenSet.has(String(record?.confidence_score || "High"));
    if (mode === "space_type") return !hiddenSet.has(String(record?.space_type || "").trim() || "unknown");
    if (mode === "dimension") {
      const tol = Number(state?.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
      const l = Number(record?.measured_linear_feet);
      const w = Number(record?.measured_width_feet);
      if (!Number.isFinite(l) || !Number.isFinite(w)) return true;
      const key = dimensionKey(l, w, tol);
      return !key || !hiddenSet.has(key);
    }
    return true;
  }

  function dimensionInfoForPolygon(poly) {
    const tol = Number(state?.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
    const record = findRecordForPolygon(poly);
    const lengthForColor = Number(record?.measured_linear_feet);
    const widthForColor = Number(record?.measured_width_feet);
    const key = Number.isFinite(lengthForColor) && Number.isFinite(widthForColor)
      ? dimensionKey(lengthForColor, widthForColor, tol)
      : dimensionKey(poly?.lengthFt, poly?.widthFt, tol);
    const color = key ? paletteColorForKey(key) : "#38bdf8";
    return {
      key,
      color,
      label: key ? `${key} ft` : "Unknown",
    };
  }

  function colorForPolygon(poly) {
    const mode = String(state.colorSlipsBy || "none");
    if (mode === "dimension") {
      return dimensionInfoForPolygon(poly).color;
    }
    if (mode === "confidence") {
      const record = findRecordForPolygon(poly);
      return confidenceColor(record?.confidence_score);
    }
    if (mode === "space_type") {
      const record = findRecordForPolygon(poly);
      const spaceType = String(record?.space_type || "").trim() || "unknown";
      return spaceTypeColorMap.get(spaceType) ?? "#38bdf8";
    }
    return "#38bdf8";
  }

  function renderGeometry() {
    normalizeGeometrySelection();
    if (!slipHandleDragging) geometryHandlesLayer.clearLayers();
    const mode = String(state.colorSlipsBy || "none");
    if (mode === "space_type") {
      const allTypes = [...new Set(records.map((r) => String(r?.space_type || "").trim() || "unknown"))];
      spaceTypeColorMap = new Map(allTypes.map((t) => [t, SPACE_TYPE_COLOR_MAP[t] ?? paletteColorForKey(t)]));
    }
    const dimensionLegendMap = new Map();
    const spaceTypeLegendMap = new Map();
    const activePolyIds = new Set();

    state.slipGeometryPolygons.forEach((poly) => {
      if (poly.committed) {
        const record = findRecordForPolygon(poly);
        if (record && !isRecordVisible(record)) return;
      }
      activePolyIds.add(poly.id);
      const selected = isGeometrySelected(poly.id);
      const dimInfo = mode === "dimension" ? dimensionInfoForPolygon(poly) : null;
      const baseColor = dimInfo ? dimInfo.color : colorForPolygon(poly);
      if (dimInfo?.key && !dimensionLegendMap.has(dimInfo.key)) {
        dimensionLegendMap.set(dimInfo.key, { key: dimInfo.key, color: dimInfo.color, label: dimInfo.label });
      }
      if (mode === "space_type") {
        const record = findRecordForPolygon(poly);
        const spaceType = String(record?.space_type || "").trim() || "unknown";
        if (!spaceTypeLegendMap.has(spaceType)) {
          spaceTypeLegendMap.set(spaceType, { key: spaceType, color: spaceTypeColorMap.get(spaceType) ?? "#38bdf8", label: spaceType });
        }
      }
      const needsInteraction = selected || !poly.committed;
      const styleColor = selected ? "#f59e0b" : baseColor;
      const styleWeight = selected ? 3 : (poly.committed ? 1.5 : 2);
      const styleFillOpacity = selected ? 0.35 : (poly.committed ? 0.28 : (poly.locked ? 0.12 : 0.22));
      const styleDash = poly.locked ? "5 4" : "";
      const ringKey = poly.freeform
        ? `f|${(poly.ringLatLngs || []).length}|${poly.ringLatLngs?.[0]?.[0] ?? ""}|${poly.ringLatLngs?.[0]?.[1] ?? ""}`
        : `r|${poly.lengthFt}|${poly.widthFt}|${poly.headingDeg}|${poly.center?.lat}|${poly.center?.lng}|${poly.chamferEnabled ? 1 : 0}|${poly.chamferAngleDeg}|${poly.chamferMagnitudeFt}|${poly.chamferSide}`;
      const renderKey = `${needsInteraction ? 1 : 0}|${styleColor}|${styleWeight}|${styleFillOpacity}|${styleDash}|${ringKey}`;

      const cached = geometryLayerCache.get(poly.id);

      if (cached && cached.renderKey === renderKey) {
        return; // nothing changed — skip entirely
      }

      if (cached && cached.interactive === needsInteraction) {
        // update in-place — no layer destroy/create, no GC pressure
        cached.path.setStyle({ color: styleColor, weight: styleWeight, fillColor: styleColor, fillOpacity: styleFillOpacity, dashArray: styleDash });
        if (cached.ringKey !== ringKey) {
          cached.path.setLatLngs(computeSlipPolygonLatLngs(poly));
        }
        if (needsInteraction) {
          const record = findRecordForPolygon(poly);
          const operational = String(record?.operational_status || "operable");
          cached.path.setTooltipContent(`${Number(poly.lengthFt).toFixed(1)} x ${Number(poly.widthFt).toFixed(1)} ft | ${operational}`);
        }
        cached.renderKey = renderKey;
        cached.ringKey = ringKey;
      } else {
        // new polygon or interactivity changed — must recreate
        if (cached) geometryLayer.removeLayer(cached.path);
        const ring = computeSlipPolygonLatLngs(poly);
        const path = L.polygon(ring, {
          color: styleColor, weight: styleWeight, fillColor: styleColor,
          fillOpacity: styleFillOpacity, dashArray: styleDash,
          interactive: needsInteraction, renderer: canvasRenderer,
        }).addTo(geometryLayer);
        if (needsInteraction) {
          const record = findRecordForPolygon(poly);
          const operational = String(record?.operational_status || "operable");
          path.bindTooltip(`${Number(poly.lengthFt).toFixed(1)} x ${Number(poly.widthFt).toFixed(1)} ft | ${operational}`);
        }
        geometryLayerCache.set(poly.id, { path, interactive: needsInteraction, renderKey, ringKey });
      }
    });

    // Remove polygons that no longer exist in state
    geometryLayerCache.forEach((cached, polyId) => {
      if (!activePolyIds.has(polyId)) {
        geometryLayer.removeLayer(cached.path);
        geometryLayerCache.delete(polyId);
      }
    });

    if (mode === "dimension") {
      const entries = [...dimensionLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "space_type") {
      const entries = [...spaceTypeLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else {
      clearDimensionLegend();
    }
    renderSlipEditHandles();
  }

  function renderSlipEditHandles() {
    if (slipHandleDragging) return;
    geometryHandlesLayer.clearLayers();

    const selIds = getSelectedMeasurementIds();
    if (selIds.length !== 1) return;

    const record = records.find((r) => r.measurement_id === selIds[0]);
    if (!record) return;

    const ring = parsePolygonWkt(record.geometry_wkt);
    if (!ring || ring.length < 3) return;

    const n = ring.length;
    const R = 6371000;

    for (let i = 0; i < n; i++) {
      const a = { lat: ring[i][0], lng: ring[i][1] };
      const b = { lat: ring[(i + 1) % n][0], lng: ring[(i + 1) % n][1] };
      const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };

      // Side vector and perpendicular normal in meter space
      const midLatRad = mid.lat * Math.PI / 180;
      const svx = (b.lng - a.lng) * Math.cos(midLatRad) * R;
      const svy = (b.lat - a.lat) * R;
      const sideLenM = Math.sqrt(svx * svx + svy * svy);
      if (sideLenM < 0.001) continue;
      const nx = -svy / sideLenM; // normal unit east component
      const ny = svx / sideLenM;  // normal unit north component

      const iCopy = i;
      const nCopy = n;
      const midCopy = { lat: mid.lat, lng: mid.lng };
      const nxCopy = nx;
      const nyCopy = ny;
      const origRing = ring.map((p) => ({ lat: p[0], lng: p[1] }));
      const measurementId = record.measurement_id;

      const handle = L.marker([mid.lat, mid.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div style="width:20px;height:20px;display:flex;align-items:center;justify-content:center;cursor:move;"><div style="width:11px;height:11px;background:#f59e0b;border:2px solid #1e293b;border-radius:2px;transform:rotate(45deg);box-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;"></div></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
        draggable: true,
        interactive: true,
        bubblingMouseEvents: false,
        zIndexOffset: 1000,
      });

      handle.on("dragstart", () => {
        slipHandleDragging = true;
      });

      handle.on("drag", (ev) => {
        const pos = ev.latlng;
        const dx = (pos.lng - midCopy.lng) * Math.cos(midCopy.lat * Math.PI / 180) * R;
        const dy = (pos.lat - midCopy.lat) * R;
        const perpM = dx * nxCopy + dy * nyCopy;
        const snappedFt = Math.round(perpM * 3.280839895 / 0.1) * 0.1;
        const snappedM = snappedFt / 3.280839895;

        const newRing = origRing.map((pt, j) => {
          if (j === iCopy || j === (iCopy + 1) % nCopy) {
            const ptLatRad = pt.lat * Math.PI / 180;
            return {
              lat: pt.lat + snappedM * nyCopy / R,
              lng: pt.lng + snappedM * nxCopy / (R * Math.cos(ptLatRad)),
            };
          }
          return pt;
        });

        const poly = state.slipGeometryPolygons.find((p) =>
          String(p?.linkedMeasurementId || "") === String(measurementId));
        if (poly) {
          const cached = geometryLayerCache.get(poly.id);
          if (cached) cached.path.setLatLngs(newRing.map((p) => [p.lat, p.lng]));
        }

        const snappedLat = midCopy.lat + snappedM * nyCopy / R;
        const snappedLng = midCopy.lng + snappedM * nxCopy / (R * Math.cos(midCopy.lat * Math.PI / 180));
        handle.setLatLng([snappedLat, snappedLng]);
      });

      handle.on("dragend", async () => {
        slipHandleDragging = false;
        const pos = handle.getLatLng();
        const dx = (pos.lng - midCopy.lng) * Math.cos(midCopy.lat * Math.PI / 180) * R;
        const dy = (pos.lat - midCopy.lat) * R;
        const perpM = dx * nxCopy + dy * nyCopy;
        const snappedFt = Math.round(perpM * 3.280839895 / 0.1) * 0.1;
        const snappedM = snappedFt / 3.280839895;

        const newRing = origRing.map((pt, j) => {
          if (j === iCopy || j === (iCopy + 1) % nCopy) {
            const ptLatRad = pt.lat * Math.PI / 180;
            return {
              lat: pt.lat + snappedM * nyCopy / R,
              lng: pt.lng + snappedM * nxCopy / (R * Math.cos(ptLatRad)),
            };
          }
          return pt;
        });

        const newWkt = buildPolygonWkt(newRing.map((p) => [p.lat, p.lng]));
        records = records.map((r) =>
          r.measurement_id === measurementId ? { ...r, geometry_wkt: newWkt } : r
        );
        recordsVersion++;
        synchronizeCommittedGeometryFromRecords();
        renderGeometry();

        try {
          const updatedRecord = records.find((r) => r.measurement_id === measurementId);
          await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, {
            method: "PATCH",
            body: updatedRecord,
          });
          const rec = records.find((r) => r.measurement_id === measurementId);
          app.log("slip.geometry_edited", { slip_id: rec?.slip_id, marina: state.marinaId });
          status = `Resized slip ${rec?.slip_id || measurementId}.`;
        } catch (error) {
          status = error?.message || "Failed to save resized geometry.";
        }
        render();
      });

      geometryHandlesLayer.addLayer(handle);
      const el = handle.getElement();
      if (el) el.style.cursor = "move";
    }
  }

  function drawGeometryRunGhost() {
    geometryRunPreviewLayer.clearLayers();
    if (!geometryRunLayoutActive) return;
    const guidePoints = [
      ...geometryRunEndpoints,
      ...(geometryRunEndpoints.length === 1 && geometryRunHoverPoint ? [geometryRunHoverPoint] : []),
    ];
    const points = geometryRunPreviewPoints();
    const selected = selectedGeometryPolygon();
    if (!selected || !points.length) return;
    if (guidePoints.length >= 2) {
      L.polyline(guidePoints.map((point) => [point.lat, point.lng]), {
        color: "#f59e0b",
        weight: 2,
        dashArray: "6 6",
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
    }
    const heading = Number(selected.headingDeg || 0);
    const previewIds = previewSlipIds(state, points.length);
    const selectedCenter = {
      lat: Number(selected.center?.lat || 0),
      lng: Number(selected.center?.lng || 0),
    };
    points.forEach((point, index) => {
      if (index === 0) return;
      const targetCenter = { lat: Number(point.lat), lng: Number(point.lng) };
      const translatedRing = (Array.isArray(selected.ringLatLngs) && selected.ringLatLngs.length >= 3)
        ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, targetCenter)
        : null;
      const previewPoly = {
        ...selected,
        center: targetCenter,
        headingDeg: heading,
        ringLatLngs: translatedRing || null,
      };
      const ring = computeSlipPolygonLatLngs(previewPoly);
      L.polygon(ring, {
        color: "rgba(245,158,11,0.95)",
        weight: 2,
        fillColor: "rgba(245,158,11,0.35)",
        fillOpacity: 0.12,
        dashArray: "4 4",
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
      L.circleMarker([point.lat, point.lng], {
        radius: index === points.length - 1 ? 4 : 3,
        color: "#f59e0b",
        weight: 1.5,
        fillColor: "#fbbf24",
        fillOpacity: 0.85,
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
      const idLabel = previewIds[index] || "";
      if (idLabel && geometryRunEndpoints.length >= 2) {
        L.marker([point.lat, point.lng], {
          interactive: false,
          icon: L.divIcon({
            className: "slip-geom-run-id-wrap",
            iconSize: null,
            iconAnchor: [0, 0],
            html: `<div style="transform:translate(-50%,-135%);padding:2px 8px;border-radius:999px;background:rgba(15,23,42,0.86);border:1px solid rgba(56,189,248,0.45);color:#e2e8f0;font:700 11px/1.1 system-ui,sans-serif;white-space:nowrap;">${esc(idLabel)}</div>`,
          }),
        }).addTo(geometryRunPreviewLayer);
      }
    });
  }

  function renderGeometryPlacePreview() {
    geometryPlacePreviewLayer.clearLayers();
    if (!activeGeometryPlaceMode || !geometryPlaceHoverPoint || !geometryPlaceDraft) return;
    const translatedRing = (
      Array.isArray(geometryPlaceDraft.ringLatLngs)
      && geometryPlaceDraft.ringLatLngs.length >= 3
      && geometryPlaceDraft.sourceCenter
    )
      ? translateRingLatLngs(
        geometryPlaceDraft.ringLatLngs,
        geometryPlaceDraft.sourceCenter,
        { lat: Number(geometryPlaceHoverPoint.lat), lng: Number(geometryPlaceHoverPoint.lng) },
      )
      : null;
    const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
    const previewPoly = {
      id: "draft",
      center: translatedRing
        ? { lat: Number(inferred?.center?.lat ?? geometryPlaceHoverPoint.lat), lng: Number(inferred?.center?.lng ?? geometryPlaceHoverPoint.lng) }
        : { lat: Number(geometryPlaceHoverPoint.lat), lng: Number(geometryPlaceHoverPoint.lng) },
      lengthFt: Number((inferred?.lengthFt ?? geometryPlaceDraft.lengthFt) || 40),
      widthFt: Number((inferred?.widthFt ?? geometryPlaceDraft.widthFt) || 14),
      headingDeg: Number((inferred?.headingDeg ?? geometryPlaceDraft.headingDeg) || 0),
      chamferEnabled: !!geometryPlaceDraft.chamferEnabled,
      chamferSide: Number(geometryPlaceDraft.chamferSide || inferred?.chamferSide || 1) < 0 ? -1 : 1,
      chamferAngleDeg: Number(geometryPlaceDraft.chamferAngleDeg || 0),
      chamferMagnitudeFt: Number(geometryPlaceDraft.chamferMagnitudeFt || 0),
      ringLatLngs: translatedRing || null,
    };
    const ring = computeSlipPolygonLatLngs(previewPoly);
    L.polygon(ring, {
      color: "rgba(16,185,129,0.95)",
      weight: 2,
      fillColor: "rgba(16,185,129,0.35)",
      fillOpacity: 0.15,
      dashArray: "6 4",
      interactive: false,
      renderer: canvasRenderer,
    }).addTo(geometryPlacePreviewLayer);
    const previewId = previewSlipIds(state, 1)[0] || "SLIP";
    L.marker([previewPoly.center.lat, previewPoly.center.lng], {
      interactive: false,
      icon: L.divIcon({
        className: "slip-geom-draft-id-wrap",
        iconSize: null,
        iconAnchor: [0, 0],
        html: `<div style="transform:translate(-50%,-135%);padding:2px 8px;border-radius:999px;background:rgba(15,23,42,0.88);border:1px solid rgba(52,211,153,0.6);color:#d1fae5;font:700 11px/1.1 system-ui,sans-serif;white-space:nowrap;">${esc(previewId)}</div>`,
      }),
    }).addTo(geometryPlacePreviewLayer);
  }

  function renderGeometryRunPreview() {
    geometryRunHandleLayer.clearLayers();
    drawGeometryRunGhost();
    if (!geometryRunLayoutActive) return;
    const selected = selectedGeometryPolygon();
    if (!selected) return;
    if (geometryRunEndpoints.length >= 1) {
      const startHandle = L.marker([geometryRunEndpoints[0].lat, geometryRunEndpoints[0].lng], {
        draggable: true,
        autoPan: true,
        zIndexOffset: 1200,
        icon: L.divIcon({
          className: "slip-geom-run-start",
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          html: `<div style="width:18px;height:18px;border-radius:999px;background:rgba(34,197,94,.95);border:2px solid rgba(15,23,42,.9);box-shadow:0 0 0 1px rgba(187,247,208,.55),0 6px 12px rgba(15,23,42,.22);"></div>`,
        }),
      }).addTo(geometryRunHandleLayer);
      startHandle.bindTooltip("Drag run start", { direction: "top", offset: [0, -8] });
      startHandle.on("mousedown", (event) => {
        event?.originalEvent?.preventDefault?.();
        event?.originalEvent?.stopPropagation?.();
      });
      startHandle.on("dragstart", () => {
        suppressMapClicks(700);
        try { map.dragging.disable(); } catch {}
      });
      startHandle.on("drag", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        geometryRunEndpoints[0] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        if (geometryRunEndpoints.length === 1) geometryRunHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
        // Keep the actual selected polygon anchored to the run start point.
        updateSelectedGeometry({ center: { lat: Number(ll.lat), lng: Number(ll.lng) } });
        // Repaint live polygon position while dragging, like the run ghost.
        renderGeometry();
        drawGeometryRunGhost();
      });
      startHandle.on("dragend", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        suppressMapClicks(700);
        try { map.dragging.enable(); } catch {}
        geometryRunEndpoints[0] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        updateSelectedGeometry({ center: { lat: Number(ll.lat), lng: Number(ll.lng) } });
        status = "Adjusted run start point.";
        render();
      });
    }
    if (geometryRunEndpoints.length >= 2) {
      const endHandle = L.marker([geometryRunEndpoints[1].lat, geometryRunEndpoints[1].lng], {
        draggable: true,
        autoPan: true,
        zIndexOffset: 1200,
        icon: L.divIcon({
          className: "slip-geom-run-end",
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          html: `<div style="width:18px;height:18px;border-radius:999px;background:rgba(245,158,11,.95);border:2px solid rgba(15,23,42,.9);box-shadow:0 0 0 1px rgba(254,243,199,.55),0 6px 12px rgba(15,23,42,.22);"></div>`,
        }),
      }).addTo(geometryRunHandleLayer);
      endHandle.bindTooltip("Drag run end", { direction: "top", offset: [0, -8] });
      endHandle.on("mousedown", (event) => {
        event?.originalEvent?.preventDefault?.();
        event?.originalEvent?.stopPropagation?.();
      });
      endHandle.on("dragstart", () => {
        suppressMapClicks(700);
        try { map.dragging.disable(); } catch {}
      });
      endHandle.on("drag", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        geometryRunEndpoints[1] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        drawGeometryRunGhost();
      });
      endHandle.on("dragend", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        suppressMapClicks(700);
        try { map.dragging.enable(); } catch {}
        geometryRunEndpoints[1] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        status = "Adjusted run end point.";
        render();
      });
    }
  }

  function renderFreeDrawPreview() {
    geometryFreeDrawLayer.clearLayers();
    geometryFreeDrawDynamicLayer.clearLayers();
    if (!freeDrawActive) return;
    const pts = freeDrawPoints;
    pts.forEach((pt, i) => {
      L.circleMarker([pt.lat, pt.lng], {
        radius: i === 0 ? 7 : 5,
        color: i === 0 ? "#10b981" : "#60a5fa",
        fillColor: i === 0 ? "#10b981" : "#60a5fa",
        fillOpacity: 1, weight: 2, interactive: false, renderer: canvasRenderer,
      }).addTo(geometryFreeDrawLayer);
    });
    if (pts.length >= 2) {
      L.polyline(pts.map((p) => [p.lat, p.lng]), {
        color: "#60a5fa", weight: 2, dashArray: "5 4", interactive: false, renderer: canvasRenderer,
      }).addTo(geometryFreeDrawLayer);
    }
    renderFreeDrawDynamic();
  }

  function renderFreeDrawDynamic() {
    geometryFreeDrawDynamicLayer.clearLayers();
    if (!freeDrawActive) return;
    const pts = freeDrawPoints;
    const hover = freeDrawHoverPoint;
    if (!hover || pts.length < 1) return;
    const last = pts[pts.length - 1];
    L.polyline([[last.lat, last.lng], [hover.lat, hover.lng]], {
      color: "#60a5fa", weight: 1.5, dashArray: "3 3", opacity: 0.65, interactive: false, renderer: canvasRenderer,
    }).addTo(geometryFreeDrawDynamicLayer);
    if (pts.length >= 2) {
      L.polygon([...pts.map((p) => [p.lat, p.lng]), [hover.lat, hover.lng]], {
        color: "#60a5fa", weight: 1, fillColor: "#60a5fa", fillOpacity: 0.1,
        dashArray: "5 4", interactive: false, renderer: canvasRenderer,
      }).addTo(geometryFreeDrawDynamicLayer);
    }
    if (pts.length >= 3) {
      const firstPx = map.latLngToContainerPoint(L.latLng(pts[0].lat, pts[0].lng));
      const hoverPx = map.latLngToContainerPoint(L.latLng(hover.lat, hover.lng));
      if (Math.hypot(firstPx.x - hoverPx.x, firstPx.y - hoverPx.y) <= 16) {
        L.circleMarker([pts[0].lat, pts[0].lng], {
          radius: 10, color: "#10b981", fillColor: "#10b981", fillOpacity: 0.3,
          weight: 2, interactive: false, renderer: canvasRenderer,
        }).addTo(geometryFreeDrawDynamicLayer);
      }
    }
  }

  function slipLabelScale() {
    const z = Number(map?.getZoom?.() ?? 14);
    // Shrink labels while zooming out to reduce overlap.
    return clampNumber(0.55 + ((z - 12) * 0.075), 0.45, 1);
  }

  function renderMap() {
    markersLayer.clearLayers();
    slipIdMarkersLayer.clearLayers();
    depthMarkersLayer.clearLayers();
    const zoom = Number(map?.getZoom?.() ?? 14);
    const showSlipIdBadges = zoom >= 20;
    const labelScale = slipLabelScale();
    const labelFontPx = Math.max(8, Math.round(9 * labelScale));
    const labelPadY = Math.max(2, Math.round(3 * labelScale));
    const labelPadX = Math.max(4, Math.round(6 * labelScale));
    records.forEach((record) => {
      const point = pointFromAnyWkt(record.geometry_wkt);
      if (!point) return;
      const selected = record.measurement_id === state.selectedMeasurementId;
      const lineCoords = parseLineStringWkt(record.geometry_wkt);
      if (Array.isArray(lineCoords) && lineCoords.length >= 2) {
        const line = L.polyline(lineCoords.map((p) => [p.lat, p.lng]), {
          color: selected ? "#f59e0b" : "#d946ef",
          weight: selected ? 9 : 7,
          opacity: 0.94,
          lineCap: "round",
          renderer: canvasRenderer,
        }).addTo(markersLayer);
        line.on("click", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          event?.originalEvent?.stopPropagation?.();
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          const toggle = !!(event?.originalEvent?.ctrlKey || event?.originalEvent?.metaKey || event?.originalEvent?.shiftKey);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle });
          persist();
          render();
        });
      }
      if (state.showSlipIds && showSlipIdBadges) {
        const label = record.slip_id || record.measurement_id;
        const marker = L.marker([point.lat, point.lng], {
          interactive: true,
          draggable: selected,
          pane: "slip-id-badge-pane",
          zIndexOffset: selected ? 1500 : 1000,
          icon: L.divIcon({
            className: "slip-inventory-map-label-wrap",
            iconSize: null,
            iconAnchor: [0, 0],
            html: `
              <div style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                padding:${labelPadY}px ${labelPadX}px;
                border-radius:999px;
                background:${selected ? "rgba(245,158,11,0.92)" : "rgba(15,23,42,0.88)"};
                color:${selected ? "#111827" : "#e2e8f0"};
                border:1px solid ${selected ? "rgba(254,243,199,0.95)" : "rgba(56,189,248,0.55)"};
                box-shadow:${selected ? "0 0 0 1px rgba(245,158,11,0.28)" : "0 6px 14px rgba(15,23,42,0.22)"};
                font:700 ${labelFontPx}px/1.2 system-ui,sans-serif;
                white-space:nowrap;
                transform:translate(-50%, -50%);
                pointer-events:auto;
                cursor:pointer;
              ">${esc(label)}</div>
            `,
          }),
        }).addTo(slipIdMarkersLayer);
        marker.on("click", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          event?.originalEvent?.preventDefault?.();
          event?.originalEvent?.stopPropagation?.();
          event?.originalEvent?.stopImmediatePropagation?.();
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          const toggle = !!(event?.originalEvent?.ctrlKey || event?.originalEvent?.metaKey || event?.originalEvent?.shiftKey);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle });
          persist();
          render();
        });
        marker.on("dragstart", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle: false });
          persist();
        });
        marker.on("drag", (event) => {
          const ll = event?.target?.getLatLng?.();
          const lat = Number(ll?.lat);
          const lng = Number(ll?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          const geom = findGeometryForRecord(record);
          if (!geom?.id) return;
          const selectedIds = getSelectedGeometryIds();
          if (!selectedIds.includes(String(geom.id))) setSelectedGeometryIds([String(geom.id)], String(geom.id));
          updateSelectedGeometry({ center: { lat, lng } });
          renderGeometry();
          renderGeometryRunPreview();
        });
        marker.on("dragend", async (event) => {
          const ll = event?.target?.getLatLng?.();
          const lat = Number(ll?.lat);
          const lng = Number(ll?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          const geom = findGeometryForRecord(record);
          if (!geom?.id) return;
          const selectedIds = getSelectedGeometryIds();
          if (!selectedIds.includes(String(geom.id))) setSelectedGeometryIds([String(geom.id)], String(geom.id));
          updateSelectedGeometry({ center: { lat, lng } });
          const moved = selectedGeometryPolygon();
          const movedRing = moved ? computeSlipPolygonLatLngs(moved) : null;
          const nextWkt = movedRing && movedRing.length >= 3
            ? buildPolygonWkt(movedRing.map((pair) => [pair.lat, pair.lng]))
            : buildPointWkt(lng, lat);
          const nextRecord = {
            ...record,
            geometry_wkt: nextWkt,
          };
          records = records.map((entry) => (
            String(entry?.measurement_id || "") === String(record?.measurement_id || "")
              ? { ...entry, geometry_wkt: nextWkt }
              : entry
          )); recordsVersion++;
          try {
            await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
              method: "PATCH",
              body: nextRecord,
            });
            app.log("slip.geometry_moved", { slip_id: record.slip_id, marina: state.marinaId });
            status = `Moved ${record.slip_id || record.measurement_id}.`;
          } catch (error) {
            status = error?.message || "Failed to persist moved slip geometry.";
          }
          persist();
          render();
        });
      }
      if (state.showDepths && zoom >= 17) {
        const dv = Number.isFinite(Number(record.measured_depth_feet)) ? Number(record.measured_depth_feet) : null;
        if (dv !== null) {
          const offset = state.showSlipIds && showSlipIdBadges ? "12px" : "-50%";
          L.marker([point.lat, point.lng], {
            interactive: false,
            pane: "slip-id-badge-pane",
            icon: L.divIcon({
              className: "slip-inventory-map-label-wrap",
              iconSize: null,
              iconAnchor: [0, 0],
              html: `<div style="font:600 ${labelFontPx}px/1.2 system-ui,sans-serif;white-space:nowrap;color:#e2e8f0;text-shadow:0 1px 3px rgba(0,0,0,0.9),0 0 6px rgba(0,0,0,0.7);transform:translate(-50%,${offset});pointer-events:none;">${dv.toFixed(1)} ft</div>`,
            }),
          }).addTo(depthMarkersLayer);
        }
      }
    });
    renderPreview();
    renderGeometry();
    renderGeometryPlacePreview();
    renderGeometryRunPreview();
    renderFreeDrawPreview();
  }

  function findGeometryForRecord(record) {
    const measurementId = String(record?.measurement_id || "");
    if (measurementId) {
      const direct = state.slipGeometryPolygons.find((poly) => String(poly?.linkedMeasurementId || "") === measurementId);
      if (direct) return direct;
    }
    const slipId = String(record?.slip_id || "");
    if (slipId) {
      const sameSlip = state.slipGeometryPolygons.filter((poly) => String(poly?.linkedSlipId || "") === slipId);
      if (sameSlip.length === 1) return sameSlip[0];
    }
    const point = pointFromAnyWkt(record?.geometry_wkt);
    if (!point) return null;
    const polygons = state.slipGeometryPolygons.map((poly) => ({
      poly,
      ring: computeSlipPolygonLatLngs(poly),
    }));
    const hit = polygons.find((entry) => pointInPolygon(point, entry.ring)) || null;
    return hit?.poly || null;
  }

  function sortedRecordGeometryRows(recordGeometry = []) {
    const sortKey = String(state.tableSortKey || "slip_id");
    const dir = String(state.tableSortDir || "asc") === "desc" ? -1 : 1;
    const toText = (value) => String(value ?? "").trim().toLowerCase();
    return [...recordGeometry].sort((a, b) => {
      if (sortKey === "slip_id") {
        const av = toText(a?.record?.slip_id);
        const bv = toText(b?.record?.slip_id);
        const an = /^(\D*)(\d+)$/.exec(av);
        const bn = /^(\D*)(\d+)$/.exec(bv);
        if (an && bn && an[1] === bn[1]) {
          const diff = Number(an[2]) - Number(bn[2]);
          if (diff !== 0) return diff * dir;
        }
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
      }
      const av = toText(a?.record?.[sortKey]);
      const bv = toText(b?.record?.[sortKey]);
      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }

  function formatCellValue(key, value, displayToleranceFt) {
    if (value == null || value === "") return "";
    if (key === "measured_linear_feet" || key === "measured_width_feet" || key === "marketable_linear_feet" || key === "marketable_width_feet") {
      return formatFeetForDisplay(value, displayToleranceFt);
    }
    if (key === "measured_depth_feet") return Number.isFinite(Number(value)) ? Number(value).toFixed(1) : String(value);
    if (key === "geometry_wkt") {
      const s = String(value);
      return s.length > 60 ? s.slice(0, 57) + "…" : s;
    }
    return String(value);
  }

  function renderCellContent(col, record, displayToleranceFt) {
    const mid = esc(record.measurement_id);
    if (BINARY_COLS.has(col.key)) {
      const checked = String(record[col.key] || "").toLowerCase() === "yes";
      return `<input type="checkbox" data-slip-binary="${esc(col.key)}" data-slip-meas="${mid}" ${checked ? "checked" : ""} style="width:16px;height:16px;cursor:pointer;accent-color:#60a5fa;" />`;
    }
    if (col.key === "space_type") {
      const v = String(record.space_type || "");
      const opts = SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${v === o ? " selected" : ""}>${esc(o)}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;"><option value=""></option>${opts}</select>`;
    }
    if (col.key === "out_of_service_reason") {
      const v = String(record.out_of_service_reason || "");
      const oos = String(record.out_of_service || "").toLowerCase() === "yes";
      const opts = OOS_REASON_OPTIONS.map((o) => `<option value="${esc(o)}"${v.toLowerCase() === o.toLowerCase() ? " selected" : ""}>${esc(o)}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" ${oos ? "" : "disabled"} style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;opacity:${oos ? 1 : 0.4};"><option value=""></option>${opts}</select>`;
    }
    if (col.key === "confidence_score") {
      const v = String(record.confidence_score || "High");
      const opts = ["High", "Medium", "Low"].map((o) => `<option value="${o}"${v === o ? " selected" : ""}>${o}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;">${opts}</select>`;
    }
    if (!NON_EDITABLE_COLS.has(col.key) && col.key !== "slip_id") {
      const isNum = NUMERIC_COLS.has(col.key);
      const isDate = col.key === "measurement_date";
      const isComment = col.key === "marina_comments";
      const rawVal = record[col.key];
      let displayVal = "";
      if (rawVal != null && rawVal !== "") {
        if (isNum) { const n = Number(rawVal); displayVal = Number.isFinite(n) ? formatFeetForDisplay(n, displayToleranceFt) : ""; }
        else { displayVal = String(rawVal); }
      }
      return `<input type="${isDate ? "date" : isNum ? "number" : "text"}" data-slip-text="${esc(col.key)}" data-slip-meas="${mid}"
        value="${esc(displayVal)}"${isNum ? ' step="0.1" min="0"' : ""}
        style="width:calc(100% - 4px);background:transparent;border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 3px;font-size:11px;font-family:inherit;text-align:${isComment ? "left" : "center"};" />`;
    }
    return esc(formatCellValue(col.key, record[col.key], displayToleranceFt));
  }

  function getSelectedMeasurementIds() {
    const selGeomIds = new Set(getSelectedGeometryIds());
    return records
      .filter((r) => {
        if (r.measurement_id === state.selectedMeasurementId) return true;
        if (tableSelectedMeasurementIds.has(r.measurement_id)) return true;
        const geom = findGeometryForRecord(r);
        const gid = String(geom?.id || "");
        return gid && selGeomIds.has(gid);
      })
      .map((r) => r.measurement_id);
  }

  async function saveRecordField(measurementId, fieldKey, newValue) {
    const idx = records.findIndex((r) => r.measurement_id === measurementId);
    if (idx < 0) return;
    const updated = normalizeRecord({ ...records[idx], [fieldKey]: newValue });
    records[idx] = updated;
    lastLocalSaveAt = Date.now();
    await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "PATCH", body: updated });
  }

  async function saveFieldForSelected(triggerMeasurementId, fieldKey, newValue) {
    const selectedIds = getSelectedMeasurementIds();
    const idsToSave = selectedIds.length > 0 && selectedIds.includes(triggerMeasurementId)
      ? selectedIds
      : [triggerMeasurementId];
    const slipIdChanges = [];

    // Optimistic update all matching records
    for (const mid of idsToSave) {
      const idx = records.findIndex((r) => r.measurement_id === mid);
      if (idx < 0) continue;
      const current = records[idx];
      const nextPatch = { ...current, [fieldKey]: newValue };
      if (fieldKey === "dock_name") {
        const nextSlipId = slipIdForDockNameChange(current, newValue, state.slipIdSeparator);
        nextPatch.slip_id = nextSlipId;
        if (String(current.slip_id || "") !== String(nextSlipId || "")) {
          slipIdChanges.push({ measurementId: String(mid), previousSlipId: String(current.slip_id || ""), nextSlipId: String(nextSlipId || "") });
        }
      }
      records[idx] = normalizeRecord(nextPatch);
    }

    if (slipIdChanges.length) {
      state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => {
        const linkedMeasurementId = String(poly?.linkedMeasurementId || "");
        const byMeasurement = slipIdChanges.find((change) => change.measurementId === linkedMeasurementId);
        if (byMeasurement) return { ...poly, linkedSlipId: byMeasurement.nextSlipId };
        if (linkedMeasurementId) return poly;
        const linkedSlipId = String(poly?.linkedSlipId || "");
        const bySlipId = slipIdChanges.find((change) => change.previousSlipId && change.previousSlipId === linkedSlipId);
        return bySlipId ? { ...poly, linkedSlipId: bySlipId.nextSlipId } : poly;
      });
      persist();
    }

    lastLocalSaveAt = Date.now();
    recordsVersion++;
    renderBottomPanel();
    renderMap();
    renderGeometry();
    // Fire PATCH for all in parallel, report first error
    const errors = (await Promise.all(
      idsToSave.map((mid) => {
        const rec = records.find((r) => r.measurement_id === mid);
        if (!rec) return Promise.resolve(null);
        return requestJson(`${API_BASE}/${encodeURIComponent(mid)}`, { method: "PATCH", body: rec })
          .then(() => null)
          .catch((err) => err?.message || String(err));
      })
    )).filter(Boolean);
    if (!errors.length) {
      app.log("slip.field_edited", { field: fieldKey, count: idsToSave.length, marina: state.marinaId });
      renderBottomPanel();
    }
    if (errors.length) { status = `${errors.length} save error(s): ${errors[0]}`; render(); }
  }

  function renderColCfgPanel(cfg) {
    const rows = cfg.order.map((key) => {
      const col = ALL_COLUMNS.find((c) => c.key === key);
      if (!col) return "";
      const checked = !cfg.hidden.has(key);
      return `<div class="slip-col-cfg-row" data-cfg-key="${esc(key)}" draggable="true" style="display:flex;align-items:center;gap:8px;padding:4px 6px;border-radius:4px;cursor:grab;user-select:none;background:transparent;border:1px solid transparent;">
        <span style="color:rgba(148,163,184,.5);font-size:14px;line-height:1;">⠿</span>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;flex:1;min-width:0;">
          <input type="checkbox" data-cfg-toggle="${esc(key)}" ${checked ? "checked" : ""} />
          <span style="font-size:12px;white-space:nowrap;">${esc(col.label)}</span>
          <span class="small" style="color:rgba(148,163,184,.5);font-size:10px;">${esc(col.key)}</span>
        </label>
        <button class="btn" data-cfg-reset-width="${esc(key)}" style="padding:1px 5px;font-size:10px;" title="Reset width">↺</button>
      </div>`;
    }).join("");
    return `
      <div id="slip-col-cfg-panel" style="position:absolute;top:0;right:0;bottom:0;width:300px;background:rgba(15,23,42,.97);border-left:1px solid rgba(148,163,184,.2);z-index:10;display:flex;flex-direction:column;padding:10px;overflow:hidden;">
        <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:8px;">
          <div class="lm-title" style="margin:0;">Column Config</div>
          <div class="row" style="gap:6px;">
            <button id="slip-col-cfg-reset-all" class="btn" style="font-size:11px;">Reset All</button>
            <button id="slip-col-cfg-close" class="btn" style="font-size:11px;">Done</button>
          </div>
        </div>
        <div class="small" style="color:rgba(148,163,184,.6);margin-bottom:6px;">Drag to reorder · Toggle visibility · ↺ reset width</div>
        <div id="slip-col-cfg-list" style="flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:2px;">
          ${rows}
        </div>
      </div>`;
  }

  let _lastPanelKey = "";
  function renderBottomPanel({ force = false } = {}) {
    const _selectedGeomIds = getSelectedGeometryIds();
    const _modeKey = `${geometryRunLayoutActive ? 1 : 0}${activeGeometryPlaceMode ? 1 : 0}${lineLayoutActive ? 1 : 0}${freeDrawActive ? 1 : 0}`;
    const _filterKey = [...(state.filterHiddenValues?.[state.colorSlipsBy] || [])].sort().join(",");
    const _panelKey = `${recordsVersion}|${state.selectedMeasurementId}|${_selectedGeomIds.join(",")}|${state.tableSortDir}|${state.colorSlipsBy}|${_modeKey}|${status}|${_filterKey}`;
    if (!force && _panelKey === _lastPanelKey) return;
    _perfLastPanelKey = _panelKey;
    _lastPanelKey = _panelKey;
    _perfPanelRebuildCount++;
    const current = selectedRecord();
    const selectedGeomIds = new Set(_selectedGeomIds);
    const recordGeometry = sortedRecordGeometryRows(records.map((record) => ({ record, geom: findGeometryForRecord(record) })));
    const filterMode = String(state.colorSlipsBy || "none");
    const filterHiddenArr = Array.isArray(state.filterHiddenValues?.[filterMode]) ? state.filterHiddenValues[filterMode] : [];
    const filterHiddenSet = new Set(filterHiddenArr);
    const filterOptions = (() => {
      if (filterMode === "confidence") {
        return [
          { value: "High", label: "High", color: "#22c55e" },
          { value: "Medium", label: "Medium", color: "#f59e0b" },
          { value: "Low", label: "Low", color: "#ef4444" },
        ];
      }
      if (filterMode === "space_type") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const t = String(r?.space_type || "").trim() || "unknown";
          if (!seen.has(t)) { seen.add(t); opts.push({ value: t, label: t, color: spaceTypeColorMap.get(t) ?? "#38bdf8" }); }
        });
        return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
      }
      if (filterMode === "dimension") {
        const tol = Number(state.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const l = Number(r?.measured_linear_feet);
          const w = Number(r?.measured_width_feet);
          if (!Number.isFinite(l) || !Number.isFinite(w)) return;
          const key = dimensionKey(l, w, tol);
          if (key && !seen.has(key)) { seen.add(key); opts.push({ value: key, label: `${key} ft`, color: paletteColorForKey(key) }); }
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      return [];
    })();
    const filterHiddenCount = filterOptions.filter((o) => filterHiddenSet.has(o.value)).length;
    const filterLabel = filterHiddenCount > 0 ? ` (${filterOptions.length - filterHiddenCount}/${filterOptions.length})` : "";
    const visibleRecordGeometry = filterHiddenCount > 0
      ? recordGeometry.filter(({ record }) => isRecordVisible(record))
      : recordGeometry;
    const slipSortArrow = String(state.tableSortDir || "asc") === "desc" ? "▼" : "▲";
    const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);
    const cfg = readColCfg();
    const cols = visibleCols(cfg);

    const headerCells = cols.map((col) => {
      const w = getColWidth(cfg, col.key);
      const isSort = col.key === "slip_id";
      return `<th data-col-key="${esc(col.key)}" draggable="true"
          style="position:relative;width:${w}px;min-width:40px;text-align:${col.key === "marina_comments" ? "left" : "center"};padding:2px 18px 2px 5px;white-space:nowrap;cursor:grab;user-select:none;background:rgba(15,23,42,.95);">
        ${isSort
    ? `<button id="slip-sort-slipid" type="button" class="btn" style="padding:1px 5px;font-size:11px;">${esc(col.label)} ${slipSortArrow}</button>`
    : esc(col.label)}
        <div class="slip-col-resize" data-resize-key="${esc(col.key)}"
          style="position:absolute;right:0;top:0;width:6px;height:100%;cursor:col-resize;background:transparent;z-index:2;"></div>
      </th>`;
    }).join("");

    const rows = visibleRecordGeometry.map(({ record, geom }) => {
      const geomId = String(geom?.id || "");
      const rowActive = record.measurement_id === state.selectedMeasurementId || (geomId && selectedGeomIds.has(geomId));
      const cells = cols.map((col) => {
        const w = getColWidth(cfg, col.key);
        if (col.key === "slip_id") {
          return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:center;">
            <input data-slip-edit-id="${esc(record.measurement_id)}" data-slip-edit-field="slip_id" type="text" value="${esc(record.slip_id || "")}"
              style="width:${Math.max(50, w - 10)}px;background:transparent;border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 3px;font-size:11px;font-family:inherit;" />
          </td>`;
        }
        const isBin = BINARY_COLS.has(col.key);
        if (!NON_EDITABLE_COLS.has(col.key)) {
          return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:${isBin || col.key !== "marina_comments" ? "center" : "left"};">${renderCellContent(col, record, displayToleranceFt)}</td>`;
        }
        const isLong = col.key === "geometry_wkt" || col.key === "record_created_timestamp";
        return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:${col.key === "marina_comments" ? "left" : "center"};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"${isLong ? ` title="${esc(String(record[col.key] || ""))}"` : ""}>${esc(formatCellValue(col.key, record[col.key], displayToleranceFt))}</td>`;
      }).join("");
      return `<tr data-slip-row="${esc(record.measurement_id)}" data-geom-id="${esc(geomId)}"
        style="cursor:pointer;background:${rowActive ? "rgba(245,158,11,0.12)" : "transparent"};">${cells}</tr>`;
    }).join("");

    const html = `
      <style id="slip-inv-styles">
        #slip-inventory-bottom { display:flex; flex-direction:column; padding:8px; box-sizing:border-box; overflow:hidden; }
        #slip-inventory-bottom .card { flex:1; min-height:0; display:flex; flex-direction:column; padding:8px; position:relative; }
        #slip-inv-controls { flex-shrink:0; }
        #slip-bottom-table-scroll { flex:1 1 0; min-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px; position:relative; }
        #slip-bottom-table-scroll table { border-collapse:collapse; font-size:11px; table-layout:fixed; }
        #slip-bottom-table-scroll th { border:1px solid rgba(148,163,184,.12); border-bottom:1px solid rgba(148,163,184,.22); }
        #slip-bottom-table-scroll td { border:1px solid rgba(148,163,184,.08); }
        #slip-bottom-table-scroll tr:hover td { background:rgba(148,163,184,.04); }
        .slip-cell-input { width:calc(100% - 4px); background:transparent; border:none; outline:none; color:#e2e8f0; padding:1px 3px; font-size:11px; font-family:inherit; }
        .slip-cell-input:focus { background:rgba(96,165,250,.08); border-radius:2px; }
        .slip-cell-select { width:100%; background:rgba(2,6,23,.5); border:none; outline:none; color:#e2e8f0; padding:1px 2px; font-size:11px; font-family:inherit; }
        .slip-cell-select:focus { background:rgba(96,165,250,.1); }
        #slip-bottom-table-scroll.select-mode { cursor:pointer; }
        #slip-bottom-table-scroll.select-mode tr:hover td { background:rgba(96,165,250,.08); }
        #slip-bottom-table-scroll.select-mode input,
        #slip-bottom-table-scroll.select-mode select { cursor:pointer; }
      </style>
      <div id="slip-inventory-bottom">
        <div class="card">
          <div id="slip-inv-controls" class="row" style="justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;">
            <div>
              <div class="lm-title">Slip Inventory</div>
              <div class="small">${esc(records.length ? `${records.length} record${records.length === 1 ? "" : "s"}` : "No records loaded.")}${filterMarinaId ? esc(` — filtered to ${filterMarinaId}`) : " — all marinas"}</div>
            </div>
            <div class="row" style="gap:8px;align-items:center;flex-wrap:wrap;">
              <label class="small" style="display:flex;align-items:center;gap:4px;white-space:nowrap;">Color By
                <select id="slip-color-by-table" style="min-width:110px;margin-left:4px;">
                  <option value="none"${filterMode === "none" ? " selected" : ""}>None</option>
                  <option value="dimension"${filterMode === "dimension" ? " selected" : ""}>Dimension</option>
                  <option value="confidence"${filterMode === "confidence" ? " selected" : ""}>Confidence</option>
                  <option value="space_type"${filterMode === "space_type" ? " selected" : ""}>Space Type</option>
                </select>
              </label>
              ${filterOptions.length > 0 ? `<div style="position:relative;">
                <button id="slip-filter-toggle" type="button" class="btn" style="font-size:11px;padding:3px 10px;">Filter${filterLabel} ▾</button>
                <div id="slip-filter-panel" style="display:none;position:absolute;top:calc(100% + 3px);right:0;z-index:200;background:rgba(15,23,42,.97);border:1px solid rgba(148,163,184,.22);border-radius:6px;padding:8px;min-width:170px;max-height:260px;overflow-y:auto;box-shadow:0 4px 16px rgba(0,0,0,.45);">
                  <div style="display:flex;gap:6px;margin-bottom:6px;">
                    <button id="slip-filter-select-all" type="button" class="btn" style="font-size:10px;padding:2px 8px;">Select All</button>
                    <button id="slip-filter-deselect-all" type="button" class="btn" style="font-size:10px;padding:2px 8px;">Deselect All</button>
                  </div>
                  ${filterOptions.map((opt) => `<label style="display:flex;align-items:center;gap:6px;padding:3px 0;font-size:11px;cursor:pointer;white-space:nowrap;"><input type="checkbox" data-slip-filter-val="${esc(opt.value)}"${filterHiddenSet.has(opt.value) ? "" : " checked"} /><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(opt.color)};flex-shrink:0;"></span>${esc(opt.label)}</label>`).join("")}
                </div>
              </div>` : ""}
              ${filterHiddenCount > 0 ? `<span class="small" style="color:rgba(251,191,36,.8);">${visibleRecordGeometry.length}/${recordGeometry.length}</span>` : ""}
              ${filterMarinaId ? `<label class="small" style="display:flex;align-items:center;gap:4px;"><input type="checkbox" id="slip-show-no-marina" ${showNoMarinaCode ? "checked" : ""} /> Include unassigned</label>` : ""}
              <label class="small">L/W Tol
                <select id="slip-display-measurement-tol" style="min-width:70px;">
                  <option value="0.1"${displayToleranceFt === 0.1 ? " selected" : ""}>0.1 ft</option>
                  <option value="0.25"${displayToleranceFt === 0.25 ? " selected" : ""}>0.25 ft</option>
                  <option value="0.5"${displayToleranceFt === 0.5 ? " selected" : ""}>0.5 ft</option>
                  <option value="1"${displayToleranceFt === 1 ? " selected" : ""}>1.0 ft</option>
                </select>
              </label>
              <button id="slip-bottom-refresh" class="btn">Refresh</button>
              <button id="slip-bottom-show-selected" class="btn"${current ? "" : " disabled"}>Show Selected</button>
              <button id="slip-col-cfg-open" class="btn" title="Configure columns" style="padding:4px 8px;">⚙ Columns</button> ${renderSlipExportMenu({ disabled: !records.length })}
            </div>
          </div>
          <div id="slip-bottom-table-scroll">
            <table>
              <thead style="position:sticky;top:0;z-index:1;">
                <tr>${headerCells}</tr>
              </thead>
              <tbody>
                ${rows || `<tr><td colspan="${cols.length}" class="small" style="padding:8px;">No slip inventory rows found.</td></tr>`}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    if (!tablePanelEl) return;
    tablePanelEl.innerHTML = html;
    bindBottomPanelControls();
  }

  function synchronizeCommittedGeometryFromRecords() {
    const existing = Array.isArray(state.slipGeometryPolygons) ? state.slipGeometryPolygons : [];
    const uncommitted = existing.filter((poly) => !poly?.committed);
    const existingCommittedByMeasurementId = new Map(
      existing
        .filter((poly) => poly?.committed && poly?.linkedMeasurementId)
        .map((poly) => [String(poly.linkedMeasurementId), poly])
    );
    const rebuiltCommitted = [];
    for (const record of records) {
      const measurementId = String(record?.measurement_id || "");
      if (!measurementId) continue;
      const ring = parsePolygonWkt(record?.geometry_wkt);
      if (!Array.isArray(ring) || ring.length < 3) continue;
      const inferred = inferPolyMetricsFromRing(ring);
      if (!inferred) continue;
      const prev = existingCommittedByMeasurementId.get(measurementId) || null;
      const slipId = String(record?.slip_id || "");
      rebuiltCommitted.push({
        id: String(prev?.id || makeId("GEOM")),
        typeId: String(prev?.typeId || state.slipGeometryDraftTypeId || ""),
        center: { lat: Number(inferred.center.lat), lng: Number(inferred.center.lng) },
        lengthFt: Number(inferred.lengthFt || record?.measured_linear_feet || 0),
        widthFt: Number(inferred.widthFt || record?.measured_width_feet || 0),
        headingDeg: Number(inferred.headingDeg || 0),
        chamferEnabled: true,
        chamferSide: Number(inferred.chamferSide || 1) < 0 ? -1 : 1,
        chamferAngleDeg: Number(inferred.chamferAngleDeg || 0),
        chamferMagnitudeFt: Number(inferred.chamferMagnitudeFt || 0),
        ringLatLngs: ring.map((pair) => [Number(pair[0]), Number(pair[1])]),
        freeform: !!(prev?.freeform) || ring.length >= 6,
        locked: true,
        committed: true,
        linkedMeasurementId: measurementId,
        linkedSlipId: slipId,
      });
    }
    state.slipGeometryPolygons = [...uncommitted, ...rebuiltCommitted];
    const selectedIds = getSelectedGeometryIds().filter((id) => state.slipGeometryPolygons.some((poly) => String(poly?.id || "") === String(id)));
    if (selectedIds.length !== getSelectedGeometryIds().length) {
      setSelectedGeometryIds(selectedIds, selectedIds[0] || "");
    }
  }

  async function refreshRecords({ quiet = false } = {}) {
    if (!quiet) taskStatus(filterMarinaId ? `Fetching ${filterMarinaId} records…` : "Fetching records…", 15);
    try {
      const payload = await requestJson(`${API_BASE}?limit=5000`);
      let allItems = Array.isArray(payload?.items) ? payload.items : [];
      if (filterMarinaId) {
        allItems = allItems.filter((item) => {
          const mId = String(item.marina_id || "").trim();
          if (!mId) return showNoMarinaCode;
          return mId === filterMarinaId;
        });
      }
      if (!quiet) taskStatus("Building table…", 60);
      records = allItems.map((item) => normalizeRecord(item)); recordsVersion++;
      synchronizeCommittedGeometryFromRecords();
      syncSelection();
      renderMap();
      if (!quiet) taskStatus("Rendering…", 85);
      renderBottomPanel();
      if (!quiet) status = `Loaded ${records.length} slip inventory record${records.length === 1 ? "" : "s"}.`;
      render();
      if (!quiet) taskIdle();
    } catch (error) {
      if (!quiet) taskIdle();
      status = error?.message || "Failed to load slip inventory records.";
      render();
      throw error;
    }
  }

  function slipTableHasFocusedEditor() {
    const active = document.activeElement;
    if (!active) return false;
    const host = document.getElementById("slip-inventory-bottom");
    if (!host || !host.contains(active)) return false;
    const tag = String(active.tagName || "").toLowerCase();
    return tag === "input" || tag === "select" || tag === "textarea" || active.isContentEditable === true;
  }

  function shouldSkipRemoteSync() {
    if (!initialFetchDone) return true;
    if (remoteSyncInFlight) return true;
    if (Date.now() - lastLocalSaveAt < LOCAL_SAVE_GRACE_MS) return true;
    if (slipTableHasFocusedEditor()) return true;
    if (bottomTablePointerInside) return true;
    if (Date.now() < bottomTableScrollActiveUntil) return true;
    return false;
  }

  async function syncRemoteRecords(reason = "interval") {
    if (shouldSkipRemoteSync()) return;
    remoteSyncInFlight = true;
    try {
      await refreshRecords({ quiet: true });
      app.log?.("slip.remote_sync", { reason, marina: filterMarinaId || state.marinaId || "" });
    } catch (error) {
      console.warn("[Slip Inventory] remote sync failed", error);
    } finally {
      remoteSyncInFlight = false;
    }
  }

  function startRemoteSync() {
    if (!remoteSyncTimer) {
      remoteSyncTimer = setInterval(() => {
        if (typeof requestIdleCallback !== "undefined") {
          requestIdleCallback(() => syncRemoteRecords("interval").catch(() => {}), { timeout: 4000 });
        } else {
          syncRemoteRecords("interval").catch(() => {});
        }
      }, REMOTE_SYNC_INTERVAL_MS);
    }
    if (!remoteSyncFocusHandler) {
      remoteSyncFocusHandler = () => {
        syncRemoteRecords("focus").catch(() => {});
      };
      window.addEventListener("focus", remoteSyncFocusHandler);
    }
    if (!remoteSyncVisibilityHandler) {
      remoteSyncVisibilityHandler = () => {
        if (document.visibilityState === "visible") {
          syncRemoteRecords("visible").catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", remoteSyncVisibilityHandler);
    }
  }

  function stopRemoteSync() {
    if (remoteSyncTimer) {
      clearInterval(remoteSyncTimer);
      remoteSyncTimer = null;
    }
    if (remoteSyncFocusHandler) {
      window.removeEventListener("focus", remoteSyncFocusHandler);
      remoteSyncFocusHandler = null;
    }
    if (remoteSyncVisibilityHandler) {
      document.removeEventListener("visibilitychange", remoteSyncVisibilityHandler);
      remoteSyncVisibilityHandler = null;
    }
    remoteSyncInFlight = false;
  }

  function stopPointDrop(reason = "stopped") {
    if (pointDropOff) {
      pointDropOff();
      pointDropOff = null;
    }
    pointDropActive = false;
    try { map.getContainer().style.cursor = ""; } catch {}
    if (reason === "cancelled") {
      status = "Point drop cancelled.";
      render();
    }
  }

  function stopLineLayout(reason = "stopped") {
    if (lineLayoutOff) {
      lineLayoutOff();
      lineLayoutOff = null;
    }
    lineLayoutActive = false;
    lineEndpoints = [];
    lineLayoutMode = "endpoint";
    lineHoverPoint = null;
    lineAnchorPoint = null;
    imageryEstimateDebug = null;
    try { map.getContainer().style.cursor = ""; } catch {}
    renderPreview();
    if (reason === "cancelled") {
      status = "Line layout cancelled.";
      render();
    }
  }

  async function createRecordFromPoint(latlng) {
    const measurementId = makeId("MEAS");
    const slipId = buildSlipIdFromState(state) || `SLIP-${String(selectedIndex() + 1).padStart(4, "0")}`;
    const payload = {
      measurement_id: measurementId,
      marina_id: state.marinaId,
      marina_name: state.marinaName,
      slip_id: slipId,
      dock_name: state.dockName || null,
      space_type: state.spaceType || null,
      multislip_yn: draftMultiSlip ? "yes" : "no",
      multislip_id: draftMultiSlipId || null,
      covered_yn: draftCovered ? "yes" : "no",
      boatlift_yn: draftBoatlift ? "yes" : "no",
      fueldock_yn: draftFuelDock ? "yes" : "no",
      marina_comments: draftComments || null,
      measured_linear_feet: null,
      measured_width_feet: null,
      measured_depth_feet: null,
      operational_status: "operable",
      notes: "",
      measurement_date: state.measurementDate,
      confidence_score: state.confidenceScore,
      record_created_timestamp: new Date().toISOString(),
      lat: Number(latlng.lat),
      lng: Number(latlng.lng),
    };
    const response = await requestJson(`${API_BASE}/from-point`, {
      method: "POST",
      body: payload,
    });
    state.selectedMeasurementId = response?.item?.measurement_id || measurementId;
    incrementSlipIdNumber(state);
    persist();
    await refreshRecords({ quiet: true });
    app.log("slip.created", { slip_id: slipId, tool: "point_drop", marina: state.marinaId });
    status = `Created ${slipId} from map point.`;
    render();
  }

  async function createRecordsFromPoints(points = []) {
    const segments = linePreviewSegments();
    const ids = previewSlipIds(state, segments.length || points.length);
    for (let index = 0; index < Math.max(segments.length, points.length); index += 1) {
      const segment = segments[index] || null;
      const point = points[index];
      const measurementId = makeId("MEAS");
      const slipId = ids[index] || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`;
      const linearFeet = segment
        ? roundToTenthFoot(toFeetDistance(L.latLng(segment.start.lat, segment.start.lng), L.latLng(segment.end.lat, segment.end.lng)))
        : null;
      const geometryWkt = segment
        ? buildLineWkt([[segment.start.lat, segment.start.lng], [segment.end.lat, segment.end.lng]])
        : buildPointWkt(Number(point?.lng), Number(point?.lat));
      await requestJson(`${API_BASE}`, {
        method: "POST",
        body: {
          measurement_id: measurementId,
          marina_id: state.marinaId,
          marina_name: state.marinaName,
          slip_id: slipId,
          dock_name: state.dockName || null,
          space_type: state.spaceType || null,
              multislip_yn: draftMultiSlip ? "yes" : "no",
          multislip_id: draftMultiSlipId || null,
          covered_yn: draftCovered ? "yes" : "no",
          boatlift_yn: draftBoatlift ? "yes" : "no",
          fueldock_yn: draftFuelDock ? "yes" : "no",
          marina_comments: draftComments || null,
          measured_linear_feet: Number.isFinite(Number(linearFeet)) ? roundToTenthFoot(linearFeet) : null,
          measured_width_feet: null,
          measured_depth_feet: null,
          operational_status: "operable",
          notes: "",
          measurement_date: state.measurementDate,
          confidence_score: state.confidenceScore,
          record_created_timestamp: new Date().toISOString(),
          geometry_wkt: geometryWkt,
        },
      });
      incrementSlipIdNumber(state);
    }
    persist();
    await refreshRecords({ quiet: true });
    app.log("slip.created", { count: Math.max(segments.length, points.length), tool: "line_layout", marina: state.marinaId });
    status = `Created ${Math.max(segments.length, points.length)} slips from line preview.`;
    render();
  }

  function startPointDrop() {
    if (pointDropActive) return;
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopLineLayout("replaced");
    pointDropActive = true;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;

    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = {
        x: Number(event.clientX),
        y: Number(event.clientY),
      };
      pointerMoved = false;
    };

    const onPointerMove = (event) => {
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) {
        pointerMoved = true;
      }
    };

    const onPointerUp = async (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCreate = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCreate) return;
      try {
        await createRecordFromPoint(latlng);
        app.ui.toast("Slip inventory point created. Click again to continue.");
      } catch (error) {
        status = error?.message || "Failed to create slip inventory record.";
        app.ui.toast(status);
        render();
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        document.removeEventListener("keydown", onKey);
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        stopPointDrop("cancelled");
      }
    };
    pointDropOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    status = "Click to create slips. Click and drag pans the map. Press Esc to cancel.";
    render();
  }

  function stopGeometryPlaceMode() {
    activeGeometryPlaceMode = false;
    geometryPlaceHoverPoint = null;
    geometryPlaceDraft = null;
    if (geometryPlaceOff) {
      try { geometryPlaceOff(); } catch {}
      geometryPlaceOff = null;
    }
    geometryPlacePreviewLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function stopFreeDrawMode(reason = "") {
    if (!freeDrawActive && !freeDrawOff) return;
    freeDrawActive = false;
    freeDrawPoints = [];
    freeDrawHoverPoint = null;
    if (freeDrawOff) { try { freeDrawOff(); } catch {} freeDrawOff = null; }
    geometryFreeDrawLayer.clearLayers();
    geometryFreeDrawDynamicLayer.clearLayers();
    try { map.getContainer().classList.remove("slip-free-draw-cursor"); } catch {}
    if (reason === "cancelled") { status = "Irregular polygon drawing cancelled."; render(); }
  }

  // ── Place Multislip ──────────────────────────────────────────────────────────

  function stopMultislipMode() {
    if (!multislipActive && !multislipOff) return;
    multislipActive = false;
    multislipMode = "length";
    multislipP1 = null; multislipP2 = null; multislipWidthFt = 0; multislipPartitions = 1;
    multislipSlotLengths = []; multislipDragging = false;
    multislipHoverPoint = null;
    multislipRotating = false; multislipP2Base = null; multislipP3 = null;
    if (multislipOff) { try { multislipOff(); } catch {} multislipOff = null; }
    multislipPreviewLayer.clearLayers();
    multislipHandleLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function renderMultislipPreview() {
    multislipPreviewLayer.clearLayers();
    if (!multislipActive) return;
    const p1 = multislipP1;
    const hover = multislipHoverPoint;
    const green = "rgba(16,185,129,0.95)";
    const greenFill = "#10b981";

    // Phase 1: awaiting P1 — show nothing (cursor is crosshair)
    if (!p1) return;

    // Phase 2: P1 set, tracking P2
    if (!multislipP2) {
      if (!hover) return;
      L.circleMarker([p1.lat, p1.lng], { radius: 5, color: green, fillColor: greenFill, fillOpacity: 1, weight: 2, interactive: false }).addTo(multislipPreviewLayer);
      L.polyline([[p1.lat, p1.lng], [hover.lat, hover.lng]], { color: green, weight: 2, dashArray: "6 4", interactive: false }).addTo(multislipPreviewLayer);
      return;
    }

    const p2 = multislipP2;

    // Phase 3: P2 set, tracking extrusion
    if (multislipWidthFt === 0) {
      L.polyline([[p1.lat, p1.lng], [p2.lat, p2.lng]], { color: green, weight: 3, interactive: false }).addTo(multislipPreviewLayer);
      if (!hover) return;
      const rawFt = msPerpDistFt(p1, p2, hover);
      const snapFt = Math.round(rawFt / 0.1) * 0.1;
      if (snapFt === 0) return;
      const corners = msRectCorners(p1, p2, snapFt);
      L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.15, dashArray: "6 4", interactive: false }).addTo(multislipPreviewLayer);
      const { ux, uy } = msDockvec(p1, p2);
      const midAlong = { lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2 };
      const wM = snapFt / 3.280839895;
      const labelPt = offsetPoint(midAlong, -uy * wM, ux * wM);
      const labelText = multislipMode === "width" ? `L: ${Math.abs(snapFt)} ft` : `W: ${Math.abs(snapFt)} ft`;
      L.marker([labelPt.lat, labelPt.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%)">${labelText}</div>` }),
      }).addTo(multislipPreviewLayer);
      return;
    }

    // Phase 4: Width/length set — per-slot rectangles with drag handles
    // Both modes: P1→P2 is split into N slices; perpendicular extrusion = slotLen.
    // Length-first: P1→P2 = dock run (L), slotLen = offshore (W).
    // Width-first:  P1→P2 = offshore (W), slotLen = dock-along (L).
    const N = multislipPartitions;
    const wFt = multislipWidthFt;
    const { lenM: runLenM } = msDockvec(p1, p2);
    const slipAlongFt = (runLenM * 3.280839895) / N;

    const ids = previewSlipIds(state, N);
    for (let i = 0; i < N; i++) {
      const slotLen = multislipSlotLengths[i] != null ? multislipSlotLengths[i] : wFt;
      const corners = msSlotCornersPerLen(p1, p2, slotLen, i, N);
      L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.12, interactive: false }).addTo(multislipPreviewLayer);
      const cen = msSlotCentroid(p1, p2, slotLen, i, N);
      L.marker([cen.lat, cen.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 11px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(ids[i])}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${slipAlongFt.toFixed(1)}×${Math.abs(slotLen).toFixed(1)} ft</span></div>` }),
      }).addTo(multislipPreviewLayer);
    }

    // Drag handles on the perpendicular edge of each slot (skip rebuild while dragging)
    if (!multislipDragging) {
      multislipHandleLayer.clearLayers();
      for (let i = 0; i < N; i++) {
        const slotLen = multislipSlotLengths[i] != null ? multislipSlotLengths[i] : wFt;
        const offPt = msSlotOffshorePoint(p1, p2, slotLen, i, N);
        const iCopy = i;
        const handle = L.marker([offPt.lat, offPt.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div style="width:12px;height:12px;background:#38bdf8;border:2px solid #fff;border-radius:50%;cursor:grab;margin-left:-6px;margin-top:-6px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
            iconSize: [0, 0], iconAnchor: [0, 0],
          }),
          draggable: true,
          zIndexOffset: 1000,
        });
        handle.on("dragstart", () => { multislipDragging = true; });
        handle.on("drag", (e) => {
          const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
          const rawFt = msPerpDistFt(p1, p2, newPos);
          const sign = wFt < 0 ? -1 : 1;
          const snapFt = (Math.round(Math.abs(rawFt) / 0.1) * 0.1) || 0.1;
          multislipSlotLengths[iCopy] = sign * snapFt;
          renderMultislipPreview();
        });
        handle.on("dragend", () => { multislipDragging = false; renderMultislipPreview(); });
        multislipHandleLayer.addLayer(handle);
      }
    }
  }

  async function commitMultislip() {
    if (!multislipP1 || !multislipP2 || multislipWidthFt === 0) return;
    const p1 = multislipP1, p2 = multislipP2;
    const N = multislipPartitions;
    const wFt = multislipWidthFt;
    const mode = multislipMode;
    const slotLengths = multislipSlotLengths.slice(); // capture before stopMultislipMode clears them
    // Both modes: P1→P2 is split into N slices; slotLen = perpendicular extrusion.
    // Width-first: P1→P2 = offshore (W axis); slotLen = dock-along (L). L/W fields swapped.
    // Length-first: P1→P2 = dock run (L axis); slotLen = offshore (W). Normal field order.
    const slipAlongFt = roundToTenthFoot((msDockvec(p1, p2).lenM * 3.280839895) / N);
    const ids = previewSlipIds(state, N);
    // multislip_yn and multislip_id come entirely from the panel, same as all other slip tools.
    // When N>1 and the panel checkbox is checked, auto-generate one shared group ID so all
    // N slips are linked — but only if the panel ID field is empty.
    const sharedGroupId = (N > 1 && draftMultiSlip) ? (draftMultiSlipId || makeId("MS")) : null;
    stopMultislipMode();
    status = `Committing ${N} slip${N > 1 ? "s" : ""}...`;
    render();
    try {
      for (let i = 0; i < N; i++) {
        const slotWFt = slotLengths[i] != null ? slotLengths[i] : wFt;
        const corners = msSlotCornersPerLen(p1, p2, slotWFt, i, N);
        const ring = corners.map(c => [c.lat, c.lng]);
        const wkt = buildPolygonWkt(ring);
        await requestJson(API_BASE, {
          method: "POST",
          body: {
            measurement_id: makeId("MEAS"),
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            slip_id: ids[i],
            dock_name: state.dockName || null,
            space_type: state.spaceType || null,
            multislip_yn: draftMultiSlip ? "yes" : "no",
            multislip_id: sharedGroupId ?? (draftMultiSlipId || null),
            covered_yn: draftCovered ? "yes" : "no",
            boatlift_yn: draftBoatlift ? "yes" : "no",
            fueldock_yn: draftFuelDock ? "yes" : "no",
            marina_comments: draftComments || null,
            measured_linear_feet: mode === "width" ? Math.abs(slotWFt) : slipAlongFt,
            measured_width_feet:  mode === "width" ? slipAlongFt : Math.abs(slotWFt),
            measured_depth_feet: null,
            operational_status: "operable",
            notes: "",
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            record_created_timestamp: new Date().toISOString(),
            geometry_wkt: wkt,
          },
        });
        incrementSlipIdNumber(state);
      }
      await refreshRecords({ quiet: true });
      const uniqueW = [...new Set(slotLengths.map((l) => roundToTenthFoot(Math.abs(l != null ? l : wFt))))];
      const wDesc = uniqueW.length === 1 ? `${uniqueW[0]} ft W` : "varied W";
      app.log("slip.created", { count: N, tool: "multislip", mode, marina: state.marinaId });
      status = `Committed ${N} side-tie slip${N > 1 ? "s" : ""} — ${slipAlongFt.toFixed(1)} ft L × ${wDesc}.`;
    } catch (err) {
      status = `Multislip commit failed: ${err?.message || "Unknown error"}`;
    }
    render();
  }

  function startMultislipMode(widthFirst = false) {
    stopPointDrop("replaced");
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopFreeDrawMode();
    multislipActive = true;
    multislipMode = widthFirst ? "width" : "length";
    multislipP1 = null; multislipP2 = null; multislipWidthFt = 0; multislipPartitions = 1;
    multislipSlotLengths = []; multislipDragging = false;
    multislipHoverPoint = null;
    multislipRotating = false; multislipP2Base = null; multislipP3 = null;
    status = widthFirst
      ? "Multislip (width-first): click one end of a single slip width along the dock."
      : "Multislip: click the start of the dock run.";
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) {
        multislipHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
        if (multislipRotating && multislipP1 && multislipP2Base && multislipP3) {
          multislipP2 = msRotateAroundPivot(multislipP2Base, multislipP1, multislipP3, multislipHoverPoint);
        }
      }
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderMultislipPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!multislipP1) {
        multislipP1 = pt;
        status = multislipMode === "width"
          ? "Multislip (width-first): click the other end of one slip width."
          : "Multislip: click the end of the dock run.";
      } else if (!multislipP2) {
        multislipP2 = pt;
        status = multislipMode === "width"
          ? "Multislip (width-first): move offshore and click to set slip length (snaps 1 ft). W/X to add/remove slips."
          : "Multislip: move perpendicular and click to set width (snaps 1 ft). W/X to add/remove slips after.";
      } else if (multislipWidthFt === 0) {
        const rawFt = msPerpDistFt(multislipP1, multislipP2, pt);
        const snapFt = Math.round(rawFt / 0.1) * 0.1;
        multislipWidthFt = snapFt !== 0 ? snapFt : 0.1;
        multislipSlotLengths = Array(multislipPartitions).fill(multislipWidthFt);
        multislipP3 = pt;
        multislipP2Base = { lat: multislipP2.lat, lng: multislipP2.lng };
        multislipRotating = true;
        status = "Multislip: move cursor to rotate around P1, click to lock. Esc=cancel.";
      } else if (multislipRotating) {
        multislipRotating = false;
        status = `Multislip: ${multislipPartitions} slip${multislipPartitions > 1 ? "s" : ""}. W=add, X=remove, Enter=commit, Esc=cancel.`;
      }
      renderMultislipPreview();
      render();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { stopMultislipMode(); status = "Multislip placement cancelled."; render(); return; }
      if (multislipWidthFt !== 0 && !multislipRotating) {
        if (e.key === "w" || e.key === "W") {
          const prev = multislipPartitions;
          multislipPartitions = Math.min(50, multislipPartitions + 1);
          if (multislipPartitions > prev) {
            const lastLen = multislipSlotLengths[multislipSlotLengths.length - 1] ?? multislipWidthFt;
            multislipSlotLengths.push(lastLen);
          }
          status = `Multislip: ${multislipPartitions} slip${multislipPartitions > 1 ? "s" : ""}. W=add, X=remove, Enter=commit, Esc=cancel.`;
          renderMultislipPreview(); render();
        } else if (e.key === "x" || e.key === "X") {
          const prev = multislipPartitions;
          multislipPartitions = Math.max(1, multislipPartitions - 1);
          if (multislipPartitions < prev) multislipSlotLengths.pop();
          status = `Multislip: ${multislipPartitions} slip${multislipPartitions > 1 ? "s" : ""}. W=add, X=remove, Enter=commit, Esc=cancel.`;
          renderMultislipPreview(); render();
        } else if (e.key === "Enter") { e.preventDefault(); commitMultislip().catch(console.error); }
      }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    multislipOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor"); } catch {}
    };
    render();
  }

  // ─────────────────────────────────────────────────────────────────────────────

  function startFreeDrawMode() {
    stopPointDrop("replaced");
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopFreeDrawMode();
    stopMultislipMode();
    freeDrawActive = true;
    freeDrawPoints = [];
    freeDrawHoverPoint = null;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    let lastLeftClickTime = 0;
    const onContextMenu = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (freeDrawPoints.length >= 3) commitFreeDrawPolygon().catch(() => {});
    };
    const onPointerDown = (event) => {
      if (event.button === 2) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    const onPointerMove = (event) => {
      const latlng = map?.mouseEventToLatLng?.(event);
      if (latlng) { freeDrawHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) }; renderFreeDrawDynamic(); }
      if (!pointerDown) return;
      if (Math.abs(Number(event.clientX) - pointerDown.x) > 6 || Math.abs(Number(event.clientY) - pointerDown.y) > 6) pointerMoved = true;
    };
    const onPointerUp = (event) => {
      if (event.button === 2) { event.preventDefault(); event.stopPropagation(); return; }
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldPlace = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldPlace) return;
      if (event) event.__htHandled = "slip-inventory";
      const now = Date.now();
      const isDoubleClick = (now - lastLeftClickTime) < 350;
      lastLeftClickTime = now;
      if (isDoubleClick) {
        if (freeDrawPoints.length >= 3) commitFreeDrawPolygon().catch(() => {});
        return;
      }
      const newPt = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
      if (freeDrawPoints.length >= 3) {
        const firstPx = map.latLngToContainerPoint(L.latLng(freeDrawPoints[0].lat, freeDrawPoints[0].lng));
        const clickPx = map.latLngToContainerPoint(latlng);
        if (Math.hypot(firstPx.x - clickPx.x, firstPx.y - clickPx.y) <= 16) {
          commitFreeDrawPolygon().catch(() => {});
          return;
        }
      }
      freeDrawPoints = [...freeDrawPoints, newPt];
      suppressMapClicks(200);
      renderFreeDrawPreview();
    };
    const onKey = (event) => {
      if (event.key === "Enter" && freeDrawPoints.length >= 3) {
        commitFreeDrawPolygon().catch(() => {});
      } else if (event.key === "Escape") {
        stopFreeDrawMode("cancelled");
      } else if ((event.key === "Backspace" || event.key === "Delete") && freeDrawPoints.length > 0) {
        freeDrawPoints = freeDrawPoints.slice(0, -1);
        renderFreeDrawPreview();
      }
    };
    mapContainer?.addEventListener?.("contextmenu", onContextMenu, true);
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    freeDrawOff = () => {
      mapContainer?.removeEventListener?.("contextmenu", onContextMenu, true);
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    status = `Click to add vertices. Right-click or double-click to close. Backspace removes last. Esc cancels.`;
    render();
  }

  async function commitFreeDrawPolygon() {
    if (freeDrawPoints.length < 3) return;
    const ring = freeDrawPoints.map((p) => [p.lat, p.lng]);
    const inferred = inferPolyMetricsFromRing(ring);
    const center = inferred?.center || {
      lat: ring.reduce((s, p) => s + p[0], 0) / ring.length,
      lng: ring.reduce((s, p) => s + p[1], 0) / ring.length,
    };
    const poly = {
      id: makeGeometryId(),
      slipTypeId: state.slipGeometryDraftTypeId || "",
      freeform: true,
      lengthFt: Number(roundToTenthFoot(inferred?.lengthFt) ?? 0),
      widthFt: Number(roundToTenthFoot(inferred?.widthFt) ?? 0),
      headingDeg: Number(inferred?.headingDeg ?? 0),
      locked: false,
      center,
      chamferEnabled: false,
      chamferSide: 1,
      chamferAngleDeg: 0,
      chamferMagnitudeFt: 0,
      ringLatLngs: ring,
      committed: false,
    };
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, poly];
    setSelectedGeometryIds([poly.id], poly.id);
    persist();
    stopFreeDrawMode("silent");
    const slipId = buildSlipIdFromState(state) || `SLIP-${String(state.slipGeometryPolygons.length).padStart(4, "0")}`;
    const polygonWkt = buildPolygonWkt(ring);
    const measuredLengthFt = roundToTenthFoot(inferred?.lengthFt ?? null);
    const measuredWidthFt = roundToTenthFoot(inferred?.widthFt ?? null);
    const measurementId = makeId("MEAS");
    try {
      await requestJson(API_BASE, {
        method: "POST",
        body: {
          measurement_id: measurementId,
          marina_id: state.marinaId,
          marina_name: state.marinaName,
          slip_id: slipId,
          dock_name: state.dockName || null,
          space_type: state.spaceType || null,
          multislip_yn: draftMultiSlip ? "yes" : "no",
          multislip_id: draftMultiSlipId || null,
          covered_yn: draftCovered ? "yes" : "no",
          boatlift_yn: draftBoatlift ? "yes" : "no",
          fueldock_yn: draftFuelDock ? "yes" : "no",
          marina_comments: draftComments || null,
          measured_linear_feet: measuredLengthFt,
          measured_width_feet: measuredWidthFt,
          measured_depth_feet: null,
          operational_status: "operable",
          notes: "",
          measurement_date: state.measurementDate,
          confidence_score: state.confidenceScore,
          record_created_timestamp: new Date().toISOString(),
          geometry_wkt: polygonWkt,
        },
      });
      state.slipGeometryPolygons = state.slipGeometryPolygons.map((p) =>
        p.id === poly.id ? { ...p, linkedMeasurementId: measurementId, committed: true } : p
      );
      incrementSlipIdNumber(state);
      persist();
      await refreshRecords({ quiet: true });
      app.log("slip.created", { slip_id: slipId, tool: "free_draw", marina: state.marinaId });
      status = `Drew irregular slip ${slipId} (${(measuredLengthFt ?? 0).toFixed(1)} × ${(measuredWidthFt ?? 0).toFixed(1)} ft). Select and drag vertices to adjust.`;
    } catch (err) {
      status = `Error committing polygon: ${err?.message || "Unknown error"}`;
    }
    render();
  }

  function startGeometryPlaceMode({ template = null } = {}) {
    if (!state.slipGeometryDraftTypeId) return;
    stopPointDrop("replaced");
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopFreeDrawMode();
    stopMultislipMode();
    activeGeometryPlaceMode = true;
    const selected = selectedGeometryPolygon();
    const type = getSlipTypeById(state.slipGeometryDraftTypeId);
    geometryPlaceDraft = template && typeof template === "object"
      ? {
        lengthFt: Number(template.lengthFt || 40),
        widthFt: Number(template.widthFt || 14),
        headingDeg: Number(template.headingDeg || 0),
        chamferEnabled: true,
        chamferSide: Number(template.chamferSide || 1) < 0 ? -1 : 1,
        chamferAngleDeg: Number(template.chamferAngleDeg || 0),
        chamferMagnitudeFt: Number(template.chamferMagnitudeFt || 0),
        ringLatLngs: Array.isArray(template.ringLatLngs) ? template.ringLatLngs : null,
        sourceCenter: template.sourceCenter || null,
      }
      : selected
      ? {
        lengthFt: Number(selected.lengthFt || 40),
        widthFt: Number(selected.widthFt || 14),
        headingDeg: Number(selected.headingDeg || 0),
        chamferEnabled: true,
        chamferSide: Number(selected.chamferSide || 1) < 0 ? -1 : 1,
        chamferAngleDeg: Number(selected.chamferAngleDeg || 0),
        chamferMagnitudeFt: Number(selected.chamferMagnitudeFt || 0),
      }
      : {
        lengthFt: Number(type?.lengthFt || 40),
        widthFt: Number(type?.widthFt || 14),
        headingDeg: 0,
        chamferEnabled: true,
        chamferSide: Number(type?.chamferSide || 1) < 0 ? -1 : 1,
        chamferAngleDeg: Number(type?.chamferAngleDeg || 0),
        chamferMagnitudeFt: Number(type?.chamferMagnitudeFt || 0),
      };
    try { map.getContainer().style.cursor = "crosshair"; } catch {}
    const mapContainer = map?.getContainer?.() || null;
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    let placePreviewRafPending = false;
    const onPointerMove = (event) => {
      const latlng = map?.mouseEventToLatLng?.(event);
      if (latlng) {
        geometryPlaceHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
        if (!placePreviewRafPending) {
          placePreviewRafPending = true;
          requestAnimationFrame(() => { placePreviewRafPending = false; renderGeometryPlacePreview(); });
        }
      }
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldPlace = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldPlace || !geometryPlaceDraft) return;
      if (event) event.__htHandled = "slip-inventory";
      const created = placeGeometryAt(latlng, state.slipGeometryDraftTypeId, geometryPlaceDraft);
      if (!created) return;
      suppressMapClicks(800);
      stopGeometryPlaceMode();
      status = `Placed polygon ${created.id}. Press S to start run layout from this slip.`;
      render();
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        stopGeometryPlaceMode();
        status = "Placement cancelled.";
        render();
      }
    };
    geometryPlaceOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    status = "Move mouse to position draft slip, shape with hotkeys, left-click to place, then press S to start run.";
    render();
  }

  function startLineLayout(mode = "endpoint") {
    if (lineLayoutActive) return;
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopPointDrop("replaced");
    stopFreeDrawMode();
    stopMultislipMode();
    lineLayoutActive = true;
    lineLayoutMode = mode === "spacing" ? "spacing" : "endpoint";
    lineEndpoints = [];
    lineHoverPoint = null;
    lineAnchorPoint = null;
    imageryEstimateStatus = "";
    imageryEstimateDebug = null;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    const onPointerMove = (event) => {
      if (lineLayoutMode === "spacing" && lineEndpoints.length >= 2 && lineEndpoints.length < 3) {
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) {
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          renderPreview();
        }
      }
      if (pointerDown) {
        const dx = Math.abs(Number(event.clientX) - pointerDown.x);
        const dy = Math.abs(Number(event.clientY) - pointerDown.y);
        if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
      }
    };
    const onWheel = (event) => {
      if (lineLayoutMode === "spacing") return;
      if (!event.ctrlKey || lineEndpoints.length < 2) return;
      if (!mapContainer?.contains?.(event.target)) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      event.stopImmediatePropagation?.();
      const direction = Number(event.deltaY) > 0 ? -1 : 1;
      state.lineSlipCount = Math.max(1, Number(state.lineSlipCount || 1) + direction);
      persist();
      renderPreview();
      render();
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCapture = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCapture) return;
      if (lineLayoutMode === "spacing") {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          status = "First slip center captured. Click the next slip center to set spacing.";
        } else if (lineEndpoints.length === 1) {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = "Spacing captured. Move along the pier to preview labels, then click the end of the run.";
        } else {
          lineEndpoints = [lineEndpoints[0], lineEndpoints[1], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = null;
          state.lineSlipCount = linePreviewPoints().length;
          persist();
          status = `Spacing run ready for ${state.lineSlipCount} slips. Press Enter or Generate Line.`;
        }
      } else {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineAnchorPoint = null;
          status = "Line start captured. Click the end point.";
        } else {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineAnchorPoint = null;
          status = `Line preview ready for ${state.lineSlipCount} slips. Adjust the count, optionally use Auto Estimate From Imagery, or generate.`;
        }
      }
      renderPreview();
      render();
    };
    const onKey = (event) => {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (event.key === "Enter" && canGenerate) {
        event.preventDefault?.();
        const points = linePreviewPoints();
        if (points.length >= 2) {
          createRecordsFromPoints(points).then(() => {
            stopLineLayout("completed");
            app.ui.toast(`Generated ${points.length} slips from line preview.`);
          }).catch((error) => {
            status = error?.message || "Failed to generate line slips.";
            app.ui.toast(status);
            render();
          });
        }
        return;
      }
      if (event.key === "Escape") {
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        window.removeEventListener("wheel", onWheel, { capture: true });
        stopLineLayout("cancelled");
      }
    };
    lineLayoutOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    document.addEventListener("keydown", onKey);
    status = lineLayoutMode === "spacing"
      ? "Click the first slip center, then the next slip center to set spacing."
      : "Click the first slip location, then click the last slip location. Use Ctrl+mouse wheel to adjust slip count after the line is set. Drag any preview slip to re-space around that slip.";
    renderPreview();
    render();
  }

  async function saveSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    const selectedMarinaId = String(containerEl?.querySelector("#slip-marina-id")?.value || "");
    const selectedMarinaName = String(containerEl?.querySelector("#slip-marina-name")?.value || "");
    const nextRecord = {
      ...current,
      marina_id: selectedMarinaId,
      marina_name: selectedMarinaName,
      slip_id: String(containerEl?.querySelector("#slip-slip-id")?.value || ""),
      measured_linear_feet: containerEl?.querySelector("#slip-linear-feet")?.value === "" ? null : roundToTenthFoot(containerEl?.querySelector("#slip-linear-feet")?.value),
      measured_width_feet: containerEl?.querySelector("#slip-width-feet")?.value === "" ? null : roundToTenthFoot(containerEl?.querySelector("#slip-width-feet")?.value),
      measured_depth_feet: containerEl?.querySelector("#slip-depth-feet")?.value === "" ? null : Number(containerEl?.querySelector("#slip-depth-feet")?.value),
      space_type: String(containerEl?.querySelector("#slip-space-type")?.value || ""),
      operational_status: String(containerEl?.querySelector("#slip-operational-status")?.value || "operable"),
      notes: String(containerEl?.querySelector("#slip-notes")?.value || ""),
      measurement_date: String(containerEl?.querySelector("#slip-measurement-date")?.value || ""),
      confidence_score: String(containerEl?.querySelector("#slip-confidence")?.value || "High"),
      geometry_wkt: String(containerEl?.querySelector("#slip-geometry-wkt")?.value || current.geometry_wkt || buildPointWkt(0, 0)),
      record_created_timestamp: String(containerEl?.querySelector("#slip-record-created-timestamp")?.value || current.record_created_timestamp || new Date().toISOString()),
    };
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "PATCH",
      body: nextRecord,
    });
    state.marinaId = nextRecord.marina_id;
    state.marinaName = nextRecord.marina_name;
    state.measurementDate = nextRecord.measurement_date;
    state.confidenceScore = nextRecord.confidence_score;
    persist();
    await refreshRecords({ quiet: true });
    status = `Saved ${nextRecord.slip_id}.`;
    render();
  }

  async function deleteSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    const measurementId = String(current.measurement_id || "");
    const deletedSlipId = String(current.slip_id || "");
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "DELETE",
    });
    // Keep geometry and labels coupled: remove committed polygons linked to deleted record.
    // Also clear stale slip-id links that may reference the deleted slip id.
    state.slipGeometryPolygons = state.slipGeometryPolygons
      .filter((poly) => String(poly?.linkedMeasurementId || "") !== measurementId)
      .map((poly) => (
        String(poly?.linkedSlipId || "") === deletedSlipId
          ? { ...poly, linkedSlipId: "" }
          : poly
      ));
    app.log("slip.deleted", { slip_id: current.slip_id, marina: state.marinaId });
    status = `Deleted ${current.slip_id || current.measurement_id}.`;
    state.selectedMeasurementId = "";
    persist();
    await refreshRecords({ quiet: true });
    render();
  }

  async function saveSlipIdFromTable(measurementId, slipIdValue) {
    const id = String(measurementId || "").trim();
    if (!id) return;
    const target = records.find((record) => String(record.measurement_id) === id);
    if (!target) return;
    const nextSlipId = String(slipIdValue || "").trim();
    if (String(target.slip_id || "") === nextSlipId) return;
    const nextRecord = {
      ...target,
      slip_id: nextSlipId,
    };
    const scrollEl = document.getElementById("slip-bottom-table-scroll");
    if (scrollEl) bottomTableScrollTop = Number(scrollEl.scrollTop || 0);
    lastLocalSaveAt = Date.now();
    await requestJson(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: nextRecord,
    });
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      String(poly?.linkedMeasurementId || "") === id
        ? { ...poly, linkedSlipId: nextSlipId }
        : poly
    ));
    persist();
    status = `Updated slip ID for ${id}.`;
    await refreshRecords({ quiet: true });
    render();
  }

  function exportRecordsForTable() {     return sortedRecordGeometryRows(records.map((record) => ({ record, geom: findGeometryForRecord(record) })))       .filter(({ record }) => record && isRecordVisible(record))       .map(({ record }) => record);   }    function exportBaseName() {     return sanitizeFilenamePart(filterMarinaId || state.marinaId || "slip_inventory");   }    function exportProjectCrs() {     const code = String(app?.services?.projections?.getProjectProfile?.()?.horizontalCRS || "EPSG:4326").trim().toUpperCase();     return code || "EPSG:4326";   }    function buildSlipFeatureCollection(sourceRecords = exportRecordsForTable()) {     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     const targetCrs = exportProjectCrs();     const featureCollection = {       type: "FeatureCollection",       name: `${exportBaseName()}_slip_inventory`,       crs: { type: "name", properties: { name: targetCrs } },       features: sourceRecords.map((record) => {         const normalized = buildDisplayExportRecord(record, displayToleranceFt);         const sourceGeometry = geometryFromWkt(normalized.geometry_wkt);         const geometry = sourceGeometry && targetCrs !== "EPSG:4326"           ? app.services.projections.transformGeometry(sourceGeometry, "EPSG:4326", targetCrs)           : sourceGeometry;         const properties = { ...normalized, export_crs: targetCrs };         delete properties.geometry_wkt;         return { type: "Feature", geometry, properties };       }),     };     return { featureCollection, targetCrs };   }    function dxfText(value) {     return String(value ?? "").replace(new RegExp("[\\r\\n]+", "g"), " ").slice(0, 255);   }    function dxfNumber(value) {     const n = Number(value);     return Number.isFinite(n) ? Number(n.toFixed(4)) : 0;   }    function dxfOrderedCoords(coords, closed = false) { const clean = (Array.isArray(coords) ? coords : []).map((pair) => [Number(pair?.[0]), Number(pair?.[1])]).filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y)); if (clean.length < 2) return []; const ring = closed && clean.length > 2 && clean[0][0] === clean[clean.length - 1][0] && clean[0][1] === clean[clean.length - 1][1] ? clean.slice(0, -1) : clean; const dedup = []; const seen = new Set(); ring.forEach(([x, y]) => { const key = String(dxfNumber(x)) + "," + String(dxfNumber(y)); if (!seen.has(key)) { seen.add(key); dedup.push([x, y]); } }); return dedup; }    function dxfPolyline(lines, coords, { closed = false, layer = "SLIPS" } = {}) { const ring = dxfOrderedCoords(coords, closed); if (ring.length < 2) return; lines.push("0", "POLYLINE", "8", layer, "66", "1", "70", closed ? "1" : "0"); for (const [x, y] of ring) { lines.push("0", "VERTEX", "8", layer, "10", String(dxfNumber(x)), "20", String(dxfNumber(y)), "30", "0"); } lines.push("0", "SEQEND", "8", layer); }    function dxfFeatureLabelPoint(geometry) {     if (!geometry) return null;     if (geometry.type === "Point") return geometry.coordinates;     if (geometry.type === "LineString" && Array.isArray(geometry.coordinates) && geometry.coordinates.length) {       return geometry.coordinates[Math.floor(geometry.coordinates.length / 2)];     }     if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates?.[0])) {       const ring = geometry.coordinates[0];       const sum = ring.reduce((acc, pair) => [acc[0] + Number(pair?.[0] || 0), acc[1] + Number(pair?.[1] || 0)], [0, 0]);       return [sum[0] / Math.max(1, ring.length), sum[1] / Math.max(1, ring.length)];     }     return null;   }    function buildDxfFromFeatureCollection(featureCollection, targetCrs) {     const lines = [       "0", "SECTION", "2", "HEADER", "9", "$ACADVER", "1", "AC1009", "0", "ENDSEC",       "0", "SECTION", "2", "TABLES", "0", "TABLE", "2", "LAYER", "70", "2",       "0", "LAYER", "2", "SLIPS", "70", "0", "62", "3", "6", "CONTINUOUS",       "0", "ENDTAB", "0", "ENDSEC", "0", "SECTION", "2", "ENTITIES",     ];     lines.push("999", "HYDROTOOLS_DXF_V5");     lines.push("999", "Hydrotools slip inventory export CRS: " + targetCrs);     for (const feature of featureCollection.features || []) {       const geometry = feature?.geometry;       if (!geometry) continue;       if (geometry.type === "Point") {         const [x, y] = geometry.coordinates || [];         lines.push("0", "POINT", "8", "SLIPS", "10", String(dxfNumber(x)), "20", String(dxfNumber(y)), "30", "0");       } else if (geometry.type === "LineString") {         dxfPolyline(lines, geometry.coordinates, { closed: false });       } else if (geometry.type === "Polygon") {         dxfPolyline(lines, geometry.coordinates?.[0], { closed: true });       }     }     lines.push("0", "ENDSEC", "0", "EOF");     const nl = String.fromCharCode(10); return lines.join(nl) + nl;   }    function renderSlipExportMenu({ disabled = false, includePlot = true } = {}) {     const d = disabled ? " disabled" : "";     const exportCrs = exportProjectCrs();     const crsIsGeo = exportCrs === "EPSG:4326";     const crsNote = crsIsGeo       ? `<div style="padding:4px 6px 2px;font-size:10px;color:rgba(251,191,36,.8);border-top:1px solid rgba(148,163,184,.12);margin-top:2px;">DXF/GeoJSON: ${exportCrs} — set CRS in Projection Manager</div>`       : `<div style="padding:4px 6px 2px;font-size:10px;color:rgba(148,163,184,.55);border-top:1px solid rgba(148,163,184,.12);margin-top:2px;">DXF/GeoJSON: ${exportCrs}</div>`;     return `<details class="slip-export-menu" style="position:relative;display:inline-block;">       <summary class="btn" style="list-style:none;cursor:${disabled ? "not-allowed" : "pointer"};opacity:${disabled ? 0.55 : 1};"${d}>Export ▾</summary>       <div style="position:absolute;right:0;top:calc(100% + 4px);z-index:240;min-width:154px;background:rgba(15,23,42,.98);border:1px solid rgba(148,163,184,.24);border-radius:6px;padding:4px;box-shadow:0 8px 22px rgba(0,0,0,.45);display:grid;gap:2px;">         <button type="button" class="btn" data-slip-export="csv"${d}>CSV</button>         <button type="button" class="btn" data-slip-export="xls"${d}>XLSX</button>         <button type="button" class="btn" data-slip-export="json"${d}>JSON</button>         <button type="button" class="btn" data-slip-export="geojson"${d}>GeoJSON</button>         <button type="button" class="btn" data-slip-export="dxf"${d}>DXF</button>         ${includePlot ? `<button type="button" class="btn" data-slip-export="plot"${d}>PDF Plot</button>` : ""}         ${crsNote}       </div>     </details>`;   }    async function runSlipExport(kind, statusEl = null) {     const action = String(kind || "").toLowerCase();     if (action === "csv") return exportCsv();     if (action === "xls" || action === "xlsx") return exportXls();     if (action === "json") return exportJson();     if (action === "geojson") return exportGeoJson();     if (action === "dxf") return exportDxf();     if (action === "plot") return exportPlotSvg(statusEl);     return null;   }    function exportJson() {     const outRecords = exportRecordsForTable();     downloadText(`${exportBaseName()}_slip_inventory.json`, JSON.stringify(outRecords, null, 2), "application/json;charset=utf-8");   }    function exportGeoJson() {     const { featureCollection } = buildSlipFeatureCollection(exportRecordsForTable());     downloadText(`${exportBaseName()}_slip_inventory.geojson`, JSON.stringify(featureCollection, null, 2), "application/geo+json;charset=utf-8");   }    function dxfProjectLngLat(lng, lat, targetCrs) {     const coord = [Number(lng), Number(lat)];     if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return null;     const projected = targetCrs === "EPSG:4326" ? coord : app.services.projections.transformCoord(coord, "EPSG:4326", targetCrs);     return Array.isArray(projected) && Number.isFinite(Number(projected[0])) && Number.isFinite(Number(projected[1])) ? [Number(projected[0]), Number(projected[1])] : null;   }    function dxfParametricRingForPoly(poly, targetCrs) {     const center = { lat: Number(poly?.center?.lat), lng: Number(poly?.center?.lng) };     const lengthFt = Number(poly?.lengthFt || 0);     const widthFt = Number(poly?.widthFt || 0);     if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng) || lengthFt <= 0 || widthFt <= 0) return null;     const ringLatLngs = computeParametricSlipPolygonLatLngs({       center,       lengthFt,       widthFt,       headingDeg: Number(poly?.headingDeg || 0),       chamferEnabled: !!poly?.chamferEnabled,       chamferSide: Number(poly?.chamferSide || 1) < 0 ? -1 : 1,       chamferAngleDeg: Number(poly?.chamferAngleDeg || 0),       chamferMagnitudeFt: Number(poly?.chamferMagnitudeFt || 0),     });     const ring = (Array.isArray(ringLatLngs) ? ringLatLngs : []).map(([lat, lng]) => dxfProjectLngLat(lng, lat, targetCrs)).filter(Boolean);     return ring.length >= 3 ? ring : null;   }    function dxfParametricRingForRecord(record, targetCrs) {     const sourceRing = parsePolygonWkt(record?.geometry_wkt);     const inferred = Array.isArray(sourceRing) && sourceRing.length >= 3 ? inferPolyMetricsFromRing(sourceRing) : null;     if (!inferred?.center) return null;     const ringLatLngs = computeParametricSlipPolygonLatLngs({       center: inferred.center,       lengthFt: Number(inferred.lengthFt || record?.measured_linear_feet || 0),       widthFt: Number(inferred.widthFt || record?.measured_width_feet || 0),       headingDeg: Number(inferred.headingDeg || 0),       chamferEnabled: !!inferred.chamferEnabled,       chamferSide: Number(inferred.chamferSide || 1) < 0 ? -1 : 1,       chamferAngleDeg: Number(inferred.chamferAngleDeg || 0),       chamferMagnitudeFt: Number(inferred.chamferMagnitudeFt || 0),     });     const ring = (Array.isArray(ringLatLngs) ? ringLatLngs : []).map(([lat, lng]) => dxfProjectLngLat(lng, lat, targetCrs)).filter(Boolean);     return ring.length >= 3 ? ring : null;   }    function buildDxfFeatureCollectionForRecords(sourceRecords = exportRecordsForTable()) {     const { featureCollection, targetCrs } = buildSlipFeatureCollection(sourceRecords);     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     const features = sourceRecords.map((record, index) => {       const poly = findGeometryForRecord(record);       const ring = (poly ? dxfParametricRingForPoly(poly, targetCrs) : null) || dxfParametricRingForRecord(record, targetCrs);       if (ring) {         const closed = [...ring, ring[0]];         const normalized = buildDisplayExportRecord(record, displayToleranceFt);         const properties = { ...normalized, export_crs: targetCrs, export_geometry_source: poly ? "parametric_map_outline" : "inferred_record_outline" };         delete properties.geometry_wkt;         return { type: "Feature", geometry: { type: "Polygon", coordinates: [closed] }, properties };       }       return featureCollection.features?.[index] || null;     }).filter(Boolean);     return { featureCollection: { ...featureCollection, features }, targetCrs };   }    function exportDxf() {     const { featureCollection, targetCrs } = buildDxfFeatureCollectionForRecords(exportRecordsForTable());     if (targetCrs === "EPSG:4326") {       app.ui?.toast?.("DXF exported in geographic coordinates (decimal degrees). Open Projection Manager and set a projected CRS for CAD use.", { level: "warn" });     }     const dxf = buildDxfFromFeatureCollection(featureCollection, targetCrs);     downloadText(exportBaseName() + "_slip_inventory_" + targetCrs.replace(/[^A-Z0-9]+/g, "_") + ".dxf", dxf, "application/dxf;charset=utf-8");   }    function exportCsv() {     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     downloadText(`${exportBaseName()}_slip_inventory.csv`, buildCsv(exportRecordsForTable(), displayToleranceFt), "text/csv;charset=utf-8");   }    async function exportXls() {
    const toleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);
    const [{ utils, write }, { default: JSZip }] = await Promise.all([
      import("xlsx"),
      import("jszip"),
    ]);

    const rows = exportRecordsForTable().map((r) => buildDisplayExportRecord(r, toleranceFt));
    const ws = utils.json_to_sheet(rows, { header: RECORD_HEADERS });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Slip Inventory");

    // SheetJS 0.18.x does not write <dataValidations> natively.
    // Generate base XLSX, re-open with JSZip, inject validation XML, re-zip.
    const xlsxBuf = write(wb, { type: "array", bookType: "xlsx" });
    const zip = await JSZip.loadAsync(xlsxBuf);
    const sheetXml = await zip.file("xl/worksheets/sheet1.xml").async("string");

    const nRows = rows.length;
    const cl = (key) => utils.encode_col(RECORD_HEADERS.indexOf(key));
    const dv = (key, opts) =>
      `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="0" sqref="${cl(key)}2:${cl(key)}${nRows + 1}"><formula1>"${opts.join(",")}"</formula1></dataValidation>`;

    const dvEntries = [
      ...[...BINARY_COLS].map((k) => dv(k, ["Yes", "No"])),
      dv("space_type",            SPACE_TYPE_OPTIONS),
      dv("out_of_service_reason", OOS_REASON_OPTIONS),
      dv("confidence_score",      ["High", "Medium", "Low"]),
    ];
    const dvXml = `<dataValidations count="${dvEntries.length}">${dvEntries.join("")}</dataValidations>`;
    zip.file("xl/worksheets/sheet1.xml", sheetXml.replace("</worksheet>", dvXml + "</worksheet>"));

    const outBuf = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
    const blob = new Blob([outBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportBaseName() + "_slip_inventory.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  async function exportPlotSvg(statusEl) {
    const setStatus = (msg) => { if (statusEl) statusEl.textContent = msg; };

    const mode = String(state.colorSlipsBy || "none");
    const marinaLabel = filterMarinaId || "Marina";

    const polysWithRecords = (state.slipGeometryPolygons || [])
      .filter((poly) => poly.committed)
      .map((poly) => ({ poly, record: findRecordForPolygon(poly) }))
      .filter(({ record }) => record && isRecordVisible(record));

    if (!polysWithRecords.length) {
      alert("No committed slip polygons to export. Draw and commit polygons first.");
      return;
    }

    // Web Mercator projection (same CRS as XYZ tiles — no distortion at marina scale)
    const toMx = (lng) => lng * Math.PI / 180;
    const toMy = (lat) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));

    // Build polygon data tracking both mercator coords and raw lat/lng bounds
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    const polyData = polysWithRecords.map(({ poly, record }) => {
      const ring = computeSlipPolygonLatLngs(poly);
      const pts = ring.map(([lat, lng]) => {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        return { mx: toMx(lng), my: toMy(lat) };
      });
      // Shoelace centroid in local coords to avoid floating-point cancellation
      // (Mercator coords are large absolute values; translate to near-zero first)
      const omx = pts.reduce((s, p) => s + p.mx, 0) / pts.length;
      const omy = pts.reduce((s, p) => s + p.my, 0) / pts.length;
      const lp = pts.map(p => ({ mx: p.mx - omx, my: p.my - omy }));
      let area = 0, lcx = 0, lcy = 0;
      for (let i = 0, j = lp.length - 1; i < lp.length; j = i++) {
        const cross = lp[j].mx * lp[i].my - lp[i].mx * lp[j].my;
        area += cross; lcx += (lp[j].mx + lp[i].mx) * cross; lcy += (lp[j].my + lp[i].my) * cross;
      }
      area /= 2;
      const cx = Math.abs(area) > 1e-20 ? lcx / (6 * area) + omx : omx;
      const cy = Math.abs(area) > 1e-20 ? lcy / (6 * area) + omy : omy;
      return { poly, record, pts, cx, cy };
    });

    // Mercator bounds
    const allPts = polyData.flatMap((d) => d.pts);
    const minMx = Math.min(...allPts.map((p) => p.mx));
    const maxMx = Math.max(...allPts.map((p) => p.mx));
    const minMy = Math.min(...allPts.map((p) => p.my));
    const maxMy = Math.max(...allPts.map((p) => p.my));

    const SVG_W = 1800;
    const PAD = 80;
    const rawBearing = Number(map?.getBearing?.());
    const mapBearingDeg = Number.isFinite(rawBearing) ? ((rawBearing % 360) + 360) % 360 : 0;
    const mapBearingRad = mapBearingDeg * Math.PI / 180;
    const cosBearing = Math.cos(mapBearingRad);
    const sinBearing = Math.sin(mapBearingRad);
    const centerMx = (minMx + maxMx) / 2;
    const centerMy = (minMy + maxMy) / 2;
    const rotatedDeltas = allPts.map((p) => {
      const dx = p.mx - centerMx;
      const dy = -(p.my - centerMy); // screen-space Y: north is up
      return {
        x: (dx * cosBearing) - (dy * sinBearing),
        y: (dx * sinBearing) + (dy * cosBearing),
      };
    });
    const minRotX = Math.min(...rotatedDeltas.map((p) => p.x));
    const maxRotX = Math.max(...rotatedDeltas.map((p) => p.x));
    const minRotY = Math.min(...rotatedDeltas.map((p) => p.y));
    const maxRotY = Math.max(...rotatedDeltas.map((p) => p.y));
    const rangeX = maxRotX - minRotX || 1e-9;
    const rangeY = maxRotY - minRotY || 1e-9;
    const scale = Math.min((SVG_W - 2 * PAD) / rangeX, (SVG_W - 2 * PAD) / rangeY);
    const contentH = rangeY * scale;
    const SVG_H = contentH + 2 * PAD;
    const mapCenterX = (SVG_W - ((minRotX + maxRotX) * scale)) / 2;
    const mapCenterY = PAD - (minRotY * scale);

    const sx = (mx) => mapCenterX + (mx - centerMx) * scale;
    const sy = (my) => mapCenterY - (my - centerMy) * scale; // Y flip (Mercator Y increases northward, SVG Y increases downward)

    // ---- Tile raster background ----
    // Walk Leaflet map._layers to find all currently-visible TileLayer instances
    const tileLayerOrder = (layer, fallback) => {
      const paneName = layer.options?.pane || "tilePane";
      const pane = typeof map?.getPane === "function" ? map.getPane(paneName) : null;
      const paneZ = Number.parseInt(pane?.style?.zIndex || "", 10);
      const optionZ = Number.parseInt(layer.options?.zIndex ?? "", 10);
      const zIndex = Number.isFinite(optionZ)
        ? optionZ
        : Number.isFinite(paneZ)
          ? paneZ
          : paneName === "tilePane"
            ? 200
            : 0;
      return { zIndex, id: Number(layer._leaflet_id || fallback) };
    };
    const tileLayerDefs = Object.values(map._layers || {})
      .filter((l) => typeof l._url === "string" && l._url)
      .filter((l) => typeof map?.hasLayer !== "function" || map.hasLayer(l))
      .map((l, index) => {
        const opacity = Number(l.options?.opacity ?? 1);
        return {
          urlTemplate: l._url,
          maxNativeZoom: l.options?.maxNativeZoom ?? l.options?.maxZoom ?? 18,
          opacity: Math.max(0, Math.min(1, Number.isFinite(opacity) ? opacity : 1)),
          ...tileLayerOrder(l, index),
        };
      })
      .filter((l) => l.opacity > 0.01)
      .sort((a, b) => (a.zIndex - b.zIndex) || (a.id - b.id));

    // Standard XYZ tile coordinate helpers
    const latLngToTileXY = (lat, lng, z) => {
      const n = 1 << z;
      const lr = lat * Math.PI / 180;
      return [
        Math.floor((lng + 180) / 360 * n),
        Math.max(0, Math.min(n - 1, Math.floor((1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2 * n))),
      ];
    };
    const tileTopLeft = (tx, ty, z) => {
      const n = 1 << z;
      return [
        Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / n))) * 180 / Math.PI,
        tx / n * 360 - 180,
      ];
    };
    const buildTileUrl = (tmpl, x, y, z) =>
      tmpl.replace("{z}", z).replace("{x}", x).replace("{y}", y)
        .replace("{s}", ["a", "b", "c"][Math.abs(x + y) % 3]).replace("{r}", "");
    const fetchTileB64 = async (url) => {
      try {
        const r = await fetch(url, { mode: "cors" });
        if (!r.ok) return null;
        const blob = await r.blob();
        return await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result.split(",")[1]);
          reader.onerror = rej;
          reader.readAsDataURL(blob);
        });
      } catch { return null; }
    };

    // Auto-pick zoom: target ~12 tiles across the lon extent, capped 14–20
    const lngRange = maxLng - minLng || 0.001;
    const tileZoom = Math.max(14, Math.min(20, Math.round(Math.log2(12 * 360 / lngRange))));

    const tileImages = [];
    if (tileLayerDefs.length) {
      for (let layerIndex = 0; layerIndex < tileLayerDefs.length; layerIndex++) {
        const { urlTemplate, maxNativeZoom, opacity } = tileLayerDefs[layerIndex];
        let fetched = 0;
        const z = Math.min(tileZoom, maxNativeZoom);
        const tileCount = 1 << z;
        const rotationPad = Math.abs(Math.sin(mapBearingRad)) > 0.05 ? 3 : 1;
        const [rawTxMin, rawTyMin] = latLngToTileXY(maxLat, minLng, z);
        const [rawTxMax, rawTyMax] = latLngToTileXY(minLat, maxLng, z);
        const txMin = rawTxMin - rotationPad;
        const txMax = rawTxMax + rotationPad;
        const tyMin = Math.max(0, rawTyMin - rotationPad);
        const tyMax = Math.min(tileCount - 1, rawTyMax + rotationPad);
        const totalTiles = (txMax - txMin + 1) * (tyMax - tyMin + 1);
        const layerStatus = tileLayerDefs.length > 1 ? ` layer ${layerIndex + 1}/${tileLayerDefs.length}` : "";
        setStatus(`Fetching tiles${layerStatus}... 0 / ${totalTiles}`);

        for (let tx = txMin; tx <= txMax; tx++) {
          for (let ty = tyMin; ty <= tyMax; ty++) {
            const url = buildTileUrl(urlTemplate, tx, ty, z);
            const b64 = await fetchTileB64(url);
            fetched++;
            setStatus(`Fetching tiles${layerStatus}... ${fetched} / ${totalTiles}`);
            if (!b64) continue;

            const [tlLat, tlLng] = tileTopLeft(tx, ty, z);
            const [brLat, brLng] = tileTopLeft(tx + 1, ty + 1, z);
            const ix = sx(toMx(tlLng));
            const iy = sy(toMy(tlLat));
            const iw = sx(toMx(brLng)) - ix;
            const ih = sy(toMy(brLat)) - iy;
            const opacityAttr = opacity < 0.995 ? ` opacity="${opacity.toFixed(3)}"` : "";
            tileImages.push(`<image x="${ix.toFixed(1)}" y="${iy.toFixed(1)}" width="${Math.max(0, iw).toFixed(1)}" height="${Math.max(0, ih).toFixed(1)}" href="data:image/png;base64,${b64}" preserveAspectRatio="none"${opacityAttr}/>`);
          }
        }
      }
    }

    setStatus("Building SVG…");

    // Color lookup (mirrors renderGeometry)
    const getColor = (poly, record) => {
      if (mode === "confidence") return confidenceColor(record?.confidence_score);
      if (mode === "space_type") {
        const st = String(record?.space_type || "").trim() || "unknown";
        return SPACE_TYPE_COLOR_MAP[st] ?? paletteColorForKey(st);
      }
      if (mode === "dimension") return dimensionInfoForPolygon(poly).color;
      return "#38bdf8";
    };

    // Legend
    const legendMap = new Map();
    for (const { poly, record } of polysWithRecords) {
      const color = getColor(poly, record);
      const key = mode === "space_type" ? (String(record?.space_type || "").trim() || "unknown")
        : mode === "dimension" ? dimensionInfoForPolygon(poly).label
        : mode === "confidence" ? String(record?.confidence_score || "—")
        : null;
      if (key && !legendMap.has(key)) legendMap.set(key, color);
    }
    const legendEntries = [...legendMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));

    // Detect basemap mode for panel theming
    const isDark = document.querySelector('[data-map-mode="light"]') === null;
    const panelBg     = isDark ? "#0f172a" : "#f8fafc";
    const panelBorder = isDark ? "#334155" : "#cbd5e1";
    const panelText   = isDark ? "#e2e8f0" : "#1e293b";
    const panelSub    = isDark ? "#94a3b8" : "#64748b";
    const panelAccent = isDark ? "#60a5fa" : "#2563eb";
    const PF = "Arial,sans-serif";      // panel font family
    const PL = 16;                       // panel line height
    const PP = 10;                       // panel padding

    // Metadata — collected before layout so we know panel height
    const marinaCode = String(filterMarinaId || records[0]?.marina_id || "—");
    const marinaNameStr = (() => {
      const n = records.find((r) => String(r.marina_id || "") === marinaCode)?.marina_name
             || records[0]?.marina_name || marinaLabel || "—";
      return n.length > 22 ? n.slice(0, 20) + "…" : n;
    })();
    const exportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const oosCount = polysWithRecords.filter(({ record }) =>
      record?.out_of_service === true || String(record?.out_of_service ?? "") === "1").length;
    const typeCountMap = new Map();
    for (const { record } of polysWithRecords) {
      const st = String(record?.space_type || "").trim() || "Unknown";
      typeCountMap.set(st, (typeCountMap.get(st) || 0) + 1);
    }
    const typeCounts = [...typeCountMap.entries()].sort((a, b) => b[1] - a[1]);
    const infoRows = [
      ["Marina Code", marinaCode],
      ["Marina Name", marinaNameStr],
      ["Export Date", exportDate],
      ["Total Slips",  String(polysWithRecords.length)],
      ...(oosCount > 0 ? [["Out of Service", String(oosCount)]] : []),
    ];
    const META_TITLE_H = 20;
    const META_DIV_H   = 14;
    const META_W       = 210;
    const META_H = PP + META_TITLE_H + infoRows.length * PL + META_DIV_H + META_TITLE_H + typeCounts.length * PL + PP;

    // Legend layout
    const TOTAL_H = SVG_H;

    const xmlEsc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const rotateSvgPoint = (x, y) => {
      const dx = Number(x) - mapCenterX;
      const dy = Number(y) - mapCenterY;
      return {
        x: mapCenterX + (dx * cosBearing) - (dy * sinBearing),
        y: mapCenterY + (dx * sinBearing) + (dy * cosBearing),
      };
    };
    const rectOverlapArea = (a, b) => {
      const w = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1));
      const h = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1));
      return w * h;
    };
    const slipScreenBoxes = polyData.map(({ pts }) => {
      const rotatedPts = pts.map((p) => rotateSvgPoint(sx(p.mx), sy(p.my)));
      return {
        x1: Math.min(...rotatedPts.map((p) => p.x)),
        y1: Math.min(...rotatedPts.map((p) => p.y)),
        x2: Math.max(...rotatedPts.map((p) => p.x)),
        y2: Math.max(...rotatedPts.map((p) => p.y)),
      };
    });
    const metadataCandidates = [
      { x: PP, y: 44 + PP },
      { x: SVG_W - META_W - PP, y: 44 + PP },
      { x: PP, y: SVG_H - META_H - PP },
      { x: SVG_W - META_W - PP, y: SVG_H - META_H - PP },
    ];
    const metadataPosition = metadataCandidates
      .map((candidate, index) => {
        const rect = { x1: candidate.x, y1: candidate.y, x2: candidate.x + META_W, y2: candidate.y + META_H };
        const overlap = slipScreenBoxes.reduce((sum, box) => sum + rectOverlapArea(rect, box), 0);
        return { ...candidate, index, overlap };
      })
      .sort((a, b) => (a.overlap - b.overlap) || (a.index - b.index))[0] || metadataCandidates[0];

    const lines = [];
    lines.push(`<?xml version="1.0" encoding="UTF-8"?>`);
    lines.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${SVG_W}" height="${TOTAL_H}" viewBox="0 0 ${SVG_W} ${TOTAL_H}">`);
    lines.push(`<rect width="${SVG_W}" height="${TOTAL_H}" fill="${isDark ? "#0f172a" : "#f8fafc"}"/>`);

    lines.push(`<g>`);
    lines.push(`<rect width="${SVG_W}" height="${SVG_H}" fill="${isDark ? "#0f172a" : "#f8fafc"}"/>`);
    lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} ${mapCenterX.toFixed(3)} ${mapCenterY.toFixed(3)})">`);

    // Raster tile images (bottom layer)
    lines.push(...tileImages);
    lines.push(`</g>`);

    // Wash the basemap back as an unrotated sheet overlay; rotating this creates large diagonal panels.
    if (tileImages.length) lines.push(`<rect width="${SVG_W}" height="${SVG_H}" fill="${isDark ? "#000000" : "#ffffff"}" fill-opacity="${isDark ? "0.12" : "0.34"}"/>`);
    lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} ${mapCenterX.toFixed(3)} ${mapCenterY.toFixed(3)})">`);

    // Polygon fills
    for (const { poly, record, pts } of polyData) {
      const color = getColor(poly, record);
      const pointsStr = pts.map((p) => `${sx(p.mx).toFixed(2)},${sy(p.my).toFixed(2)}`).join(" ");
      lines.push(`<polygon points="${pointsStr}" fill="${xmlEsc(color)}" fill-opacity="0.38" stroke="${xmlEsc(color)}" stroke-width="1.5" stroke-linejoin="round"/>`);
    }

    // Metadata card — upper-left, below the 44px title band
    {
      lines.push(`</g>`);
      for (const { record, pts, cx, cy } of polyData) {
        const slipId = String(record?.slip_id || record?.measurement_id || "");
        if (!slipId) continue;
        const rotatedPts = pts.map((p) => rotateSvgPoint(sx(p.mx), sy(p.my)));
        const pxW = Math.abs(Math.max(...rotatedPts.map((p) => p.x)) - Math.min(...rotatedPts.map((p) => p.x)));
        const pxH = Math.abs(Math.max(...rotatedPts.map((p) => p.y)) - Math.min(...rotatedPts.map((p) => p.y)));
        const longSide = Math.max(pxW, pxH);
        const fitByLength = (longSide * 0.82) / Math.max(1, slipId.length * 0.58);
        const fs = Math.max(7, Math.min(11, fitByLength));
        const labelPoint = rotateSvgPoint(sx(cx), sy(cy));
        lines.push(`<text x="${labelPoint.x.toFixed(2)}" y="${labelPoint.y.toFixed(2)}" text-anchor="middle" dominant-baseline="middle" font-family="${PF}" font-size="${fs.toFixed(1)}" font-weight="650" fill="#111827" fill-opacity="0.92">${xmlEsc(slipId)}</text>`);
      }

      const mx = metadataPosition.x;
      const my0 = metadataPosition.y;
      lines.push(`<g transform="translate(${mx},${my0})" font-family="${PF}">`);
      lines.push(`<rect x="0" y="0" width="${META_W}" height="${META_H}" fill="${panelBg}" fill-opacity="0.92" stroke="${panelBorder}" stroke-width="1" rx="6"/>`);
      let cy2 = PP;
      // Info section title
      lines.push(`<text x="${PP}" y="${cy2 + 12}" font-size="10" font-weight="bold" fill="${panelAccent}" letter-spacing="0.8">MARINA INFO</text>`);
      cy2 += META_TITLE_H;
      // Info rows: left label, right value
      for (const [label, value] of infoRows) {
        lines.push(`<text x="${PP}" y="${cy2 + 9}" font-size="10" fill="${panelSub}" dominant-baseline="middle">${xmlEsc(label)}</text>`);
        lines.push(`<text x="${META_W - PP}" y="${cy2 + 9}" font-size="10" font-weight="bold" fill="${panelText}" text-anchor="end" dominant-baseline="middle">${xmlEsc(value)}</text>`);
        cy2 += PL;
      }
      // Divider
      cy2 += 4;
      lines.push(`<line x1="${PP}" y1="${cy2}" x2="${META_W - PP}" y2="${cy2}" stroke="${panelBorder}" stroke-width="1"/>`);
      cy2 += 10;
      // Space types section title
      lines.push(`<text x="${PP}" y="${cy2 + 12}" font-size="10" font-weight="bold" fill="${panelAccent}" letter-spacing="0.8">SPACE TYPES</text>`);
      cy2 += META_TITLE_H;
      // Type rows: colored dot, name, count
      for (const [type, count] of typeCounts) {
        const dotColor = SPACE_TYPE_COLOR_MAP[type] ?? paletteColorForKey(type);
        lines.push(`<circle cx="${PP + 4}" cy="${cy2 + 8}" r="4" fill="${xmlEsc(dotColor)}" stroke="none"/>`);
        lines.push(`<text x="${PP + 14}" y="${cy2 + 9}" font-size="10" fill="${panelText}" dominant-baseline="middle">${xmlEsc(type)}</text>`);
        lines.push(`<text x="${META_W - PP}" y="${cy2 + 9}" font-size="10" fill="${panelSub}" text-anchor="end" dominant-baseline="middle">${xmlEsc(String(count))}</text>`);
        cy2 += PL;
      }
      lines.push(`</g>`);
    }

    // Title overlay: semi-transparent frosted band at top of the map area
    {
      const ax = SVG_W - 72;
      const ay = 66;
      lines.push(`<g transform="translate(${ax},${ay})" font-family="${PF}">`);
      lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} 0 18)">`);
      lines.push(`<path d="M0 0 L9 31 L0 25 L-9 31 Z" fill="#ffffff" stroke="#111827" stroke-width="1" stroke-linejoin="round"/>`);
      lines.push(`<path d="M0 0 L0 25" stroke="#111827" stroke-width="0.8" stroke-linecap="round" opacity="0.72"/>`);
      lines.push(`</g>`);
      lines.push(`<text x="0" y="43" text-anchor="middle" font-size="13" font-weight="700" fill="#111827">N</text>`);
      lines.push(`</g>`);
    }

    lines.push(`<rect x="0" y="0" width="${SVG_W}" height="44" fill="#0f172a" fill-opacity="${isDark ? "0.75" : "0.60"}"/>`);
    lines.push(`<text x="${SVG_W / 2}" y="28" text-anchor="middle" font-family="${PF}" font-size="18" font-weight="bold" fill="#e2e8f0">${xmlEsc(marinaLabel)} — Slip Inventory Plot</text>`);

    lines.push(`</g>`); // end map area group

    // Legend strip removed — space type breakdown is in the metadata card.

    lines.push(`</svg>`);

    const svgContent = lines.join("\n");
    setStatus("Generating PDF…");

    // Lazy-load jsPDF + svg2pdf only when the button is clicked (keeps initial bundle lean)
    const [{ jsPDF }, { svg2pdf }] = await Promise.all([
      import("jspdf"),
      import("svg2pdf.js"),
    ]);

    // svg2pdf needs the SVG element to be in the live DOM to resolve styles/dimensions
    const offscreen = document.createElement("div");
    offscreen.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
    offscreen.innerHTML = svgContent;
    const svgEl = offscreen.querySelector("svg");
    document.body.appendChild(offscreen);

    try {
      const pdf = new jsPDF({
        unit: "px",
        format: [SVG_W, TOTAL_H],
        orientation: SVG_W > TOTAL_H ? "landscape" : "portrait",
        hotfixes: ["px_scaling"],
        compress: true,
      });
      await svg2pdf(svgEl, pdf, { x: 0, y: 0, width: SVG_W, height: TOTAL_H });
      const fname = `${marinaLabel.replace(/[^a-z0-9_-]/gi, "_")}_slip_plot.pdf`;
      pdf.save(fname);
    } finally {
      document.body.removeChild(offscreen);
    }
  }

  let _plotExporting = false;
  let _renderRafId = null;
  let _surfacesCatalog = null;
  let _surfacesCatalogLoading = false;
  function render() {
    if (_renderRafId !== null) return;
    try {
      const stack = new Error().stack || "";
      const lines = stack.split("\n").slice(1, 4).map((l) => l.replace(/.*?at\s+/, "").replace(/\s*\(.*\)/, "").trim());
      _perfLastCaller = lines.join(" → ").slice(0, 120);
    } catch {}
    _renderRafId = requestAnimationFrame(() => { _renderRafId = null; _renderNow(); });
  }
  function _renderNow() {
    _perfRenderCount++;
    const _activeMarina = filterMarinaId || state.marinaId || "";
    if (_activeMarina && !_surfacesCatalog && !_surfacesCatalogLoading) {
      _surfacesCatalogLoading = true;
      fetch(`/api/surfaces/catalog?marina=${encodeURIComponent(_activeMarina)}`).then(r => r.json()).then(data => {
        _surfacesCatalog = (data.catalog || []).filter(s => s.rasterReady);
        render();
      }).catch(() => { _surfacesCatalog = []; }).finally(() => { _surfacesCatalogLoading = false; });
    }
    renderMap();
    renderBottomPanel();
    if (!containerEl) return;
    const current = selectedRecord();
    const currentMarina = marinaOptionForId(current?.marina_id, current?.marina_name);
    const defaultMarina = marinaOptionForId(state.marinaId, state.marinaName);
    const currentOptions = [
      currentMarina,
      ...marinaCatalog,
    ]
      .filter((entry, index, arr) => entry && arr.findIndex((candidate) => candidate?.marinaId === entry.marinaId) === index)
      .sort((a, b) => String(a?.marinaId || "").localeCompare(String(b?.marinaId || ""), undefined, { numeric: true, sensitivity: "base" }));
    const previewPoints = linePreviewPoints();
    const previewIds = previewSlipIds(state, previewPoints.length);
    const canGenerateLine = lineLayoutMode === "spacing"
      ? lineEndpoints.length >= 3 && previewPoints.length >= 1
      : lineEndpoints.length >= 2 && previewPoints.length >= 1;
    const selectedGeom = selectedGeometryPolygon();
    const selectedGeomCount = selectedGeometryPolygons().length;
    const canEstimateGeometryImagery = geometryRunLayoutActive && geometryRunEndpoints.length >= 2 && !!selectedGeom;
    const canEstimateImagery = lineLayoutMode !== "spacing" && lineEndpoints.length >= 2;
    const chartSignal = Array.isArray(imageryEstimateDebug?.chosenSignal) ? imageryEstimateDebug.chosenSignal : [];
    const chartWidth = 640;
    const chartHeight = 180;
    const chartPadding = 12;
    const chartPath = buildSparklinePath(chartSignal, chartWidth, chartHeight, chartPadding);
    const chartMin = chartSignal.length ? Math.min(...chartSignal) : 0;
    const chartMax = chartSignal.length ? Math.max(...chartSignal) : 0;
    const chartRange = Math.max(1e-6, chartMax - chartMin);
    const liveCenterRatios = previewPoints.length >= 2 ? linePreviewRatios() : [];
    const chartCenterLines = liveCenterRatios.map((ratio) => {
      const x = chartPadding + (ratio * (chartWidth - (chartPadding * 2)));
      return `<line x1="${x.toFixed(1)}" y1="${chartPadding}" x2="${x.toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(245,158,11,0.55)" stroke-width="1.5" stroke-dasharray="4 4" />`;
    }).join("");
    const chartPeakDots = chartSignal.map((value, index) => {
      const x = chartPadding + ((index / Math.max(1, chartSignal.length - 1)) * (chartWidth - (chartPadding * 2)));
      const y = chartHeight - chartPadding - (((value - chartMin) / chartRange) * (chartHeight - (chartPadding * 2)));
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.6" fill="rgba(147,197,253,0.8)" />`;
    }).join("");
    const geomChartSignal = Array.isArray(geometryImageryEstimateDebug?.chosenSignal) ? geometryImageryEstimateDebug.chosenSignal : [];
    const geomChartPath = buildSparklinePath(geomChartSignal, chartWidth, chartHeight, chartPadding);
    const geomChartMin = geomChartSignal.length ? Math.min(...geomChartSignal) : 0;
    const geomChartMax = geomChartSignal.length ? Math.max(...geomChartSignal) : 0;
    const geomChartRange = Math.max(1e-6, geomChartMax - geomChartMin);
    const geomCenterRatios = Array.isArray(geometryImageryEstimateDebug?.centerRatios) ? geometryImageryEstimateDebug.centerRatios : [];
    const geomChartCenterLines = geomCenterRatios.map((ratio) => {
      const x = chartPadding + (ratio * (chartWidth - (chartPadding * 2)));
      return `<line x1="${x.toFixed(1)}" y1="${chartPadding}" x2="${x.toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(245,158,11,0.6)" stroke-width="1.5" stroke-dasharray="4 4" />`;
    }).join("");
    const geomChartDots = geomChartSignal.map((value, index) => {
      const x = chartPadding + ((index / Math.max(1, geomChartSignal.length - 1)) * (chartWidth - (chartPadding * 2)));
      const y = chartHeight - chartPadding - (((value - geomChartMin) / geomChartRange) * (chartHeight - (chartPadding * 2)));
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.5" fill="rgba(125,211,252,0.9)" />`;
    }).join("");
    containerEl.innerHTML = `
      <div class="tool-section" style="display:flex; flex-direction:column; gap:12px;">
        <div class="row" style="justify-content:space-between; align-items:flex-start; gap:12px;">
          <div>
            <div class="lm-title" style="margin:0;">Slip Inventory</div>
            <div class="small" style="margin-top:4px; color:var(--muted);">
              Draw slips, select a polygon, then edit its attributes in the inspector.
            </div>
          </div>
          <div class="small" style="text-align:right; color:var(--muted);">
            ${state.marinaName || state.marinaId
              ? `<strong style="color:#e2e8f0;">${esc(state.marinaName || state.marinaId)}</strong>${state.marinaName && state.marinaId && state.marinaName !== state.marinaId ? `<br>${esc(state.marinaId)}` : ""}`
              : `No marina selected`}
            <br>${esc(records.length)} records / ${esc(state.slipGeometryPolygons.length)} polygons
          </div>
        </div>

        <div style="display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:6px;">
          <button id="slip-geom-place" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${activeGeometryPlaceMode ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}"${state.slipGeometryDraftTypeId ? "" : " disabled"}>
            <span style="display:block; font-weight:700;">Polygon</span>
          </button>
          <button id="slip-geom-place-multislip" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${multislipActive ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
            <span style="display:block; font-weight:700;">Multislip</span>
          </button>
          <button id="slip-geom-free-draw" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${freeDrawActive ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
            <span style="display:block; font-weight:700;">Shape</span>
          </button>
        </div>

        <div style="display:grid; grid-template-columns:minmax(0,1fr); gap:10px; align-items:start;">
          <details style="border:1px solid rgba(148,163,184,0.18); border-radius:8px; padding:10px; background:rgba(15,23,42,0.24);" open>
            <summary class="small" style="font-weight:700; color:#e2e8f0; margin-bottom:8px; cursor:pointer; list-style:none; display:flex; justify-content:space-between; align-items:center;">New Slip Defaults <span style="font-size:9px; color:var(--muted);">▾</span></summary>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
              <label class="small">Dock
                <input id="slip-default-dock" type="text" value="${esc(state.dockName)}" placeholder="A" />
              </label>
              <label class="small">Next ID
                <input id="slip-default-slipid-part3" type="text" value="${esc(state.slipIdPart3)}" placeholder="001" />
              </label>
              <label class="small">Slip Type
                <select id="slip-default-space-type">
                  <option value="">-</option>
                  ${SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${state.spaceType === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
                </select>
              </label>
              <label class="small">Type Size
                <select id="slip-geom-active-type">
                  <option value="">Select size</option>
                  ${state.slipGeometryTypes.map((entry) => `<option value="${esc(entry.id)}"${String(entry.id) === String(state.slipGeometryDraftTypeId || "") ? " selected" : ""}>${esc(entry.name)} (${esc(Number(entry.lengthFt).toFixed(1))}x${esc(Number(entry.widthFt).toFixed(1))})</option>`).join("")}
                </select>
              </label>
              <label class="small" style="display:flex; align-items:center; gap:6px;">
                <input id="slip-default-slipid-autoinc" type="checkbox" ${state.slipIdAutoIncrement ? "checked" : ""} />
                Auto increment
              </label>
              <label class="small" style="display:flex; align-items:center; gap:6px;">
                <input id="slip-default-covered" type="checkbox" ${draftCovered ? "checked" : ""} />
                Covered
              </label>
              <label class="small" style="display:flex; align-items:center; gap:6px;">
                <input id="slip-default-boatlift" type="checkbox" ${draftBoatlift ? "checked" : ""} />
                Boatlift
              </label>
              <label class="small" style="display:flex; align-items:center; gap:6px;">
                <input id="slip-default-fueldock" type="checkbox" ${draftFuelDock ? "checked" : ""} />
                Fuel dock
              </label>
            </div>
            <details style="margin-top:8px;">
              <summary class="small" style="cursor:pointer; color:#93c5fd;">Size presets</summary>
              <div style="display:grid; grid-template-columns:1fr 76px 76px auto; gap:6px; margin-top:8px; align-items:end;">
                <label class="small">Name
                  <input id="slip-geom-type-name" type="text" value="Standard" />
                </label>
                <label class="small">L ft
                  <input id="slip-geom-type-length" type="number" min="0.5" step="0.5" value="40" />
                </label>
                <label class="small">W ft
                  <input id="slip-geom-type-width" type="number" min="0.5" step="0.5" value="14" />
                </label>
                <button id="slip-geom-type-add" type="button" class="btn">Add</button>
              </div>
            </details>
          </details>

          <details style="border:1px solid rgba(148,163,184,0.18); border-radius:8px; padding:10px; background:rgba(15,23,42,0.24);" open>
            <summary class="small" style="font-weight:700; color:#e2e8f0; cursor:pointer; list-style:none; display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
              <span>Selected Slip Attributes <span style="font-size:10px; font-weight:400; color:var(--muted);">${current ? esc(current.measurement_id) : (selectedGeom ? "Uncommitted polygon" : "No selection")}</span></span>
              <span style="font-size:9px; color:var(--muted);">▾</span>
            </summary>
            ${current ? `
              <table style="width:100%; border-collapse:collapse; margin-top:8px; font-size:11px; table-layout:fixed;">
                <tbody>
                  <tr>
                    <td style="width:92px; color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Slip ID</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-slip-id" type="text" value="${esc(current.slip_id)}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Slip Type</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-space-type" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value=""></option>
                        ${SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${String(current.space_type || "") === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Marina</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-marina-id" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="">Select marina</option>
                        ${currentOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === current.marina_id ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
                      </select>
                      <input id="slip-marina-name" type="hidden" value="${esc(currentMarina?.marinaName || current.marina_name)}" />
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Length ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-linear-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_linear_feet))}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Width ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-width-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_width_feet))}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Depth ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-depth-feet" type="number" step="0.01" value="${esc(current.measured_depth_feet ?? "")}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Confidence</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-confidence" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="High"${current.confidence_score === "High" ? " selected" : ""}>High</option>
                        <option value="Medium"${current.confidence_score === "Medium" ? " selected" : ""}>Medium</option>
                        <option value="Low"${current.confidence_score === "Low" ? " selected" : ""}>Low</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Date</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-measurement-date" type="date" value="${esc(current.measurement_date)}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Status</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-operational-status" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="operable"${String(current.operational_status || "operable") === "operable" ? " selected" : ""}>operable</option>
                        <option value="under_construction"${String(current.operational_status || "") === "under_construction" ? " selected" : ""}>under_construction</option>
                        <option value="maintenance"${String(current.operational_status || "") === "maintenance" ? " selected" : ""}>maintenance</option>
                        <option value="blocked"${String(current.operational_status || "") === "blocked" ? " selected" : ""}>blocked</option>
                        <option value="closed"${String(current.operational_status || "") === "closed" ? " selected" : ""}>closed</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); vertical-align:top; padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Notes</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <textarea id="slip-notes" rows="2" style="width:100%; resize:vertical; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">${esc(current.notes || "")}</textarea>
                      <textarea id="slip-geometry-wkt" style="display:none;">${esc(current.geometry_wkt || "")}</textarea>
                      <input id="slip-record-created-timestamp" type="hidden" value="${esc(current.record_created_timestamp || "")}" />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="row" style="gap:8px; margin-top:8px;">
                <button id="slip-save" type="button" class="btn primary">Save Attributes</button>
                <button id="slip-show-selected" type="button" class="btn">Zoom To</button>
                <button id="slip-delete" type="button" class="btn">Delete</button>
              </div>
            ` : selectedGeom ? `
              <div class="small" style="margin-top:8px; color:var(--muted);">This polygon is selected but has not been committed to a slip record.</div>
              <div style="display:grid; grid-template-columns:repeat(2,minmax(150px,1fr)); gap:8px; margin-top:8px;">
                <label class="small">Length: ${esc(Number(selectedGeom?.lengthFt ?? 0).toFixed(1))} ft
                  <input id="slip-geom-slider-length" type="range" min="5" max="120" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.lengthFt ?? 40)}" />
                </label>
                <label class="small">Width: ${esc(Number(selectedGeom?.widthFt ?? 0).toFixed(1))} ft
                  <input id="slip-geom-slider-width" type="range" min="3" max="40" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.widthFt ?? 14)}" />
                </label>
                <label class="small">Rotation: ${esc(Number(selectedGeom?.headingDeg ?? 0).toFixed(1))} deg
                  <input id="slip-geom-slider-rotation" type="range" min="-180" max="180" step="${esc(rotationToleranceStep())}" value="${esc(selectedGeom?.headingDeg ?? 0)}" />
                </label>
                <label class="small">Chamfer: ${esc(Number(selectedGeom?.chamferMagnitudeFt ?? 0).toFixed(1))} ft
                  <input id="slip-geom-slider-chamfer-mag" type="range" min="0" max="20" step="0.5" value="${esc(selectedGeom?.chamferMagnitudeFt ?? 0)}" />
                </label>
              </div>
              <div class="row" style="gap:8px; margin-top:8px;">
                <button id="slip-geom-commit-selected" type="button" class="btn primary">Create Slip Record</button>
                <button id="slip-geom-delete-selected" type="button" class="btn">Delete Polygon</button>
                <button id="slip-geom-clear-selection" type="button" class="btn">Clear Selection</button>
              </div>
            ` : `
              <div style="margin-top:10px; padding:18px; border:1px dashed rgba(148,163,184,0.28); border-radius:8px; color:var(--muted); text-align:center;">
                Select a slip polygon or a row in the table to edit attributes.
              </div>
            `}
          </details>
        </div>

        <div class="row" style="gap:8px; flex-wrap:wrap; align-items:center; padding-top:2px;">
          <button id="slip-toggle-ids" type="button" class="btn">${state.showSlipIds ? "Hide IDs" : "Show IDs"}</button>
          <button id="slip-toggle-depths" type="button" class="btn${state.showDepths ? " primary" : ""}">${state.showDepths ? "Hide Depths" : "Show Depths"}</button>
          <label class="small">Color
            <select id="slip-color-by" style="min-width:130px;">
              <option value="none"${state.colorSlipsBy === "none" ? " selected" : ""}>None</option>
              <option value="dimension"${state.colorSlipsBy === "dimension" ? " selected" : ""}>Dimension</option>
              <option value="confidence"${state.colorSlipsBy === "confidence" ? " selected" : ""}>Confidence</option>
              <option value="space_type"${state.colorSlipsBy === "space_type" ? " selected" : ""}>Slip Type</option>
            </select>
          </label>
          <button id="slip-geom-apply-report" type="button" class="btn primary"${records.length && state.slipGeometryPolygons.length ? "" : " disabled"}>Update Report</button>
          <button id="slip-refresh" type="button" class="btn">Refresh</button> ${renderSlipExportMenu({ disabled: !records.length })}
        </div>
        <div class="small" style="color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
      </div>

      <details hidden style="display:none; margin-top:10px;">
        <summary class="small" style="cursor:pointer; color:#93c5fd; padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:8px; background:rgba(15,23,42,0.22);">
          Advanced tools
        </summary>
        <div style="margin-top:8px;">
      <div class="tool-section">
        <div class="small">Create database records by dropping a point on the map, then edit the current export-schema fields here.</div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:center;">
          <div class="small">
            <div style="color:var(--muted); margin-bottom:4px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em;">Marina (project settings)</div>
            <div class="row" style="gap:8px; align-items:center;">
              ${state.marinaName || state.marinaId
                ? `<span style="font-size:13px; color:#e2e8f0;">${esc(state.marinaName || state.marinaId)}${state.marinaName && state.marinaId && state.marinaName !== state.marinaId ? ` <span style="color:var(--muted); font-size:11px;">(${esc(state.marinaId)})</span>` : ""}</span>`
                : `<span style="color:var(--muted); font-size:12px; font-style:italic;">Not set — select a marina in project settings</span>`}
              <button id="slip-default-marina-zoom" type="button" class="btn"${defaultMarina && Number.isFinite(defaultMarina.lat) && Number.isFinite(defaultMarina.lng) ? "" : " disabled"}>Zoom To</button>
            </div>
          </div>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small">Dock
            <input id="slip-default-dock" type="text" value="${esc(state.dockName)}" style="width:70px;" placeholder="A" />
          </label>
          <label class="small">Slip ID
            <div class="row" style="gap:4px; align-items:center; margin-top:3px;">
              <span style="font-size:12px; color:var(--muted); min-width:18px;">${esc(state.dockName || "—")}</span>
              <select id="slip-default-slipid-separator" style="width:50px; padding:2px 2px;">
                <option value="-"${state.slipIdSeparator === "-" ? " selected" : ""}>-</option>
                <option value="."${state.slipIdSeparator === "." ? " selected" : ""}>.</option>
              </select>
              <input id="slip-default-slipid-part3" type="text" value="${esc(state.slipIdPart3)}" style="width:65px;" placeholder="001" />
            </div>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-slipid-autoinc" type="checkbox" ${state.slipIdAutoIncrement ? "checked" : ""} />
            Auto Inc
          </label>
          <label class="small">Step
            <input id="slip-default-slipid-step" type="number" min="1" step="1" value="${esc(state.slipIdIncrementBy)}" style="width:58px;" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-multislip" type="checkbox" ${draftMultiSlip ? "checked" : ""} />
            Multi-Slip
          </label>
          <label class="small">MultiSlip ID
            <input id="slip-default-multislip-id" type="text" value="${esc(draftMultiSlipId)}" style="width:80px;" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-covered" type="checkbox" ${draftCovered ? "checked" : ""} />
            Covered
          </label>
          <label class="small">Space Type
            <select id="slip-default-space-type">
              <option value="">—</option>
              ${SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${state.spaceType === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
            </select>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-boatlift" type="checkbox" ${draftBoatlift ? "checked" : ""} />
            Boatlift
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-fueldock" type="checkbox" ${draftFuelDock ? "checked" : ""} />
            Fuel Dock
          </label>
          <label class="small">Confidence
            <select id="slip-default-confidence">
              <option value="High"${state.confidenceScore === "High" ? " selected" : ""}>High</option>
              <option value="Medium"${state.confidenceScore === "Medium" ? " selected" : ""}>Medium</option>
              <option value="Low"${state.confidenceScore === "Low" ? " selected" : ""}>Low</option>
            </select>
          </label>
          <label class="small">Measurement Date
            <input id="slip-default-measurement-date" type="date" value="${esc(state.measurementDate)}" />
          </label>
          <label class="small">Number of Slips
            <input id="slip-default-line-count" type="number" min="1" step="1" value="${esc(state.lineSlipCount)}" style="width:90px;" />
          </label>
          <div class="small" style="padding-bottom:6px; min-width:160px;">Next Slip ID: <strong>${esc(buildSlipIdFromState(state) || "auto")}</strong></div>
          <label class="small" style="flex:1; min-width:180px;">Comments
            <textarea id="slip-default-comments" rows="2" style="width:100%; resize:vertical; font-family:inherit; font-size:11px; background:rgba(2,6,23,.8); border:1px solid rgba(148,163,184,0.2); border-radius:4px; color:#e2e8f0; padding:4px 6px; box-sizing:border-box;">${esc(draftComments)}</textarea>
          </label>
          <div class="row" style="margin-left:auto; gap:8px; align-items:flex-end;">
            <button id="slip-drop-point" type="button" class="btn primary">${pointDropActive ? "Click Map..." : "Drop Point"}</button>
            <button id="slip-stop-drop" type="button" class="btn"${pointDropActive ? "" : " disabled"}>Stop</button>
            <button id="slip-spacing-run-layout" type="button" class="btn primary"${lineLayoutActive ? " disabled" : ""}>Spacing Run</button>
            <button id="slip-line-clear" type="button" class="btn"${lineEndpoints.length ? "" : " disabled"}>Clear Line</button>
            <button id="slip-line-estimate-imagery" type="button" class="btn"${canEstimateImagery ? "" : " disabled"}>Auto Estimate From Imagery</button>
            <button id="slip-line-generate" type="button" class="btn"${canGenerateLine ? "" : " disabled"}>Generate Line</button>
            <button id="slip-line-commit-feature" type="button" class="btn"${lineEndpoints.length >= 2 ? "" : " disabled"}>Commit Line</button>
            <button id="slip-refresh" type="button" class="btn">Refresh</button> ${renderSlipExportMenu({ disabled: !records.length })}
          </div>
        </div>
        <div style="margin-top:10px;padding-top:10px;border-top:1px solid rgba(148,163,184,0.15);">
          <div class="small" style="font-weight:600;margin-bottom:6px;color:var(--muted);">FILL DEPTHS FROM SURFACE</div>
          <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
            ${(_surfacesCatalog || []).length
              ? `<select id="slip-depth-surface-select" class="btn" style="flex:1;min-width:120px;padding:3px 6px;">
                  <option value="">Choose surface…</option>
                  ${(_surfacesCatalog || []).filter(s=>s.rasterReady).map(s=>`<option value="${esc(s.id)}">${esc(s.name || s.id)}${s.marinaCode ? ` (${s.marinaCode})` : ""}</option>`).join("")}
                </select>`
              : `<span class="small" style="color:var(--muted);">${_surfacesCatalogLoading ? "Loading…" : "No built surfaces found"}</span>`
            }
            <button id="slip-fill-depths-run" type="button" class="btn primary"${filterMarinaId && (_surfacesCatalog||[]).some(s=>s.rasterReady) ? "" : " disabled"}>Fill Depths</button>
          </div>
          <div id="slip-fill-depths-status" class="small" style="color:var(--muted);margin-top:4px;min-height:14px;"></div>
        </div>
        ${lineEndpoints.length ? `
          <div class="small" style="margin-top:8px;">
            ${lineLayoutMode === "spacing"
              ? (lineEndpoints.length === 1
                ? "First slip center set. Click the next slip center to measure spacing."
                : lineEndpoints.length === 2
                  ? `Spacing measured. Move along the pier to preview ${previewPoints.length} slips, then click the end of the run.`
                  : `Spacing run ready with ${previewPoints.length} slips. Press Enter or Generate Line.`)
              : (lineEndpoints.length === 1
                ? "Line start set. Click the end point on the map."
                : `Previewing ${previewPoints.length} slips along the line. Click a preview mark to snap the count to that position.`)}
          </div>
          ${previewIds.length ? `<div class="small" style="margin-top:4px;">Preview IDs: ${esc(previewIds.join(", "))}</div>` : ""}
        ` : ""}
        ${imageryEstimateStatus ? `<div class="small" style="margin-top:6px; color:#93c5fd;">${esc(imageryEstimateStatus)}</div>` : ""}
        ${chartSignal.length ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,.18); border-radius:8px; background:rgba(15,23,42,.35);">
            <div class="small" style="margin-bottom:6px;">Imagery signal debug: blue line is sampled imagery intensity change, dashed orange lines are suggested slip centers.</div>
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="display:block; width:100%; height:auto;">
              <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" rx="8" fill="rgba(15,23,42,0.5)" />
              <line x1="${chartPadding}" y1="${(chartHeight - chartPadding).toFixed(1)}" x2="${(chartWidth - chartPadding).toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(148,163,184,0.35)" stroke-width="1" />
              ${chartCenterLines}
              <path d="${chartPath}" fill="none" stroke="rgba(147,197,253,0.95)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
              ${chartPeakDots}
            </svg>
          </div>
        ` : ""}
      </div>
      <div class="tool-section" style="margin-top:10px;">
        <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
          <div class="lm-title">Slip Geometry (Phase 1)</div>
          <div class="small">${esc(state.slipGeometryPolygons.length)} polygon${state.slipGeometryPolygons.length === 1 ? "" : "s"} | selected: ${esc(selectedGeomCount)}</div>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small">Type Name
            <input id="slip-geom-type-name" type="text" value="Standard" style="width:120px;" />
          </label>
          <label class="small">Length (ft)
            <input id="slip-geom-type-length" type="number" min="0.5" step="0.5" value="40" style="width:90px;" />
          </label>
          <label class="small">Width (ft)
            <input id="slip-geom-type-width" type="number" min="0.5" step="0.5" value="14" style="width:90px;" />
          </label>
          <button id="slip-geom-type-add" type="button" class="btn">Add Type</button>
          <label class="small">Active Type
            <select id="slip-geom-active-type" style="min-width:180px;">
              <option value="">Select type</option>
              ${state.slipGeometryTypes.map((entry) => `<option value="${esc(entry.id)}"${String(entry.id) === String(state.slipGeometryDraftTypeId || "") ? " selected" : ""}>${esc(entry.name)} (${esc(Number(entry.lengthFt).toFixed(1))}x${esc(Number(entry.widthFt).toFixed(1))})</option>`).join("")}
            </select>
          </label>
          <button id="slip-geom-place" type="button" class="btn primary"${state.slipGeometryDraftTypeId ? "" : " disabled"}>${activeGeometryPlaceMode ? "Click Map..." : "Place Polygon"}</button>
          <button id="slip-geom-place-line" type="button" class="btn primary">${lineLayoutActive ? "Layout Active..." : "Place Line"}</button>
          <button id="slip-geom-free-draw" type="button" class="btn primary">${freeDrawActive ? "Drawing..." : "Draw Shape"}</button>
          <button id="slip-geom-place-multislip" type="button" class="btn primary" title="Place side-tie multislip. Shift+click for width-first mode.">${
            multislipActive
              ? (multislipWidthFt !== 0
                  ? `${multislipPartitions} Slip${multislipPartitions > 1 ? "s" : ""} — W/X/↵`
                  : multislipP2 ? (multislipMode === "width" ? "Click Length..." : "Click Width...") : multislipP1 ? (multislipMode === "width" ? "Click Width End..." : "Click Dock End...") : (multislipMode === "width" ? "Click Width Start..." : "Click Dock Start..."))
              : "Place Multislip"
          }</button>
          <button id="slip-toggle-ids" type="button" class="btn">${state.showSlipIds ? "Hide Slip IDs" : "Show Slip IDs"}</button>
          <button id="slip-toggle-depths" type="button" class="btn${state.showDepths ? " primary" : ""}">${state.showDepths ? "Hide Depths" : "Show Depths"}</button>
          <label class="small">Color Slips By
            <select id="slip-color-by" style="min-width:150px;">
              <option value="none"${state.colorSlipsBy === "none" ? " selected" : ""}>None</option>
              <option value="dimension"${state.colorSlipsBy === "dimension" ? " selected" : ""}>Dimension</option>
              <option value="confidence"${state.colorSlipsBy === "confidence" ? " selected" : ""}>Confidence</option>
              <option value="space_type"${state.colorSlipsBy === "space_type" ? " selected" : ""}>Space Type</option>
            </select>
          </label>
          ${state.colorSlipsBy === "dimension" ? `
          <label class="small">Dimension Color Tolerance
            <select id="slip-dimension-color-tol" style="min-width:120px;">
              <option value="0.5"${Number(state.dimensionColorToleranceFt) === 0.5 ? " selected" : ""}>0.5 ft</option>
              <option value="1"${Number(state.dimensionColorToleranceFt) === 1 ? " selected" : ""}>1.0 ft</option>
            </select>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; min-width:160px;">
            <input id="slip-dimension-legend-toggle" type="checkbox" ${state.showDimensionLegend === false ? "" : "checked"} />
            Show Dimension Legend
          </label>
          ` : ""}
          <button id="slip-geom-delete-selected" type="button" class="btn"${selectedGeomCount ? "" : " disabled"}>Delete Selected</button>
          <button id="slip-geom-select-uncommitted" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Select Latest Uncommitted</button>
          <button id="slip-geom-clear-selection" type="button" class="btn"${selectedGeomCount ? "" : " disabled"}>Clear Selection</button>
          <button id="slip-geom-clear-all" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Clear All</button>
          <button id="slip-geom-purge-all" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Purge Stale</button>
        </div>
        ${state.colorSlipsBy === "confidence" ? `
          <div class="small" style="margin-top:6px; display:flex; gap:12px; align-items:center;">
            <span>Legend:</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#16a34a; margin-right:6px;"></span>High</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#f59e0b; margin-right:6px;"></span>Medium</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#dc2626; margin-right:6px;"></span>Low</span>
          </div>
        ` : ""}
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <button id="slip-geom-lock-toggle" type="button" class="btn"${selectedGeom ? "" : " disabled"}>${selectedGeom?.locked ? "Unlock" : "Lock"}</button>
          <label class="small">Number of Slips
            <input id="slip-geom-run-between" type="number" min="1" step="1" value="${esc(state.slipGeometryRunBetweenCount)}" style="width:90px;" />
          </label>
          <button id="slip-geom-layout-run" type="button" class="btn"${selectedGeom ? "" : " disabled"}>${geometryRunLayoutActive ? "Layout Active..." : "Layout Run"}</button>
          <button id="slip-geom-estimate-run-imagery" type="button" class="btn"${canEstimateGeometryImagery ? "" : " disabled"}>Detect Run From GeoTIFF</button>
          <button id="slip-geom-generate-run" type="button" class="btn"${geometryRunEndpoints.length >= 2 ? "" : " disabled"}>Generate Run</button>
          <button id="slip-geom-cancel-run" type="button" class="btn"${geometryRunLayoutActive ? "" : " disabled"}>Cancel Run</button>
          <button id="slip-geom-hotkey-map" type="button" class="btn">Hotkey Map</button>
          <button id="slip-geom-apply-report" type="button" class="btn primary"${records.length && state.slipGeometryPolygons.length ? "" : " disabled"}>Update Slip Report from Geometry</button>
          <label class="small">L/W Tolerance
            <select id="slip-geom-tolerance-lw">
              <option value="0.1"${Number(state.lengthWidthToleranceFt) === 0.1 ? " selected" : ""}>0.1 ft</option>
              <option value="0.25"${Number(state.lengthWidthToleranceFt) === 0.25 ? " selected" : ""}>0.25 ft</option>
              <option value="0.5"${Number(state.lengthWidthToleranceFt) === 0.5 ? " selected" : ""}>0.5 ft</option>
              <option value="1"${Number(state.lengthWidthToleranceFt) === 1 ? " selected" : ""}>1 ft</option>
            </select>
          </label>
          <label class="small">Rotation Tolerance
            <select id="slip-geom-tolerance-rot">
              <option value="0.1"${Number(state.rotationToleranceDeg) === 0.1 ? " selected" : ""}>0.1 deg</option>
              <option value="0.5"${Number(state.rotationToleranceDeg) === 0.5 ? " selected" : ""}>0.5 deg</option>
              <option value="1"${Number(state.rotationToleranceDeg) === 1 ? " selected" : ""}>1 deg</option>
            </select>
          </label>
        </div>
        <div style="margin-top:8px; display:grid; grid-template-columns:repeat(2,minmax(180px,1fr)); gap:8px;">
          <label class="small">Length: ${esc(Number(selectedGeom?.lengthFt ?? 0).toFixed(1))} ft
            <input id="slip-geom-slider-length" type="range" min="5" max="120" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.lengthFt ?? 40)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Width: ${esc(Number(selectedGeom?.widthFt ?? 0).toFixed(1))} ft
            <input id="slip-geom-slider-width" type="range" min="3" max="40" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.widthFt ?? 14)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Rotation: ${esc(Number(selectedGeom?.headingDeg ?? 0).toFixed(1))} deg
            <input id="slip-geom-slider-rotation" type="range" min="-180" max="180" step="${esc(rotationToleranceStep())}" value="${esc(selectedGeom?.headingDeg ?? 0)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Chamfer Angle: ${esc(Number(selectedGeom?.chamferAngleDeg ?? 0).toFixed(0))} deg
            <input id="slip-geom-slider-chamfer-angle" type="range" min="0" max="85" step="1" value="${esc(selectedGeom?.chamferAngleDeg ?? 0)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Chamfer Magnitude: ${esc(Number(selectedGeom?.chamferMagnitudeFt ?? 0).toFixed(1))} ft
            <input id="slip-geom-slider-chamfer-mag" type="range" min="0" max="20" step="0.5" value="${esc(selectedGeom?.chamferMagnitudeFt ?? 0)}" ${selectedGeom ? "" : "disabled"} />
          </label>
        </div>
        <div class="small" style="margin-top:6px;">Selection is intentional: click a Slip ID badge (or bottom table row) to select. Polygon body clicks are disabled to avoid accidental selection.</div>
        ${geometryRunLayoutActive ? `<div class="small" style="margin-top:4px;">${geometryRunEndpoints.length <= 1 ? "Run layout: selected polygon is start; click end point." : `Run preview ready with ${geometryRunTotalCount()} slips.`} Controls: QWE / ASD / ZXC + R/F (chamfer angle) + T/G (chamfer magnitude). Also: hold 1/2/3/4/5 and mouse-wheel (0.1ft/2°), add \` for coarse (1ft/5°), or Space for fine (0.01ft).</div>` : ""}
        ${geometryImageryEstimateStatus ? `<div class="small" style="margin-top:4px; color:#93c5fd;">${esc(geometryImageryEstimateStatus)}</div>` : ""}
        ${geomChartSignal.length ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,.18); border-radius:8px; background:rgba(15,23,42,.35);">
            <div class="small" style="margin-bottom:6px;">GeoTIFF run signal debug: blue line is sampled intensity along run centerline corridor; dashed orange lines are generated slip centers.</div>
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="display:block; width:100%; height:auto;">
              <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" rx="8" fill="rgba(15,23,42,0.5)" />
              <line x1="${chartPadding}" y1="${(chartHeight - chartPadding).toFixed(1)}" x2="${(chartWidth - chartPadding).toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(148,163,184,0.35)" stroke-width="1" />
              ${geomChartCenterLines}
              <path d="${geomChartPath}" fill="none" stroke="rgba(125,211,252,0.95)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
              ${geomChartDots}
            </svg>
          </div>
        ` : ""}
      </div>
      <div class="tool-section" style="margin-top:10px;">
        <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
          <div class="small">Selected Record</div>
          <div class="small">${esc(current ? current.measurement_id : "None selected")}</div>
        </div>
        ${current ? `
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Marina ID
              <div class="row" style="gap:8px; align-items:center;">
                <select id="slip-marina-id" style="flex:1;">
                  <option value="">Select marina</option>
                  ${currentOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === current.marina_id ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
                </select>
                <button id="slip-marina-zoom" type="button" class="btn"${currentMarina && Number.isFinite(currentMarina.lat) && Number.isFinite(currentMarina.lng) ? "" : " disabled"}>Zoom To</button>
              </div>
            </label>
            <label class="small" style="flex:1;">Marina Name
              <input id="slip-marina-name" type="text" value="${esc(currentMarina?.marinaName || current.marina_name)}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Slip ID
              <input id="slip-slip-id" type="text" value="${esc(current.slip_id)}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Linear Feet
              <input id="slip-linear-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_linear_feet))}" />
            </label>
            <label class="small" style="flex:1;">Width Feet
              <input id="slip-width-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_width_feet))}" />
            </label>
            <label class="small" style="flex:1;">Depth Feet
              <input id="slip-depth-feet" type="number" step="0.01" value="${esc(current.measured_depth_feet ?? "")}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small">Measurement Date
              <input id="slip-measurement-date" type="date" value="${esc(current.measurement_date)}" />
            </label>
            <label class="small">Confidence
              <select id="slip-confidence">
                <option value="High"${current.confidence_score === "High" ? " selected" : ""}>High</option>
                <option value="Medium"${current.confidence_score === "Medium" ? " selected" : ""}>Medium</option>
                <option value="Low"${current.confidence_score === "Low" ? " selected" : ""}>Low</option>
              </select>
            </label>
            <label class="small">Operational Status
              <select id="slip-operational-status">
                <option value="operable"${String(current.operational_status || "operable") === "operable" ? " selected" : ""}>operable</option>
                <option value="under_construction"${String(current.operational_status || "") === "under_construction" ? " selected" : ""}>under_construction</option>
                <option value="maintenance"${String(current.operational_status || "") === "maintenance" ? " selected" : ""}>maintenance</option>
                <option value="blocked"${String(current.operational_status || "") === "blocked" ? " selected" : ""}>blocked</option>
                <option value="closed"${String(current.operational_status || "") === "closed" ? " selected" : ""}>closed</option>
              </select>
            </label>
          </div>
          <div class="small" style="margin-top:8px;">Notes</div>
          <textarea id="slip-notes" style="min-height:54px;" placeholder="Optional notes (construction, access, restrictions, etc.)">${esc(current.notes || "")}</textarea>
          <div class="small" style="margin-top:8px;">Geometry WKT</div>
          <textarea id="slip-geometry-wkt" style="min-height:64px;">${esc(current.geometry_wkt || "")}</textarea>
          <div class="small" style="margin-top:8px;">Record Created Timestamp</div>
          <input id="slip-record-created-timestamp" type="text" value="${esc(current.record_created_timestamp || "")}" />
          <div class="row" style="margin-top:10px; gap:8px;">
            <button id="slip-save" type="button" class="btn primary">Save Record</button>
            <button id="slip-delete" type="button" class="btn">Delete Record</button>
            <button id="slip-show-selected" type="button" class="btn">Show on Map</button>
          </div>
        ` : `<div class="small" style="margin-top:8px;">Pick a record from the bottom table or click an inventory point on the map.</div>`}
      </div>
      <div class="small" style="margin-top:10px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
        </div>
      </details>
    `;
  }

  function onClick(event) {
    if (event.target?.closest?.("#slip-sort-slipid")) {
      if (String(state.tableSortKey || "slip_id") === "slip_id") {
        state.tableSortDir = String(state.tableSortDir || "asc") === "asc" ? "desc" : "asc";
      } else {
        state.tableSortKey = "slip_id";
        state.tableSortDir = "asc";
      }
      persist();
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-type-add")) {
      const name = String(containerEl?.querySelector("#slip-geom-type-name")?.value || "").trim() || "Standard";
      const lengthFt = Number(containerEl?.querySelector("#slip-geom-type-length")?.value || 40);
      const widthFt = Number(containerEl?.querySelector("#slip-geom-type-width")?.value || 14);
      createSlipType({ name, lengthFt, widthFt });
      status = `Added slip type ${name}.`;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-place")) {
      if (activeGeometryPlaceMode) {
        stopGeometryPlaceMode();
        status = "Polygon placement stopped.";
      } else {
        startGeometryPlaceMode();
      }
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-line")) {
      startLineLayout("endpoint");
      return;
    }
    if (event.target?.closest?.("#slip-geom-free-draw")) {
      if (freeDrawActive) {
        stopFreeDrawMode("cancelled");
      } else {
        startFreeDrawMode();
      }
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-multislip")) {
      if (multislipActive) { stopMultislipMode(); status = "Multislip placement cancelled."; render(); }
      else { startMultislipMode(!!event.shiftKey); }
      return;
    }
    if (event.target?.closest?.("#slip-toggle-ids")) {
      state.showSlipIds = !state.showSlipIds;
      persist();
      render();
      return;
    }
    if (event.target?.closest?.("#slip-toggle-depths")) {
      state.showDepths = !state.showDepths;
      persist();
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-clear-all")) {
      const removed = clearUncommittedGeometryPolygons();
      status = removed
        ? `Cleared ${removed} uncommitted slip geometry polygon${removed === 1 ? "" : "s"}.`
        : "No uncommitted geometry polygons to clear.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-delete-selected")) {
      removeSelectedGeometryPolygon({ deleteLinkedRecords: true }).then((removed) => {
        status = removed ? `Deleted ${removed} selected slip geometry polygon${removed === 1 ? "" : "s"}.` : "No selected polygon to delete.";
        render();
      }).catch((error) => {
        status = error?.message || "Failed to delete selected polygon(s).";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-commit-selected")) {
      commitSelectedPolygonAndPersist().catch((error) => {
        status = error?.message || "Failed to create slip record from selected polygon.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-select-uncommitted")) {
      const ok = selectLatestUncommittedGeometry();
      if (!ok) {
        status = "No uncommitted polygons found.";
        render();
      }
      return;
    }
    if (event.target?.closest?.("#slip-geom-clear-selection")) {
      clearGeometrySelection();
      status = "Cleared geometry selection.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-purge-all")) {
      const removed = purgeStaleGeometryPolygons();
      status = removed
        ? `Purged ${removed} stale slip geometry polygon${removed === 1 ? "" : "s"}.`
        : "No stale slip geometry polygons found.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-hotkey-map")) {
      const msg = [
        "Slip Geometry Hotkeys",
        "",
        "Q / E: Rotate -/+",
        "A / D: Length -/+",
        "Z / C: Width -/+",
        "Arrow Keys: Nudge selected slip",
        "W / X: Number of slips + / -",
        "S: Start run layout / Generate run",
        "Enter: Commit selected polygon as a single slip",
        "R / F: Chamfer angle -/+",
        "T / G: Chamfer magnitude -/+",
        "Delete/Backspace: Delete selected polygon(s)",
        "Esc: Cancel active run layout",
        "",
        "Mouse Wheel (hold 1-5 + wheel): adjust length/width/rotation/chamfer",
        "  + ` (backtick): coarse step (1ft / 5°)",
        "  + Space: fine step (0.01ft / tolerance step)",
        "  (default: 0.1ft / 2°)",
      ].join("\n");
      window.alert(msg);
      return;
    }
    if (event.target?.closest?.("#slip-geom-lock-toggle")) {
      const selected = selectedGeometryPolygon();
      if (selected) {
        updateSelectedGeometry({ locked: !selected.locked });
        status = selected.locked ? "Polygon unlocked." : "Polygon locked.";
      }
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-layout-run")) {
      startGeometryRunLayout();
      geometryImageryEstimateStatus = "";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-estimate-run-imagery")) {
      estimateGeometryRunFromImagery().catch((error) => {
        geometryImageryEstimateStatus = error?.message || "GeoTIFF run estimate failed.";
        app.ui.toast(geometryImageryEstimateStatus);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-generate-run")) {
      if (geometryRunEndpoints.length < 2) return;
      generateRunAndPersist().catch((error) => {
        status = error?.message || "Failed to generate run records.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-cancel-run")) {
      stopGeometryRunLayout("cancelled");
      geometryImageryEstimateStatus = "";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-apply-report")) {
      applyGeometryToSlipRecords().catch((error) => {
        status = error?.message || "Failed to apply geometry to slip report.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-default-marina-zoom")) {
      fitMarinaById(state.marinaId, state.marinaName);
      return;
    }
    if (event.target?.closest?.("#slip-marina-zoom")) {
      const current = selectedRecord();
      if (!current) return;
      fitMarinaById(String(containerEl?.querySelector("#slip-marina-id")?.value || current.marina_id), current.marina_name);
      return;
    }
    if (event.target?.closest?.("#slip-drop-point")) {
      startPointDrop();
      return;
    }
    if (event.target?.closest?.("#slip-stop-drop")) {
      stopPointDrop("cancelled");
      return;
    }
    if (event.target?.closest?.("#slip-line-layout")) {
      startLineLayout("endpoint");
      return;
    }
    if (event.target?.closest?.("#slip-spacing-run-layout")) {
      startLineLayout("spacing");
      return;
    }
    if (event.target?.closest?.("#slip-line-clear")) {
      lineEndpoints = [];
      lineHoverPoint = null;
      lineLayoutMode = "endpoint";
      imageryEstimateStatus = "";
      imageryEstimateDebug = null;
      stopLineLayout("cleared");
      status = "Line preview cleared.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-line-estimate-imagery")) {
      if (lineLayoutMode === "spacing") return;
      estimateLineSlipCountFromImagery().catch((error) => {
        imageryEstimateStatus = error?.message || "Imagery estimate failed.";
        app.ui.toast(imageryEstimateStatus);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-line-generate")) {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (!canGenerate) return;
      const points = linePreviewPoints();
      createRecordsFromPoints(points).then(() => {
        imageryEstimateStatus = "";
        imageryEstimateDebug = null;
        stopLineLayout("completed");
        app.ui.toast(`Generated ${points.length} slips from line preview.`);
      }).catch((error) => {
        status = error?.message || "Failed to generate line slips.";
        app.ui.toast(status);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-line-commit-feature")) {
      commitLineFeatureFromCurrentLine().catch((error) => {
        status = error?.message || "Failed to commit side-tie line feature.";
        render();
      });
      return;
    }
    const exportActionButton = event.target?.closest?.("[data-slip-export]");
    if (exportActionButton) {
      const details = exportActionButton.closest("details");
      if (details) details.open = false;
      runSlipExport(exportActionButton.getAttribute("data-slip-export")).catch((err) => alert("Export failed: " + (err?.message || err)));
      return;
    }
    if (event.target?.closest?.("#slip-refresh")) {
      refreshRecords().catch(() => {});
      return;
    }
    if (event.target?.closest?.("#slip-export-csv")) {
      exportCsv();
      return;
    }
    if (event.target?.closest?.("#slip-export-xls")) {
      exportXls().catch((err) => alert(`XLS export failed: ${err.message}`));
      return;
    }
    if (event.target?.closest?.("#slip-export-json")) {
      exportJson();
      return;
    }
    if (event.target?.closest?.("#slip-export-geojson")) {
      exportGeoJson();
      return;
    }
    if (event.target?.closest?.("#slip-fill-depths-run")) {
      const sel = containerEl?.querySelector("#slip-depth-surface-select");
      const surfaceId = sel?.value || "";
      if (!surfaceId || !filterMarinaId) return;
      const btn = event.target.closest("#slip-fill-depths-run");
      const statusEl = containerEl?.querySelector("#slip-fill-depths-status");
      btn.disabled = true;
      if (statusEl) statusEl.textContent = "Running…";
      fetch("/api/surfaces/populate-slip-depths", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ surfaceId, marina: filterMarinaId }),
      }).then(r => r.json()).then(data => {
        if (statusEl) statusEl.textContent = data.ok
          ? `Done: ${data.updated} updated, ${data.nodata} no data, ${data.errors} errors (${data.total} slips)`
          : `Error: ${data.error}`;
        if (data.ok && data.updated > 0) refreshRecords().catch(() => {});
      }).catch(err => {
        if (statusEl) statusEl.textContent = `Error: ${err.message}`;
      }).finally(() => { btn.disabled = false; });
      return;
    }
    if (event.target?.closest?.("#slip-save")) {
      saveSelectedRecord().catch((error) => {
        status = error?.message || "Failed to save record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-delete")) {
      deleteSelectedRecord().catch((error) => {
        status = error?.message || "Failed to delete record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-show-selected")) {
      fitSelection();
    }
  }

  function onInput(event) {
    const id = String(event.target?.id || "");
    if (id === "slip-marina-id") {
      const current = selectedRecord();
      if (!current) return;
      const marina = marinaOptionForId(String(event.target?.value || ""), current.marina_name);
      const nameInput = containerEl?.querySelector("#slip-marina-name");
      if (nameInput) nameInput.value = marina?.marinaName || "";
      return;
    }
    if (id === "slip-geom-active-type") {
      state.slipGeometryDraftTypeId = String(event.target?.value || "");
      persist();
      render();
      return;
    }
    if (id === "slip-color-by") {
      const next = String(event.target?.value || "none").toLowerCase();
      state.colorSlipsBy = ["none", "dimension", "confidence", "space_type"].includes(next) ? next : "none";
      persist();
      render();
      return;
    }
    if (id === "slip-display-measurement-tol") {
      state.displayMeasurementToleranceFt = normalizeDisplayToleranceFt(event.target?.value);
      if (state.filterHiddenValues?.dimension?.length) state.filterHiddenValues = { ...state.filterHiddenValues, dimension: [] };
      persist();
      renderGeometry();
      render();
      return;
    }
    if (id === "slip-confidence") {
      const current = selectedRecord();
      if (!current) return;
      const next = String(event.target?.value || "High");
      records = records.map((record) => (
        String(record?.measurement_id || "") === String(current.measurement_id || "")
          ? { ...record, confidence_score: next }
          : record
      )); recordsVersion++;
      renderMap();
      renderBottomPanel();
      return;
    }
    if (id === "slip-dimension-color-tol") {
      const next = Number(event.target?.value);
      state.dimensionColorToleranceFt = [0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-dimension-legend-toggle") {
      state.showDimensionLegend = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-run-between") {
      state.slipGeometryRunBetweenCount = Math.max(1, Number(event.target?.value || 1) || 1);
      persist();
      render();
      return;
    }
    if (id === "slip-geom-tolerance-lw") {
      const next = Number(event.target?.value || 0.5);
      state.lengthWidthToleranceFt = [0.1, 0.25, 0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-tolerance-rot") {
      const next = Number(event.target?.value || 0.5);
      state.rotationToleranceDeg = [0.1, 0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-slider-length") {
      updateSelectedGeometry({ lengthFt: snapLengthWidth(event.target?.value) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-width") {
      updateSelectedGeometry({ widthFt: snapLengthWidth(event.target?.value) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-rotation") {
      updateSelectedGeometry({ headingDeg: roundToStep(event.target?.value, rotationToleranceStep(), -180) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-chamfer-angle") {
      updateSelectedGeometry({ chamferAngleDeg: Number(event.target?.value || 0) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-chamfer-mag") {
      updateSelectedGeometry({ chamferMagnitudeFt: Number(event.target?.value || 0) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-default-measurement-date") state.measurementDate = String(event.target?.value || "");
    if (id === "slip-default-confidence") state.confidenceScore = String(event.target?.value || "High");
    if (id === "slip-default-dock") state.dockName = String(event.target?.value || "");
    if (id === "slip-default-slipid-separator") state.slipIdSeparator = String(event.target?.value || "-");
    if (id === "slip-default-slipid-part3") state.slipIdPart3 = String(event.target?.value || "");
    if (id === "slip-default-slipid-autoinc") state.slipIdAutoIncrement = !!event.target?.checked;
    if (id === "slip-default-slipid-step") state.slipIdIncrementBy = Math.max(1, Number(event.target?.value || 1) || 1);
    if (id === "slip-default-multislip") draftMultiSlip = !!event.target?.checked;
    if (id === "slip-default-multislip-id") draftMultiSlipId = String(event.target?.value || "");
    if (id === "slip-default-covered") draftCovered = !!event.target?.checked;
    if (id === "slip-default-space-type") state.spaceType = String(event.target?.value || "");
    if (id === "slip-default-boatlift") draftBoatlift = !!event.target?.checked;
    if (id === "slip-default-fueldock") draftFuelDock = !!event.target?.checked;
    if (id === "slip-default-comments") draftComments = String(event.target?.value || "");
    if (id === "slip-default-line-count") {
      state.lineSlipCount = Math.max(1, Number(event.target?.value || 1) || 1);
      if (lineAnchorPoint && Number(lineAnchorPoint.index) >= state.lineSlipCount - 1) lineAnchorPoint = null;
    }
    if (id.startsWith("slip-default-slipid-")) {
      persist();
      return;
    }
    if (id === "slip-default-line-count") {
      persist();
      renderPreview();
      render();
      return;
    }
    if (id) persist();
  }

  function bindBottomPanelControls() {
    const host = document.getElementById("slip-inventory-bottom");
    if (!host) return;
    const bottomTableAnchors = () => {
      const panelView = host.closest(".bottom-panel-view");
      const catDetails = tablePanelEl?.closest?.("details[data-dock-name]");
      const catContent = catDetails?.querySelector?.(".dock-category-content") || null;
      const toolDet = tablePanelEl?.closest?.("details[data-tool-id]");
      return { panelView, catDetails, catContent, toolDet };
    };
    const clearClosedTableLayout = () => {
      const { catDetails, catContent, toolDet } = bottomTableAnchors();
      [catDetails, catContent, toolDet, tablePanelEl, host].filter(Boolean).forEach((el) => {
        ["height", "flex", "minHeight", "overflow", "display", "flexDirection"].forEach((prop) => {
          el.style[prop] = "";
        });
      });
      const scrollEl = host.querySelector("#slip-bottom-table-scroll");
      if (scrollEl) {
        ["height", "maxHeight", "flex", "minHeight"].forEach((prop) => { scrollEl.style[prop] = ""; });
      }
    };
    const sizeBottomTableViewport = () => {
      const { panelView, catDetails, toolDet } = bottomTableAnchors();
      const bottomDockRoot = tablePanelEl?.closest?.('[data-dock-root="bottom"]') || null;
      const scrollEl = host.querySelector("#slip-bottom-table-scroll");
      if (!panelView || !bottomDockRoot || !scrollEl || (catDetails && !catDetails.open) || (toolDet && !toolDet.open)) return;
      const boundsHost = panelView.closest(".bottom-panel-body") || panelView.closest(".panel-content") || panelView;
      const bounds = boundsHost.getBoundingClientRect();
      const scrollTop = scrollEl.getBoundingClientRect().top;
      const nextHeight = Math.max(180, Math.floor(bounds.bottom - scrollTop - 10));
      Object.assign(scrollEl.style, {
        flex: "0 0 auto",
        minHeight: "0",
        height: `${nextHeight}px`,
        maxHeight: `${nextHeight}px`,
      });
    };
    const applyBottomTableLayout = () => {
      const { panelView, catDetails, catContent, toolDet } = bottomTableAnchors();
      if (!panelView) return;
      const bottomDockRoot = tablePanelEl?.closest?.('[data-dock-root="bottom"]') || null;
      if (bottomDockRoot) {
        if (catDetails && !catDetails.open) catDetails.open = true;
        if (toolDet && !toolDet.open) toolDet.open = true;
        bottomDockRoot.querySelectorAll(":scope > .tool-drop-end-zone").forEach((zone) => {
          Object.assign(zone.style, { flex:"0 0 0", minHeight:"0", height:"0", overflow:"hidden" });
        });
      }
      if ((catDetails && !catDetails.open) || (toolDet && !toolDet.open)) {
        clearClosedTableLayout();
        return;
      }
      // Build a flex chain from .bottom-panel-view down so #slip-inventory-bottom fills the panel.
      // height:100% on inner elements can't work when <details> ancestors are auto-height;
      // flex:1 at every level lets CSS handle resizing without JS measurement.
      const bottomBody = panelView.closest(".bottom-panel-body");
      const bottomStack = panelView.closest(".bottom-panel-stack");
      const panelContent = panelView.closest(".panel-content");
      if (panelContent) Object.assign(panelContent.style, { height:"100%", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      if (bottomStack) Object.assign(bottomStack.style, { height:"100%", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      if (bottomBody) Object.assign(bottomBody.style, { flex:"1", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      Object.assign(panelView.style, { height:"100%", overflow:"hidden", display:"flex", flexDirection:"column" });
      const sidebar = panelView.querySelector("#bottom-toolset-sidebar");
      if (sidebar) Object.assign(sidebar.style, { height:"100%", flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (catDetails) Object.assign(catDetails.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (catContent) Object.assign(catContent.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (toolDet) Object.assign(toolDet.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (tablePanelEl) Object.assign(tablePanelEl.style, { flex:"1", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      Object.assign(host.style, { flex:"1", minHeight:"0", height:"100%", overflow:"hidden" });
      requestAnimationFrame(sizeBottomTableViewport);
    };
    // renderBottomPanelHost calls onShow() on every re-render (tab switches, etc.),
    // not only when content is replaced. Guard against stacking listeners.
    if (host.dataset.listenersBound === "1") {
      applyBottomTableLayout();
      return;
    }
    host.dataset.listenersBound = "1";
    applyBottomTableLayout();
    const { catDetails, toolDet } = bottomTableAnchors();
    [catDetails, toolDet].filter(Boolean).forEach((details) => {
      details.addEventListener("toggle", () => {
        requestAnimationFrame(applyBottomTableLayout);
      });
    });
    if (!tablePanelResizeObserver && typeof ResizeObserver !== "undefined") {
      tablePanelResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(sizeBottomTableViewport);
      });
      [host.closest(".bottom-panel-body"), host.closest(".panel-content"), tablePanelEl].filter(Boolean).forEach((el) => {
        tablePanelResizeObserver.observe(el);
      });
    }
    const scrollEl = host.querySelector("#slip-bottom-table-scroll");
    if (scrollEl) {
      const targetScrollTop = Number(bottomTableScrollTop || 0);
      if (Math.abs(Number(scrollEl.scrollTop || 0) - targetScrollTop) > 1) scrollEl.scrollTop = targetScrollTop;
      scrollEl.addEventListener("pointerenter", () => {
        bottomTablePointerInside = true;
      });
      scrollEl.addEventListener("pointerleave", () => {
        bottomTablePointerInside = false;
        bottomTableScrollActiveUntil = Date.now() + 1500;
      });
      scrollEl.addEventListener("scroll", () => {
        bottomTableScrollTop = Number(scrollEl.scrollTop || 0);
        bottomTableScrollActiveUntil = Date.now() + 2500;
      }, { passive: true });
    }

    // Slip ID inline edit
    host.querySelectorAll("[data-slip-edit-field='slip_id']").forEach((input) => {
      input.addEventListener("click", (event) => { event.stopPropagation(); });
      input.addEventListener("keydown", (event) => {
        event.stopPropagation();
        if (event.key === "Enter") {
          event.preventDefault();
          const measurementId = String(input.getAttribute("data-slip-edit-id") || "");
          saveSlipIdFromTable(measurementId, String(input.value || "")).catch((error) => {
            status = error?.message || "Failed to update slip ID."; render();
          });
          input.blur();
        }
      });
      input.addEventListener("blur", () => {
        const measurementId = String(input.getAttribute("data-slip-edit-id") || "");
        saveSlipIdFromTable(measurementId, String(input.value || "")).catch((error) => {
          status = error?.message || "Failed to update slip ID."; render();
        });
      });
    });

    host.querySelector("#slip-show-no-marina")?.addEventListener("change", (e) => {
      showNoMarinaCode = e.target.checked;
      refreshRecords({ quiet: true }).catch(() => {});
    });
    host.querySelector("#slip-bottom-refresh")?.addEventListener("click", () => { refreshRecords().catch(() => {}); });
    host.querySelectorAll("[data-slip-export]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const details = btn.closest("details");
        if (details) details.open = false;
        runSlipExport(btn.getAttribute("data-slip-export"), btn).catch((err) => alert("Export failed: " + (err?.message || err)));
      });
    });
    host.querySelector("#slip-bottom-export-csv")?.addEventListener("click", () => { exportCsv(); });
    host.querySelector("#slip-bottom-export-xls")?.addEventListener("click", () => { exportXls().catch((err) => alert(`XLS export failed: ${err.message}`)); });
    host.querySelector("#slip-bottom-export-plot")?.addEventListener("click", async (e) => {
      if (_plotExporting) return;
      _plotExporting = true;
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.textContent = "Fetching…";
      try { await exportPlotSvg(btn); } catch (err) { alert(`Plot export failed: ${err.message}`); }
      _plotExporting = false;
      btn.disabled = false;
      btn.textContent = "PDF Plot";
    });
    host.querySelector("#slip-display-measurement-tol")?.addEventListener("change", (event) => {
      state.displayMeasurementToleranceFt = normalizeDisplayToleranceFt(event.target?.value);
      if (state.filterHiddenValues?.dimension?.length) state.filterHiddenValues = { ...state.filterHiddenValues, dimension: [] };
      persist(); renderGeometry(); render();
    });
    host.querySelector("#slip-bottom-show-selected")?.addEventListener("click", () => { fitSelection(); });
    host.querySelector("#slip-sort-slipid")?.addEventListener("click", () => {
      state.tableSortDir = state.tableSortDir === "asc" ? "desc" : "asc";
      persist(); renderBottomPanel();
    });

    // Row selection — plain click on non-interactive area, OR modifier+click anywhere in the row.
    // Capture-phase mousedown intercepts Ctrl/Shift+click before any cell handler fires,
    // allowing row selection without activating the cell underneath.
    function applyRowSelection(event, row) {
      const clickedMeasurementId = String(row.getAttribute("data-slip-row") || "");
      const clickedGeomId = String(row.getAttribute("data-geom-id") || "");
      const ctrl = !!(event.ctrlKey || event.metaKey);
      const shift = !!event.shiftKey;
      const allRows = [...host.querySelectorAll("[data-slip-row]")];
      const previousMeasurementId = String(state.selectedMeasurementId || "");
      state.selectedMeasurementId = clickedMeasurementId;

      if (shift && previousMeasurementId) {
        const from = allRows.findIndex((el) => String(el.getAttribute("data-slip-row") || "") === previousMeasurementId);
        const to = allRows.findIndex((el) => String(el.getAttribute("data-slip-row") || "") === clickedMeasurementId);
        if (from >= 0 && to >= 0) {
          const [a, b] = from <= to ? [from, to] : [to, from];
          const rangeMids = allRows.slice(a, b + 1).map((el) => String(el.getAttribute("data-slip-row") || "")).filter(Boolean);
          const rangeGeomIds = allRows.slice(a, b + 1).map((el) => String(el.getAttribute("data-geom-id") || "")).filter(Boolean);
          if (ctrl) {
            rangeMids.forEach((mid) => tableSelectedMeasurementIds.add(mid));
          } else {
            tableSelectedMeasurementIds = new Set(rangeMids);
          }
          if (rangeGeomIds.length) {
            if (ctrl) {
              const merged = [...new Set([...getSelectedGeometryIds(), ...rangeGeomIds])];
              setSelectedGeometryIds(merged, clickedGeomId || merged[0] || "");
            } else {
              setSelectedGeometryIds(rangeGeomIds, clickedGeomId || rangeGeomIds[0] || "");
            }
          } else if (!ctrl) { clearGeometrySelection(); }
        }
      } else if (ctrl) {
        // Ctrl+click: toggle this row in the table selection set
        if (tableSelectedMeasurementIds.has(clickedMeasurementId)) {
          tableSelectedMeasurementIds.delete(clickedMeasurementId);
        } else {
          tableSelectedMeasurementIds.add(clickedMeasurementId);
        }
        if (clickedGeomId) applyGeometrySelection(clickedGeomId, { toggle: true });
      } else {
        // Plain click: single selection
        tableSelectedMeasurementIds = new Set([clickedMeasurementId]);
        if (clickedGeomId) applyGeometrySelection(clickedGeomId, { toggle: false });
        else clearGeometrySelection();
      }
      persist(); render();
    }

    // Capture-phase: runs before cell handlers. When modifier held, handle selection
    // and block cell interaction (preventDefault stops input focus / checkbox toggle).
    const tableScroll = host.querySelector("#slip-bottom-table-scroll");
    if (tableScroll) {
      tableScroll.addEventListener("mousedown", (event) => {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return;
        const row = event.target?.closest?.("[data-slip-row]");
        if (!row) return;
        event.preventDefault();
        applyRowSelection(event, row);
      }, true);
      tableScroll.addEventListener("click", (event) => {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return;
        const row = event.target?.closest?.("[data-slip-row]");
        if (!row) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      // Toggle select-mode class for visual cursor feedback — remove old handler first to prevent accumulation
      if (_selectModeHandler) {
        document.removeEventListener("keydown", _selectModeHandler);
        document.removeEventListener("keyup", _selectModeHandler);
      }
      _selectModeHandler = (e) => tableScroll.classList.toggle("select-mode", !!(e.ctrlKey || e.metaKey || e.shiftKey));
      document.addEventListener("keydown", _selectModeHandler);
      document.addEventListener("keyup", _selectModeHandler);
    }

    // Plain click on non-interactive row area (no modifier needed)
    host.querySelectorAll("[data-slip-row]").forEach((row) => {
      row.addEventListener("click", (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey) return; // handled by capture above
        if (event.target?.closest?.("[data-slip-binary],[data-slip-select],[data-slip-text],[data-slip-edit-id]")) return;
        applyRowSelection(event, row);
      });
    });

    // Binary checkbox changes — applies to all selected rows when multi-selected
    host.querySelectorAll("[data-slip-binary]").forEach((cb) => {
      cb.addEventListener("click", (e) => e.stopPropagation());
      cb.addEventListener("change", () => {
        const fieldKey = String(cb.getAttribute("data-slip-binary") || "");
        const measurementId = String(cb.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        saveFieldForSelected(measurementId, fieldKey, cb.checked ? "yes" : "no").catch(() => {});
      });
    });

    // Dropdown select changes — applies to all selected rows when multi-selected
    host.querySelectorAll("[data-slip-select]").forEach((sel) => {
      sel.addEventListener("click", (e) => e.stopPropagation());
      sel.addEventListener("change", () => {
        const fieldKey = String(sel.getAttribute("data-slip-select") || "");
        const measurementId = String(sel.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        saveFieldForSelected(measurementId, fieldKey, sel.value || "").catch(() => {});
      });
    });

    // Text / number / date inputs — applies to all selected rows when multi-selected
    host.querySelectorAll("[data-slip-text]").forEach((input) => {
      input.addEventListener("click", (e) => e.stopPropagation());
      input.addEventListener("focus", () => {
        // Sync left-panel + row highlight to the record being edited, without disrupting the
        // focused input. We defer the render until after this focus event completes so the
        // browser doesn't lose the caret position.
        const measurementId = String(input.getAttribute("data-slip-meas") || "");
        if (!measurementId || state.selectedMeasurementId === measurementId) return;
        state.selectedMeasurementId = measurementId;
        tableSelectedMeasurementIds = new Set([measurementId]);
        const row = input.closest("[data-slip-row]");
        const geomId = String(row?.getAttribute("data-geom-id") || "");
        if (geomId) setSelectedGeometryIds([geomId], geomId);
        else clearGeometrySelection();
        persist();
        // Defer render so focus is not disrupted by DOM replacement
        requestAnimationFrame(() => render());
      });
      input.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") { e.preventDefault(); input.blur(); }
      });
      input.addEventListener("blur", () => {
        const fieldKey = String(input.getAttribute("data-slip-text") || "");
        const measurementId = String(input.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        const current = records.find((r) => r.measurement_id === measurementId);
        if (!current) return;
        const newRaw = String(input.value ?? "").trim();
        const prevRaw = String(current[fieldKey] ?? "").trim();
        if (newRaw === prevRaw) return;
        saveFieldForSelected(measurementId, fieldKey, newRaw || null).catch(() => {});
      });
    });

    // Column config panel open/close
    host.querySelector("#slip-col-cfg-open")?.addEventListener("click", () => {
      const card = host.querySelector(".card");
      if (!card) return;
      const existing = card.querySelector("#slip-col-cfg-panel");
      if (existing) { existing.remove(); return; }
      const cfg = readColCfg();
      card.insertAdjacentHTML("beforeend", renderColCfgPanel(cfg));
      bindColCfgPanel(card, cfg);
    });

    // Color By select in table header — synced with sidebar
    host.querySelector("#slip-color-by-table")?.addEventListener("change", (e) => {
      const next = String(e.target?.value || "none").toLowerCase();
      state.colorSlipsBy = ["none", "dimension", "confidence", "space_type"].includes(next) ? next : "none";
      persist();
      renderGeometry();
      render();
    });

    // Filter panel toggle + checkbox handlers
    if (_filterPanelCloseHandler) {
      document.removeEventListener("click", _filterPanelCloseHandler);
      _filterPanelCloseHandler = null;
    }
    const filterToggleBtn = host.querySelector("#slip-filter-toggle");
    const filterPanelEl = host.querySelector("#slip-filter-panel");
    if (filterToggleBtn && filterPanelEl) {
      // Restore open state if panel was open before a re-render triggered by a filter change
      if (_filterPanelShouldBeOpen) filterPanelEl.style.display = "block";
      filterToggleBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const opening = filterPanelEl.style.display === "none";
        filterPanelEl.style.display = opening ? "block" : "none";
        _filterPanelShouldBeOpen = opening;
      });
      _filterPanelCloseHandler = (e) => {
        if (!filterPanelEl.isConnected) { document.removeEventListener("click", _filterPanelCloseHandler); _filterPanelCloseHandler = null; return; }
        if (!filterPanelEl.contains(e.target) && e.target !== filterToggleBtn) { filterPanelEl.style.display = "none"; _filterPanelShouldBeOpen = false; }
      };
      document.addEventListener("click", _filterPanelCloseHandler);
      filterPanelEl.querySelector("#slip-filter-select-all")?.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!state.filterHiddenValues) state.filterHiddenValues = {};
        state.filterHiddenValues[state.colorSlipsBy] = [];
        _filterPanelShouldBeOpen = true;
        persist(); renderGeometry(); render();
      });
      filterPanelEl.querySelector("#slip-filter-deselect-all")?.addEventListener("click", (e) => {
        e.stopPropagation();
        const allVals = [...filterPanelEl.querySelectorAll("[data-slip-filter-val]")].map((cb) => String(cb.getAttribute("data-slip-filter-val") || "")).filter(Boolean);
        if (!state.filterHiddenValues) state.filterHiddenValues = {};
        state.filterHiddenValues[state.colorSlipsBy] = allVals;
        _filterPanelShouldBeOpen = true;
        persist(); renderGeometry(); render();
      });
      filterPanelEl.querySelectorAll("[data-slip-filter-val]").forEach((cb) => {
        cb.addEventListener("change", (e) => {
          e.stopPropagation();
          const val = String(cb.getAttribute("data-slip-filter-val") || "");
          if (!val) return;
          if (!state.filterHiddenValues) state.filterHiddenValues = {};
          const arr = Array.isArray(state.filterHiddenValues[state.colorSlipsBy]) ? [...state.filterHiddenValues[state.colorSlipsBy]] : [];
          if (cb.checked) {
            state.filterHiddenValues[state.colorSlipsBy] = arr.filter((v) => v !== val);
          } else {
            if (!arr.includes(val)) arr.push(val);
            state.filterHiddenValues[state.colorSlipsBy] = arr;
          }
          _filterPanelShouldBeOpen = true;
          persist(); renderGeometry(); render();
        });
      });
    }

    // Column header resize
    host.querySelectorAll(".slip-col-resize").forEach((grip) => {
      grip.addEventListener("mousedown", (startEvt) => {
        startEvt.preventDefault(); startEvt.stopPropagation();
        const key = String(grip.getAttribute("data-resize-key") || "");
        if (!key) return;
        const th = grip.closest("th");
        if (!th) return;
        const startX = startEvt.clientX;
        const startW = th.offsetWidth;
        const onMove = (moveEvt) => {
          const newW = Math.max(40, startW + (moveEvt.clientX - startX));
          th.style.width = newW + "px";
        };
        const onUp = (upEvt) => {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
          const newW = Math.max(40, startW + (upEvt.clientX - startX));
          const cfg = readColCfg();
          cfg.widths[key] = newW;
          writeColCfg(cfg);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });
    });

    // Column header drag-to-reorder
    let dragSrcKey = null;
    host.querySelectorAll("th[data-col-key]").forEach((th) => {
      th.addEventListener("dragstart", (e) => {
        dragSrcKey = th.getAttribute("data-col-key");
        e.dataTransfer.effectAllowed = "move";
        th.style.opacity = "0.5";
      });
      th.addEventListener("dragend", () => { th.style.opacity = ""; dragSrcKey = null; });
      th.addEventListener("dragover", (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; });
      th.addEventListener("drop", (e) => {
        e.preventDefault();
        const dropKey = th.getAttribute("data-col-key");
        if (!dragSrcKey || dragSrcKey === dropKey) return;
        const cfg = readColCfg();
        const fromIdx = cfg.order.indexOf(dragSrcKey);
        const toIdx = cfg.order.indexOf(dropKey);
        if (fromIdx < 0 || toIdx < 0) return;
        cfg.order.splice(fromIdx, 1);
        cfg.order.splice(toIdx, 0, dragSrcKey);
        writeColCfg(cfg);
        renderBottomPanel();
      });
    });
  }

  function bindColCfgPanel(card, cfg) {
    const panel = card.querySelector("#slip-col-cfg-panel");
    if (!panel) return;

    function reopenColCfgPanel() {
      const newCard = document.querySelector("#slip-inventory-bottom .card");
      if (!newCard) return;
      newCard.querySelector("#slip-col-cfg-panel")?.remove();
      const newCfg = readColCfg();
      newCard.insertAdjacentHTML("beforeend", renderColCfgPanel(newCfg));
      bindColCfgPanel(newCard, newCfg);
    }

    // Close — force table re-render so col cfg changes are applied
    panel.querySelector("#slip-col-cfg-close")?.addEventListener("click", () => {
      panel.remove();
      _lastPanelKey = "";
      renderBottomPanel();
    });

    // Reset all to defaults
    panel.querySelector("#slip-col-cfg-reset-all")?.addEventListener("click", () => {
      try { localStorage.removeItem(COL_CFG_KEY); } catch {}
      panel.remove();
      _lastPanelKey = "";
      renderBottomPanel();
    });

    // Toggle visibility
    panel.querySelectorAll("[data-cfg-toggle]").forEach((cb) => {
      cb.addEventListener("change", () => {
        const key = String(cb.getAttribute("data-cfg-toggle") || "");
        if (!key) return;
        const fresh = readColCfg();
        if (cb.checked) fresh.hidden.delete(key); else fresh.hidden.add(key);
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });

    // Reset individual column width
    panel.querySelectorAll("[data-cfg-reset-width]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = String(btn.getAttribute("data-cfg-reset-width") || "");
        if (!key) return;
        const fresh = readColCfg();
        delete fresh.widths[key];
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });

    // Drag-to-reorder rows in config panel
    let cfgDragSrc = null;
    panel.querySelectorAll(".slip-col-cfg-row").forEach((row) => {
      row.addEventListener("dragstart", (e) => {
        cfgDragSrc = row.getAttribute("data-cfg-key");
        e.dataTransfer.effectAllowed = "move";
        row.style.background = "rgba(148,163,184,.1)";
      });
      row.addEventListener("dragend", () => { row.style.background = ""; cfgDragSrc = null; });
      row.addEventListener("dragover", (e) => { e.preventDefault(); row.style.background = "rgba(96,165,250,.15)"; });
      row.addEventListener("dragleave", () => { row.style.background = ""; });
      row.addEventListener("drop", (e) => {
        e.preventDefault();
        row.style.background = "";
        const dropKey = row.getAttribute("data-cfg-key");
        if (!cfgDragSrc || cfgDragSrc === dropKey) return;
        const fresh = readColCfg();
        const fromIdx = fresh.order.indexOf(cfgDragSrc);
        const toIdx = fresh.order.indexOf(dropKey);
        if (fromIdx < 0 || toIdx < 0) return;
        fresh.order.splice(fromIdx, 1);
        fresh.order.splice(toIdx, 0, cfgDragSrc);
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    if (!zoomHandler) {
      zoomHandler = () => renderMap();
      map.on("zoomend", zoomHandler);
    }
    if (!mapSelectClickHandler) {
      mapSelectClickHandler = (event) => {
        if (activeGeometryPlaceMode || geometryRunLayoutActive || lineLayoutActive || pointDropActive) return;
        if (Date.now() < Number(suppressMapClickUntil || 0)) return;
        const handledBy = String(event?.originalEvent?.__htHandled || "");
        if (event?.originalEvent?.defaultPrevented || handledBy.startsWith("sv360") || handledBy.startsWith("slip-inventory")) return;
        const hadSelection = getSelectedGeometryIds().length > 0 || !!state.selectedMeasurementId;
        clearGeometrySelection();
        state.selectedMeasurementId = "";
        if (!hadSelection) return;
        persist();
        render();
      };
      map.on("click", mapSelectClickHandler);
    }
    if (!globalRunHotkeyHandler) {
      globalRunHotkeyHandler = (event) => {
        if (shouldIgnoreGeometryHotkeys(event)) return;
        if (!activeGeometryPlaceMode && !geometryRunLayoutActive && !lineLayoutActive && !pointDropActive) {
          const nudgeHandled = nudgeSelectedGeometry(event.key);
          if (nudgeHandled) {
            event.preventDefault?.();
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            return;
          }
        }
        if (event.key === "Escape") {
          if (activeGeometryPlaceMode || geometryRunLayoutActive || lineLayoutActive || pointDropActive) return;
          const hadSelection = getSelectedGeometryIds().length > 0 || !!state.selectedMeasurementId;
          clearGeometrySelection();
          state.selectedMeasurementId = "";
          if (!hadSelection) return;
          persist();
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        const ctrlOrMeta = !!(event.ctrlKey || event.metaKey);
        if (ctrlOrMeta && String(event.key || "").toLowerCase() === "c") {
          const selected = selectedGeometryPolygon();
          if (!selected) return;
          const selectedRing = computeSlipPolygonLatLngs(selected);
          const inferred = inferPolyMetricsFromRing(selectedRing);
          const ringBacked = Array.isArray(selectedRing) && selectedRing.length >= 3 && !!inferred;
          copiedSlipGeometryTemplate = {
            lengthFt: Number(ringBacked ? inferred.lengthFt : (selected.lengthFt || 40)),
            widthFt: Number(ringBacked ? inferred.widthFt : (selected.widthFt || 14)),
            headingDeg: Number(ringBacked ? inferred.headingDeg : (selected.headingDeg || 0)),
            chamferSide: Number(ringBacked ? inferred.chamferSide : (selected.chamferSide || 1)) < 0 ? -1 : 1,
            chamferAngleDeg: Number(ringBacked ? inferred.chamferAngleDeg : (selected.chamferAngleDeg || 0)),
            chamferMagnitudeFt: Number(ringBacked ? inferred.chamferMagnitudeFt : (selected.chamferMagnitudeFt || 0)),
            ringLatLngs: Array.isArray(selectedRing) && selectedRing.length >= 3 ? selectedRing : null,
            sourceCenter: ringBacked
              ? { lat: Number(inferred.center.lat), lng: Number(inferred.center.lng) }
              : (selected?.center ? { lat: Number(selected.center.lat), lng: Number(selected.center.lng) } : null),
          };
          status = `Copied slip geometry ${Number(copiedSlipGeometryTemplate.lengthFt).toFixed(1)} x ${Number(copiedSlipGeometryTemplate.widthFt).toFixed(1)} ft.`;
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (ctrlOrMeta && String(event.key || "").toLowerCase() === "v") {
          if (!copiedSlipGeometryTemplate) return;
          startGeometryPlaceMode({ template: copiedSlipGeometryTemplate });
          status = "Paste mode active: move mouse and click to place copied slip.";
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        const deletePressed = event.key === "Delete" || event.key === "Backspace";
        if (deletePressed) {
          const selectedCount = getSelectedGeometryIds().length;
          if (!selectedCount) return;
          const ok = window.confirm(`Delete ${selectedCount} selected slip geometry polygon${selectedCount === 1 ? "" : "s"}?`);
          if (!ok) return;
          removeSelectedGeometryPolygon({ deleteLinkedRecords: true }).then((removed) => {
            status = removed
              ? `Deleted ${removed} selected slip geometry polygon${removed === 1 ? "" : "s"}.`
              : "No selected polygon to delete.";
            render();
          }).catch((error) => {
            status = error?.message || "Failed to delete selected polygon(s).";
            render();
          });
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (event.key === "Enter") {
          if (geometryRunLayoutActive) return;
          const selected = selectedGeometryPolygon();
          if (!selected) return;
          commitSelectedPolygonAndPersist().catch((error) => {
            status = error?.message || "Failed to commit selected polygon.";
            render();
          });
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        const key = String(event.key || "").toLowerCase();
        if (!["q", "w", "e", "a", "s", "d", "z", "x", "c", "r", "f", "t", "g"].includes(key)) return;
        if (multislipActive && (key === "w" || key === "x")) return;
        const handled = performRunControl(key);
        if (!handled) return;
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      window.addEventListener("keydown", globalRunHotkeyHandler, true);
    }
    if (!globalWheelAdjustKeydownHandler) {
      globalWheelAdjustKeydownHandler = (event) => {
        if (shouldIgnoreGeometryHotkeys(event)) return;
        const code = String(event?.code || "");
        const key = String(event?.key || "");
        if (code === "Space" || key === " ") {
          wheelAdjustSpaceHeld = true;
          return;
        }
        if (code === "Backquote" || key === "`") {
          wheelAdjustBacktickHeld = true;
          return;
        }
        const slot = (
          code === "Digit1" || key === "1" ? 1
            : code === "Digit2" || key === "2" ? 2
              : code === "Digit3" || key === "3" ? 3
                : code === "Digit4" || key === "4" ? 4
                  : code === "Digit5" || key === "5" ? 5
                    : null
        );
        if (!slot) return;
        wheelAdjustHeldSlots.add(slot);
      };
      window.addEventListener("keydown", globalWheelAdjustKeydownHandler, true);
    }
    if (!globalWheelAdjustKeyupHandler) {
      globalWheelAdjustKeyupHandler = (event) => {
        const code = String(event?.code || "");
        const key = String(event?.key || "");
        const slot = (
          code === "Digit1" || key === "1" ? 1
            : code === "Digit2" || key === "2" ? 2
              : code === "Digit3" || key === "3" ? 3
                : code === "Digit4" || key === "4" ? 4
                  : code === "Digit5" || key === "5" ? 5
                    : null
        );
        if (slot) wheelAdjustHeldSlots.delete(slot);
        if (code === "Space" || key === " ") wheelAdjustSpaceHeld = false;
        if (code === "Backquote" || key === "`") wheelAdjustBacktickHeld = false;
      };
      window.addEventListener("keyup", globalWheelAdjustKeyupHandler, true);
    }
    if (!globalWheelAdjustHandler) {
      globalWheelAdjustHandler = (event) => {
        if (!wheelAdjustHeldSlots.size) return;
        if (shouldIgnoreGeometryHotkeys(event)) return;
        if (!activeGeometryPlaceMode && !selectedGeometryPolygon()) return;
        const mapContainer = map?.getContainer?.();
        if (!mapContainer) return;
        const targetNode = event?.target;
        if (!targetNode || (typeof Node !== "undefined" && !(targetNode instanceof Node)) || !mapContainer.contains(targetNode)) return;
        const held = [1, 2, 3, 4, 5].filter((slot) => wheelAdjustHeldSlots.has(slot));
        if (!held.length) return;
        const deltaY = Number(event?.deltaY || 0);
        if (!Number.isFinite(deltaY) || deltaY === 0) return;
        const direction = deltaY < 0 ? 1 : -1;
        let handledAny = false;
        for (const slot of held) {
          const handled = performWheelAdjust(slot, direction, { fine: wheelAdjustSpaceHeld, coarse: wheelAdjustBacktickHeld });
          if (handled) handledAny = true;
        }
        if (!handledAny) return;
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      window.addEventListener("wheel", globalWheelAdjustHandler, { capture: true, passive: false });
    }
    render();
    startRemoteSync();
    if (!initialFetchDone) {
      initialFetchDone = true;
      refreshRecords().catch((error) => {
        status = error?.message || "Slip inventory API is unavailable.";
        render();
      });
    } else {
      // renderSidebar() re-mounts the panel — re-render with existing data, no re-fetch
      renderBottomPanel();
    }
  }

  function destroy() {
    stopRemoteSync();
    clearDimensionLegend();
    stopPointDrop("destroyed");
    stopLineLayout("destroyed");
    stopGeometryPlaceMode();
    stopGeometryRunLayout("destroyed");
    stopFreeDrawMode("destroyed");
    try { root.remove(); } catch {}
    tablePanelEl = null;
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    if (zoomHandler) {
      try { map.off("zoomend", zoomHandler); } catch {}
      zoomHandler = null;
    }
    if (mapSelectClickHandler) {
      try { map.off("click", mapSelectClickHandler); } catch {}
      mapSelectClickHandler = null;
    }
    if (globalRunHotkeyHandler) {
      window.removeEventListener("keydown", globalRunHotkeyHandler, true);
      globalRunHotkeyHandler = null;
    }
    if (globalWheelAdjustKeydownHandler) {
      window.removeEventListener("keydown", globalWheelAdjustKeydownHandler, true);
      globalWheelAdjustKeydownHandler = null;
    }
    if (globalWheelAdjustKeyupHandler) {
      window.removeEventListener("keyup", globalWheelAdjustKeyupHandler, true);
      globalWheelAdjustKeyupHandler = null;
    }
    if (globalWheelAdjustHandler) {
      window.removeEventListener("wheel", globalWheelAdjustHandler, { capture: true });
      globalWheelAdjustHandler = null;
    }
    wheelAdjustHeldSlots.clear();
    wheelAdjustSpaceHeld = false;
    wheelAdjustBacktickHeld = false;
    if (_selectModeHandler) {
      document.removeEventListener("keydown", _selectModeHandler);
      document.removeEventListener("keyup", _selectModeHandler);
      _selectModeHandler = null;
    }
    if (_filterPanelCloseHandler) {
      document.removeEventListener("click", _filterPanelCloseHandler);
      _filterPanelCloseHandler = null;
    }
    _filterPanelShouldBeOpen = false;
    containerEl = null;
  }

  function mountTablePanel(el) {
    tablePanelEl = el;
    if (tablePanelResizeObserver) { tablePanelResizeObserver.disconnect(); tablePanelResizeObserver = null; }
    renderBottomPanel({ force: true });
    if (!initialFetchDone) {
      initialFetchDone = true;
      refreshRecords().catch((error) => {
        status = error?.message || "Slip inventory API is unavailable.";
        render();
      });
    }
    document.addEventListener("hydrotools:marina-changed", (e) => {
      const marinaId = String(e.detail?.marinaId || "");
      _surfacesCatalog = null;
      setProjectMarina(marinaId);
    });
  }

  function unmountTablePanel() {
    if (tablePanelResizeObserver) {
      tablePanelResizeObserver.disconnect();
      tablePanelResizeObserver = null;
    }
    if (tablePanelEl) tablePanelEl.innerHTML = "";
    tablePanelEl = null;
    bottomTablePointerInside = false;
    bottomTableScrollActiveUntil = 0;
  }

  function snapshot() {
    return { version: 2, state };
  }

  function restore(data) {
    if (!data?.state || typeof data.state !== "object") return;
    state = { ...defaultState(), ...data.state };
    state.measurementDate = todayDate();
    state.lineSlipCount = Math.max(1, Number(state.lineSlipCount || 6) || 6);
    state.slipGeometryTypes = Array.isArray(state.slipGeometryTypes) ? state.slipGeometryTypes : [];
    state.slipGeometryPolygons = Array.isArray(state.slipGeometryPolygons) ? state.slipGeometryPolygons : [];
    state.slipGeometrySelectedId = String(state.slipGeometrySelectedId || "");
    state.slipGeometryDraftTypeId = String(state.slipGeometryDraftTypeId || "");
    state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 5) || 5);
    persist();
    render();
  }

  function setMarinaItems(items) {
    marinaCatalog = normalizeMarinaCatalog(items);
    const marinaToResolve = state.marinaId || filterMarinaId;
    if (marinaToResolve) {
      setMarinaDefaultsFromId(marinaToResolve);
    }
    if (containerEl) render();
  }

  function setProjectMarina(code) {
    const newCode = String(code || "");
    const changed = newCode !== filterMarinaId;
    filterMarinaId = newCode;
    showNoMarinaCode = false;
    if (newCode) {
      setMarinaDefaultsFromId(newCode);
    }
    if (changed) {
      if (containerEl) render();
      refreshRecords({ quiet: true }).catch(() => {});
    }
  }

  return { mount, destroy, mountTablePanel, unmountTablePanel, snapshot, restore, setMarinaItems, setProjectMarina };
}

let sharedSlipInventoryRuntime = null;

function getSlipInventoryRuntime(app) {
  sharedSlipInventoryRuntime = sharedSlipInventoryRuntime ?? createSlipInventoryRuntime(app);
  return sharedSlipInventoryRuntime;
}

export const slipDeliveryTool = {
  id: "slip-delivery",
  name: "Slip Inventory",
  init(app) { this._rt = getSlipInventoryRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; sharedSlipInventoryRuntime = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.restore?.(data);
  },
};

export const slipInventoryTableTool = {
  id: "slip-inventory-table",
  name: "Slip Inventory Table",
  init(app) { this._rt = getSlipInventoryRuntime(app); },
  destroy() { this._rt?.unmountTablePanel?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.mountTablePanel?.(el);
  },
};
